/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, no-use-before-define */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { faPenToSquare, faCheck, faArrowLeft, faCamera, faTrash, faUndo, faRedo, faTimes, faPlus, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Check } from 'lucide-react';
import { StandardSearch } from '../../styles/standardStyles';
import { clearRecipeReviewDraft, getRecipeReviewDraft, hydrateRecipeReviewDrafts, mergeRecipesWithLocalReviewDrafts, saveRecipeReviewDraft } from '../../utils/recipeReviewDrafts';
import { sortBySearchPriority } from '../../utils/searchPriority';
import { addedFromSalesLabel, isPendingAddedFromSales } from '../../utils/addedFromSales';
import UnitMappingService from '../../services/unitMappingService';
import RecipesService from '../../services/recipesService';
import MenuItemSelector from '../MenuItemSelector';
import InventoryService from '../../services/inventoryService';
import RecipeLibraryService from '../../services/recipeLibraryService';

import FeaturesService from '../../services/FeaturesService';
import ComplimentaryItemsPage from '../comps';
import CustomImageUploadModal from '../CustomImageUploadModal';
import http from '../../services/httpClient';
import { scrollToTop } from '../../utils/scroll';
import { formatQuantity, convert } from '../../utils/unitDisplay';
import { buildPerPlatePayload } from '../../utils/recipeSavePayload';
import './QRitemmap.css';
import '../itemmap.css';
import '../comps.css';
import '../NCItemsPage.css'; // Import NC Items styling for Quick Add modal
import { FaChevronDown, FaTimes } from "react-icons/fa";

// SP9 (B4): render-path logging is a no-op outside development — each of the
// hundreds of former console.log calls cost real time on low-end devices.
const debugLog = process.env.NODE_ENV === 'development' ? console.log.bind(console) : () => {};


// styles moved to itemmap.css

// Helpers: name normalization and Levenshtein distance (module scope so they are always defined)
function normalizeName(s) {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(th|dh|bh)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshtein(a, b) {
  a = normalizeName(a); b = normalizeName(b);
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[m][n];
}

// ============================================================
// JUNCTION TABLE FORMAT HELPERS
// ============================================================
/**
 * Normalize complimentary items to always return array of strings
 * Handles both old format (string[]) and new format (object[] with {id, name, ...})
 */
function normalizeComplimentaryItems(items) {
  if (!items || !Array.isArray(items)) return [];
  return items.map(item => {
    if (typeof item === 'string') return item;
    if (typeof item === 'object' && item.name) return item.name;
    return String(item);
  });
}

/**
 * Normalize add-ons to always return array of strings
 * Handles both old format (string[]) and new format (object[] with {id, name, ...})
 */
function normalizeAddOns(items) {
  if (!items || !Array.isArray(items)) return [];
  return items.map(item => {
    if (typeof item === 'string') return item;
    if (typeof item === 'object' && item.name) return item.name;
    return String(item);
  });
}

// Normalize recipe objects so we know whether a real stored image exists.
// "image_url" comes from DB (may be null) while "image" can be a fallback placeholder.
const withImageMeta = (recipe) => {
  if (!recipe) return null;
  const storedImage = typeof recipe.image_url === 'string' && recipe.image_url.trim().length > 0
    ? recipe.image_url.trim()
    : null;
  const fallbackImage = recipe.image || recipe.fallbackImage || null;
  return {
    ...recipe,
    image: storedImage,
    fallbackImage
  };
};

const DUPLICATE_OVERRIDE_OPTIONS = [
  { value: 'Different Brand', label: 'Different Brand' },
  { value: 'Different Quality', label: 'Different Quality' },
  { value: 'Intentional Variant', label: 'Intentional Variant' },
  { value: 'Other', label: 'Other' }
];

// Custom, fully-stylable select component
const NiceSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  disabled = false,
  fullWidth = false,
  style,
  onAddClick,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(() => Math.max(0, options.findIndex(o => o.value === value)));
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    const idx = options.findIndex(o => o.value === value);
    if (idx >= 0) setActiveIndex(idx);
  }, [value, options]);

  const selectByIndex = (idx) => {
    const opt = options[idx];
    if (!opt) return;
    if (opt.value === 'add') {
      onAddClick && onAddClick();
    } else {
      onChange && onChange(opt.value);
    }
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActiveIndex(i => Math.min(options.length - 1, (i ?? -1) + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setActiveIndex(i => Math.max(0, (i ?? options.length) - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); selectByIndex(activeIndex >= 0 ? activeIndex : 0); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const selected = options.find(o => o.value === value);

  return (
    <div
      ref={wrapRef}
      className={`qrb-nice-select-custom${open ? ' qrb-open' : ''}${disabled ? ' qrb-disabled' : ''} ${className}`}
      style={{ width: fullWidth ? '100%' : undefined, ...style }}
    >
      <div
        className="qrb-ns-control"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={onKeyDown}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.label ?? selected?.value ?? placeholder}
        </span>
        <span className="qrb-ns-arrow" />
      </div>
      {open && !disabled && (
        <div className="qrb-ns-menu" role="listbox">
          {options.map((opt, idx) => (
            <div
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`qrb-ns-option${opt.value === value ? ' qrb-selected' : ''}${idx === activeIndex ? ' qrb-active' : ''}`}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={(e) => { e.preventDefault(); }}
              onClick={(e) => {
                if (opt.value === 'add') {
                  e.stopPropagation();
                  onAddClick && onAddClick();
                } else {
                  selectByIndex(idx);
                }
              }}
            >
              {opt.label ?? opt.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Searchable dropdown (with inline filter input) used for unit selection
const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Search unit...',
  disabled = false,
  style,
  // Show fewer rows by default to reduce dropdown height
  visibleCount = 3,
  onAddClick,
  isOpen,
  onToggle,
  openUpward,
}) => {
  const [open, setOpen] = useState(isOpen || false);

  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(isOpen);
    }
  }, [isOpen]);

  const handleToggle = (newOpen) => {
    setOpen(newOpen);
    if (onToggle) {
      onToggle(newOpen);
    }
  };
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const controlRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState(undefined);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) handleToggle(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [handleToggle]);

  const selected = options.find(o => o.value === value);
  const filtered = sortBySearchPriority(
    options,
    query,
    (option) => option.label || option.value || ''
  );

  // When opening or when options/filter change, measure widest option and size menu accordingly
  useEffect(() => {
    if (!open) return;
    const ctrl = controlRef.current;
    if (!ctrl) return;
    const computeWidth = () => {
      const testEl = document.createElement('div');
      const cs = window.getComputedStyle(ctrl);
      testEl.style.position = 'fixed';
      testEl.style.left = '-9999px';
      testEl.style.top = '-9999px';
      testEl.style.visibility = 'hidden';
      testEl.style.whiteSpace = 'nowrap';
      testEl.style.fontFamily = cs.fontFamily || 'Inter, Arial, sans-serif';
      testEl.style.fontSize = '16px';
      testEl.style.fontWeight = '400';
      document.body.appendChild(testEl);
      let max = 0;
      const source = filtered.length ? filtered : options;
      for (const opt of source) {
        testEl.textContent = String(opt.label ?? opt.value ?? '');
        max = Math.max(max, testEl.clientWidth);
      }
      document.body.removeChild(testEl);
      const triggerW = ctrl.getBoundingClientRect().width;
      // Add generous padding and scrollbar allowance
      let needed = max + 60; // text + padding
      needed = Math.max(needed, triggerW);
      const maxViewport = Math.max(340, Math.floor(window.innerWidth * 0.9));
      setMenuWidth(Math.min(needed, maxViewport));
    };
    computeWidth();
    // Recompute on window resize while open
    const onResize = () => computeWidth();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, options, filtered]);

  const CONTROL_HEIGHT = 30; // compact control height
  const MENU_GAP = 6; // spacing between control and menu
  const optionHeight = 36; // px per option row for height math
  const listMaxHeight = Math.max(1, visibleCount) * optionHeight;

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', ...(style || {}) }}>
      <div
        ref={controlRef}
        onClick={() => !disabled && handleToggle(!open)}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%',
          height: CONTROL_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #d1d5db',
          borderRadius: 8,
          background: disabled ? '#f5f5f5' : '#fff',
          padding: '0 10px',
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.label ?? selected?.value ?? 'g'}
        </span>
        <span style={{ border: 'solid #111', borderWidth: '0 2px 2px 0', display: 'inline-block', padding: 3, transform: 'rotate(45deg)' }} />
      </div>
      {open && !disabled && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: openUpward ? 'auto' : CONTROL_HEIGHT + MENU_GAP,
            bottom: openUpward ? CONTROL_HEIGHT + MENU_GAP : 'auto',
            // Align the dropdown to the right edge of the trigger so it opens to the left,
            // keeping it inside the ingredient row container instead of overflowing to the right
            left: 'auto',
            right: 0,
            zIndex: 3000,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
            padding: 8,
            width: menuWidth || 'max-content',
            minWidth: menuWidth || 'auto',
            // Keep within viewport width as a last-resort safety
            maxWidth: '90vw',
          }}
        >
          <div style={{ maxHeight: listMaxHeight, overflowY: 'auto', overflowX: 'hidden' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 12, color: '#777', fontSize: 13 }}>No matches</div>
            ) : (
              filtered.map(opt => (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === value}
                  onClick={() => {
                    if (opt.value === 'add') {
                      onAddClick && onAddClick();
                    } else {
                      onChange && onChange(opt.value);
                    }
                    handleToggle(false);
                    setQuery('');
                  }}
                  style={{
                    padding: opt.value === 'add' ? '10px 12px' : '8px 10px',
                    borderRadius: 8,
                    background: opt.value === 'add' ? '#000' : (opt.value === value ? '#f3f4f6' : 'transparent'),
                    color: opt.value === 'add' ? '#fff' : '#111',
                    cursor: 'pointer',
                    textAlign: opt.value === 'add' ? 'center' : 'left',
                    fontWeight: opt.value === 'add' ? 700 : 400,
                    marginTop: opt.value === 'add' ? 8 : 0,
                    whiteSpace: 'nowrap', // stay on one line
                    wordBreak: 'keep-all',
                  }}
                >
                  {opt.value === 'add' ? (opt.label || '+ Add') : (opt.label ?? opt.value)}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RecipeListScreen = ({ goTo, previousScreen, showProgressPopup = false, onProgressPopupClose, onStep3Complete }) => {
  const [recipes, setRecipes] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const categoryChipsRef = useRef(null);
  const categoryMeasureRef = useRef(null);
  const [showAllCategoryChips, setShowAllCategoryChips] = useState(false);
  const [hasCategoryChipOverflow, setHasCategoryChipOverflow] = useState(false);
  const [collapsedCategoryCount, setCollapsedCategoryCount] = useState(0);
  const [savingRecipeId, setSavingRecipeId] = useState(null);
  // Track which ingredient row's unit dropdown is open (so only one opens at a time)
  const [openUnitIndex, setOpenUnitIndex] = useState(null);
  const editNameInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  const swipeStateRef = useRef({});
  const editingOriginalsRef = useRef({});
  const [swipeOffsets, setSwipeOffsets] = useState({});
  const [swipingIndex, setSwipingIndex] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [ingredientList, setIngredientList] = useState([]);
  const [localInputValues, setLocalInputValues] = useState({});
  const [creationErrors, setCreationErrors] = useState({});
  const [showRoundOffModal, setShowRoundOffModal] = useState(false);
  // Auto-save refs and status (INTERNAL ONLY - not DB)
  const autoSaveTimerRef = useRef(null);
  const ingredientListInitRef = useRef(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null);
  
  // Store original ingredients for revert on cancel
  const originalIngredientListRef = useRef([]);
  // Plate size selector: 1, 10, 100, or custom plates (hamburger menu)
  const [plateStep, setPlateStep] = useState(1); // 1, 10, 100, or custom
  const [showPlateMenu, setShowPlateMenu] = useState(false);
  const [customPlateStep, setCustomPlateStep] = useState('');
  const [showCustomPlateInput, setShowCustomPlateInput] = useState(false);
  const plateMenuRef = useRef(null);
  const [autoSelectAttempted, setAutoSelectAttempted] = useState(false);
  
  // Progress popup state
  const [displayProgressPopup, setDisplayProgressPopup] = useState(false);
  
  // Check if we came from TSR page (for back button navigation)
  const [cameFromTSR, setCameFromTSR] = useState(false);
  const [cameFromWastage, setCameFromWastage] = useState(false);
  const [cameFromMenuManagement, setCameFromMenuManagement] = useState(false);
  


  // Show progress popup when component mounts if flag is set
  useEffect(() => {
    if (showProgressPopup) {
      setDisplayProgressPopup(true);
      const timer = setTimeout(() => {
        setDisplayProgressPopup(false);
        if (onProgressPopupClose) {
          onProgressPopupClose();
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showProgressPopup, onProgressPopupClose]);
  
  useEffect(() => {
    const pendingTSRData = localStorage.getItem('pendingTSRData');
    const pendingWastageData = localStorage.getItem('pendingWastageData');
    const pendingMenuManagementData = localStorage.getItem('pendingMenuManagementData');
    setCameFromTSR(!!pendingTSRData);
    setCameFromWastage(!!pendingWastageData);
    setCameFromMenuManagement(!!pendingMenuManagementData);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const appMain = document.querySelector('.app-main');
      const scrollTop = Math.max(
        window.scrollY,
        document.documentElement.scrollTop,
        document.body.scrollTop,
        appMain ? appMain.scrollTop : 0
      );
      setShowScrollTop(scrollTop > 400);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);


  // 🔄 Internal auto-save status update (NO DATABASE - just UI feedback)
  useEffect(() => {
    // Skip on initial load / recipe switch
    if (ingredientListInitRef.current) {
      ingredientListInitRef.current = false;
      return;
    }
    if (!selectedRecipe?.id) return;
    // Only show status if there are valid ingredients
    const valid = ingredientList.filter(i => (i.name || '').trim() && i.name !== 'New Ingredient' && (i.qty10 > 0 || i.qty100 > 0));
    if (valid.length === 0) return;
    // Debounce — show "saved" status after 1.5 seconds of inactivity
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      showInternalSaveStatus();
    }, 1500);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [ingredientList]);

  // Reset init ref when recipe changes so first load doesn't auto-save
  useEffect(() => {
    ingredientListInitRef.current = true;
  }, [selectedRecipe?.id]);
  // Complimentary items state - fetched from Recipe Library
  const [complimentaryItems, setComplimentaryItems] = useState([]);
  const [showComplimentaryDropdown, setShowComplimentaryDropdown] = useState(false);
  const [showComplimentaryItemPage, setShowComplimentaryItemPage] = useState(false);
  const [selectedComplimentaryItem, setSelectedComplimentaryItem] = useState(null);
  const [complimentaryLinkModal, setComplimentaryLinkModal] = useState({
    visible: false,
    item: null,
    currentlyLinked: false,
    error: '',
    isSubmitting: false
  });
  const complimentaryDropdownRef = useRef(null);
  const [showAddComplimentaryModal, setShowAddComplimentaryModal] = useState(false);
  const [newComplimentaryForm, setNewComplimentaryForm] = useState({ name: '' });
  
  // 🆕 Advanced Complimentary System (ComplimentaryRecipes)
  const [complimentaryRecipesLibrary, setComplimentaryRecipesLibrary] = useState([]); // All available complimentary recipes
  const [linkedComplimentaryItems, setLinkedComplimentaryItems] = useState([]); // Items linked to current recipe
  const [loadingComplimentaryLibrary, setLoadingComplimentaryLibrary] = useState(false);
  
  // Customizations state - fetched from Recipe Library (add_ons)
  const [customizations, setCustomizations] = useState([]);
  const [showCustomizationsDropdown, setShowCustomizationsDropdown] = useState(false);
  const [showAddCustomizationModal, setShowAddCustomizationModal] = useState(false);
  const [newCustomizationName, setNewCustomizationName] = useState('');
  const [selectedCustomizations, setSelectedCustomizations] = useState([]);
  const customizationsDropdownRef = useRef(null);
  
  // Selected complimentary items state
  const [selectedComplimentaryItems, setSelectedComplimentaryItems] = useState([]);
  
  // Save confirmation modal state
  const [showSaveConfirmationModal, setShowSaveConfirmationModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyModalMessage, setVerifyModalMessage] = useState('');
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [summaryOpenDropdown, setSummaryOpenDropdown] = useState(null);
  const [savedPreparations, setSavedPreparations] = useState([]);

  const loadSavedPreparations = useCallback(async () => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      if (!businessId) return;

      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';
      const response = await fetch(`${apiUrl}/medium-features/decomposition/templates?businessId=${businessId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      if (!response.ok) return;

      const result = await response.json();
      setSavedPreparations(Array.isArray(result?.personalized) ? result.personalized : []);
    } catch (error) {
      console.warn('Failed to load saved preparations for summary:', error);
    }
  }, []);

  useEffect(() => {
    loadSavedPreparations();
  }, [loadSavedPreparations]);

  useEffect(() => {
    if (showSaveConfirmationModal) {
      loadSavedPreparations();
    }
  }, [showSaveConfirmationModal, loadSavedPreparations]);
  
  // Delete modal states
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteModalMessage, setDeleteModalMessage] = useState('');
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);
  
  const [showKitchenUnitModal, setShowKitchenUnitModal] = useState(false);
  const [kitchenUnitForm, setKitchenUnitForm] = useState({
    item: '',
    kitchenTool: 'Tablespoon',
    quantity: '15',
    unit: 'ml'
  });
  // Kitchen-unit popup local dropdown states (to mirror Kitchen Units screen UI)
  const [modalToolOpen, setModalToolOpen] = useState(false);
  const [modalUnitOpen, setModalUnitOpen] = useState(false);
  const [modalItemOpen, setModalItemOpen] = useState(false);
  const [modalItemSearch, setModalItemSearch] = useState('');
  const [modalToolSearch, setModalToolSearch] = useState('');
  const [modalUnitSearch, setModalUnitSearch] = useState('');
  const [modalKitchenTools, setModalKitchenTools] = useState(['Tablespoon', 'Teaspoon', 'Cup', 'Bowl']);
  const [modalShowAddTool, setModalShowAddTool] = useState(false);
  const [modalNewToolName, setModalNewToolName] = useState('');
  const baseUnitOptions = [
    { value: 'ml', label: 'ml' },
    { value: 'L', label: 'L' },
    { value: 'g', label: 'g' },
    { value: 'kg', label: 'kg' },
  ];

  const normalizeKitchenTool = (tool) => {
    const raw = String(tool || '').trim();
    const lower = raw.toLowerCase();
    if (!lower) return { symbol: '', label: '' };
    const map = {
      tablespoon: 'tbsp',
      tbsp: 'tbsp',
      teaspoon: 'tsp',
      tsp: 'tsp'
    };
    const symbol = map[lower] || lower;
    const labelMap = {
      tbsp: 'Tablespoon',
      tsp: 'Teaspoon'
    };
    return { symbol, label: labelMap[symbol] || raw };
  };

  const handleSaveKitchenUnit = async () => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      
      // Validate required fields
      if (!kitchenUnitForm.kitchenTool || !kitchenUnitForm.quantity || !kitchenUnitForm.unit) {
        showToast('Please fill all fields');
        return;
      }
      
      const result = await UnitMappingService.saveKitchenUnit({
        businessId,
        item: kitchenUnitForm.item || formData.name,
        kitchenTool: kitchenUnitForm.kitchenTool,
        quantity: Number(kitchenUnitForm.quantity),
        unit: kitchenUnitForm.unit
      });

      // Update ingredient-specific maps for immediate UI consistency
      const itemKey = (kitchenUnitForm.item || formData.name || '').trim().toLowerCase();
      if (itemKey) {
        setIngredientToolUnitsMap(prev => {
          const next = { ...prev };
          const list = next[itemKey] ? [...next[itemKey]] : [];
          const { symbol, label } = normalizeKitchenTool(kitchenUnitForm.kitchenTool);
          if (symbol && !list.find(x => x.value === symbol)) {
            list.push({ value: symbol, label });
          }
          next[itemKey] = list;
          return next;
        });
        setIngredientToolConversionsMap(prev => {
          const next = { ...prev };
          const list = next[itemKey] ? [...next[itemKey]] : [];
          list.push({
            tool: normalizeKitchenTool(kitchenUnitForm.kitchenTool).symbol,
            unit: kitchenUnitForm.unit,
            quantity: Number(kitchenUnitForm.quantity)
          });
          next[itemKey] = list;
          return next;
        });

        // If user is editing this ingredient, switch to the new tool immediately
        if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
          const tool = normalizeKitchenTool(kitchenUnitForm.kitchenTool).symbol;
          const outUnit = String(kitchenUnitForm.unit || '').trim();
          const factor = Number(kitchenUnitForm.quantity) || 0;

          if (tool && outUnit && factor > 0) {
            // Update formData
            if ((formData.name || '').trim().toLowerCase() === itemKey) {
              let newQty = formData.quantity;
              if (String(formData.unit || '').trim().toLowerCase() === outUnit.toLowerCase()) {
                newQty = Number((Number(formData.quantity || 0) / factor).toFixed(4));
              }
              setFormData(prev => ({ ...prev, unit: tool, quantity: newQty }));
            }

            // Update ingredient list for the active row
            setIngredientList(prev => {
              const updated = [...prev];
              const row = updated[editingIngredientIndex];
              if (!row) return updated;
              if ((row.name || '').trim().toLowerCase() !== itemKey) return updated;
              const currentUnit = String(row.unit || '').trim().toLowerCase();
              if (currentUnit === outUnit.toLowerCase()) {
                const curQty = getDisplayQty(row);
                const newQty = Number((curQty / factor).toFixed(4));
                const computed = setQtyForPlateStep(row, newQty);
                row.qty10 = computed.qty10;
                row.qty100 = computed.qty100;
              }
              row.unit = tool;
              updated[editingIngredientIndex] = { ...row };
              return updated;
            });
          }
        }
      }

      // Update unit options to include the new kitchen unit
      const currentOptions = [...unitOptions];
      const normalizedTool = normalizeKitchenTool(kitchenUnitForm.kitchenTool);
      if (!currentOptions.find(opt => opt.value === normalizedTool.symbol)) {
        currentOptions.push({
          value: normalizedTool.symbol,
          label: normalizedTool.label
        });
        setUnitOptions(currentOptions);
      }

      setShowKitchenUnitModal(false);
      showToast('Kitchen unit saved successfully!');
    } catch (error) {
      console.error('❌ Failed to save kitchen unit:', error);
      const errorMsg = error?.response?.data?.error || error?.message || 'Failed to save kitchen unit';
      showToast(errorMsg);
    }
  };
  const [editingIngredientIndex, setEditingIngredientIndex] = useState(null);
  const [formData, setFormData] = useState({ name: '', quantity: 0, unit: '' });
  const [unitOptions, setUnitOptions] = useState([]);
  const ingredientEditorRef = useRef(null);

  useEffect(() => {
    if (editingIngredientIndex === null || editingIngredientIndex === undefined) return;
    const timer = setTimeout(() => {
      if (editNameInputRef.current) {
        editNameInputRef.current.focus();
        editNameInputRef.current.select();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [editingIngredientIndex]);
  
  // Undo/Redo history for ingredient deletions
  const [ingredientHistory, setIngredientHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const cloneIngredientList = (list) => list.map(item => ({ ...item }));
  const pushIngredientHistory = (action, previousList, newList) => {
    const nextHistory = ingredientHistory.slice(0, historyIndex + 1);
    nextHistory.push({ action, previousList, newList });
    setIngredientHistory(nextHistory);
    setHistoryIndex(nextHistory.length - 1);
  };
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionText, setDescriptionText] = useState('');
  const [ingredientToolUnitsMap, setIngredientToolUnitsMap] = useState({}); // name -> [{symbol,label}]
  const [ingredientToolConversionsMap, setIngredientToolConversionsMap] = useState({}); // name -> [{tool, unit, quantity}]
  const [showNewRecipeForm, setShowNewRecipeForm] = useState(false);
  const [newRecipeData, setNewRecipeData] = useState({ name: '', price: '', servings: '' });
  const [recipeCards, setRecipeCards] = useState([{ id: Date.now(), name: '', price: '', servings: '' }]); // Array of recipe cards
  const [capturedImage, setCapturedImage] = useState(null);
  
  // Quick Add Menu Item (without AI) - NC Items style
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [quickAddItemName, setQuickAddItemName] = useState('');
  const [quickAddError, setQuickAddError] = useState('');
  const [isCreatingQuickItem, setIsCreatingQuickItem] = useState(false);
  const [showMenuOptionsModal, setShowMenuOptionsModal] = useState(false);
  const [showOCRModal, setShowOCRModal] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrResults, setOcrResults] = useState([]);
  const ocrMenuInputRef = useRef(null);
  const [showMenuSelector, setShowMenuSelector] = useState(false);
  const [useMasterCategories, setUseMasterCategories] = useState(() => {
    const saved = localStorage.getItem('useMasterCategories');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('useMasterCategories', JSON.stringify(useMasterCategories));
  }, [useMasterCategories]);

  const categories = React.useMemo(() => {
    const key = useMasterCategories ? 'master_category' : 'category';
    const unique = Array.from(new Set(recipes.map(r => r[key] || 'Uncategorized').filter(Boolean)));
    return unique.sort();
  }, [recipes, useMasterCategories]);

  useEffect(() => {
    setFilter('All');
  }, [useMasterCategories]);

  useEffect(() => {
    scrollToTop();
  }, [search, filter, useMasterCategories]);

  useEffect(() => {
    setLocalInputValues({});
  }, [selectedRecipe?.id]);

  useEffect(() => {
    if (!showNewRecipeForm) {
      setCreationErrors({});
    }
  }, [showNewRecipeForm]);
  const [availableCategories, setAvailableCategories] = useState([]); // Menu categories from DB
  const [selectedCategory, setSelectedCategory] = useState(null); // Selected category for recipes
  const [savingIngredients, setSavingIngredients] = useState(false);
  const savingIngredientsRef = useRef(false); // imperative guard — avoids stale React state blocking retries

  // ── Duplicate Prevention Modal state ──
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateConflictData, setDuplicateConflictData] = useState(null);
  const [duplicateOverrides, setDuplicateOverrides] = useState({});
  const duplicateOverridesRef = useRef({}); // imperative ref — ensures retries use the latest overrides
  const [duplicateForceReason, setDuplicateForceReason] = useState('Intentional Variant');
  const pendingConfirmListRef = useRef(null);
  const pendingSaveListRef = useRef(null);
  const pendingSavePlateCountRef = useRef(null);

  // Menu scan job tracking
  const [menuScanJob, setMenuScanJob] = useState(null);
  const [menuScanProgress, setMenuScanProgress] = useState(0);
  const [scannedMenuItems, setScannedMenuItems] = useState([]);
  const pollIntervalRef = useRef(null);

  // Multi-image support for menu scanning
  const [selectedMenuImages, setSelectedMenuImages] = useState([]);
  const [menuProcessingProgress, setMenuProcessingProgress] = useState({ current: 0, total: 0 });

  // Inventory items map for standard units (name -> unit symbol)
  const [inventoryStdUnitMap, setInventoryStdUnitMap] = useState({});
  const [inventoryItemNames, setInventoryItemNames] = useState([]); // for modal Item dropdown

  // Simple message modal that auto-closes after a short duration
  const MESSAGE_MODAL_DURATION_MS = 1500;
  const [toastModal, setToastModal] = useState({
    visible: false,
    message: '',
    type: 'info'
  });
  const toastTimerRef = useRef(null);
  const closeToast = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastModal((prev) => ({ ...prev, visible: false }));
  };
  const showToast = (msg, type = 'info') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastModal({
      visible: true,
      message: msg,
      type: type || 'info'
    });
    toastTimerRef.current = setTimeout(() => closeToast(), MESSAGE_MODAL_DURATION_MS);
  };

  const getComplimentaryItemName = (item) => (
    typeof item === 'string' ? item : item?.name || 'this complimentary item'
  );

  const closeComplimentaryLinkModal = () => {
    setComplimentaryLinkModal({
      visible: false,
      item: null,
      currentlyLinked: false,
      error: '',
      isSubmitting: false
    });
  };

  const requestComplimentaryLinkToggle = (item, currentlyLinked, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setShowComplimentaryDropdown(false);
    setComplimentaryLinkModal({
      visible: true,
      item,
      currentlyLinked,
      error: '',
      isSubmitting: false
    });
  };

  const applyLocalReviewDrafts = (nextRecipes) => (
    mergeRecipesWithLocalReviewDrafts(nextRecipes, UnitMappingService.getBusinessId())
  );

  // SP7: pull the server's drafts once per mount so the "Under Review" badge is truthful
  // across devices; a dead network degrades to local-only drafts.
  useEffect(() => {
    hydrateRecipeReviewDrafts(UnitMappingService.getBusinessId())
      .then((applied) => {
        if (applied > 0) setRecipes((prev) => applyLocalReviewDrafts(prev));
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistLocalReviewDraft = (draftIngredientList = ingredientList) => {
    if (!selectedRecipe?.id) return null;

    return saveRecipeReviewDraft({
      recipe: selectedRecipe,
      businessId: UnitMappingService.getBusinessId(),
      draft: {
        status: 'draft',
        source: 'qr-itemmap',
        ingredientList: Array.isArray(draftIngredientList) ? draftIngredientList : [],
        selectedComplimentaryItems: Array.isArray(selectedComplimentaryItems) ? selectedComplimentaryItems : [],
        selectedCustomizations: Array.isArray(selectedCustomizations) ? selectedCustomizations : [],
        descriptionText: descriptionText || ''
      }
    });
  };
  
  // Composite Ingredient Detection & Customization
  const [detectedComposites, setDetectedComposites] = useState([]);
  const [showCompositePopup, setShowCompositePopup] = useState(false);
  const [showCompositeEditor, setShowCompositeEditor] = useState(false);
  const [selectedComposite, setSelectedComposite] = useState(null);
  
  // 🆕 Portion Multiplier Customization
  const [showMultiplierEditor, setShowMultiplierEditor] = useState(false);
  const [pendingVariantGroups, setPendingVariantGroups] = useState([]);
  const [customMultipliers, setCustomMultipliers] = useState({}); // businessId-specific multipliers
  const [editableMultipliers, setEditableMultipliers] = useState({}); // Current editing state
  const [isCreatingRecipes, setIsCreatingRecipes] = useState(false);
  const [recipeCreationProgress, setRecipeCreationProgress] = useState({ current: 0, total: 0, message: '' });
  
  // Function to check if ingredients contain composites
  const checkForComposites = async (ingredients) => {
    const composites = [];
    const businessId = UnitMappingService.getBusinessId();
    // REACT_APP_API_URL already includes /api
    const apiUrl = process.env.REACT_APP_API_URL;
    
    
    for (const ing of ingredients) {
      try {
        const response = await fetch(`${apiUrl}/medium-features/decomposition/detect?ingredientName=${encodeURIComponent(ing.name)}`);
        const data = await response.json();
        
        
        if (data.isComposite) {
          // Handle both formats: direct quantity or qty10/qty100
          const quantity = ing.quantity || ing.qty10 || ing.qty100 || 0;
          const composite = {
            name: ing.name,
            quantity: quantity,
            unit: ing.unit,
            keyword: data.keyword,
            recommendation: data.recommendation
          };
          composites.push(composite);
        }
      } catch (error) {
        console.error(`   ❌ Failed to check composite for ${ing.name}:`, error);
      }
    }
    
    return composites;
  };
  
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

  // Close complimentary dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (complimentaryDropdownRef.current && !complimentaryDropdownRef.current.contains(e.target)) {
        setShowComplimentaryDropdown(false);
      }
    };
    if (showComplimentaryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showComplimentaryDropdown]);

  // Close customizations dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customizationsDropdownRef.current && !customizationsDropdownRef.current.contains(e.target)) {
        setShowCustomizationsDropdown(false);
      }
    };
    if (showCustomizationsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCustomizationsDropdown]);

  // Load description when recipe is selected
  useEffect(() => {
    if (selectedRecipe) {
      const localReviewDraft = getRecipeReviewDraft(selectedRecipe, UnitMappingService.getBusinessId());
      setDescriptionText(localReviewDraft?.descriptionText ?? selectedRecipe.description ?? '');
      setEditingDescription(false);
    }
  }, [selectedRecipe?.id, ingredientToolConversionsMap, inventoryStdUnitMap]);

  // Auto-open file picker when OCR modal opens (for Scan Menu)
  useEffect(() => {
    if (showOCRModal && !ocrProcessing && ocrResults.length === 0) {
      // slight delay to ensure input is rendered
      const t = setTimeout(() => {
        try { ocrMenuInputRef.current && ocrMenuInputRef.current.click(); } catch { }
      }, 50);
      return () => clearTimeout(t);
    }
  }, [showOCRModal, ocrProcessing, ocrResults.length]);

  // Load recipes, unit options, and inventory std units from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const businessId = UnitMappingService.getBusinessId();
        const recipesData = await RecipesService.getRecipes(businessId);
        const normalized = recipesData.map(r => ({ ...r })); // Normalize recipes data
        setRecipes(applyLocalReviewDrafts(normalized));
        // Dynamic categories are computed automatically from recipes state and toggle selection

        // Fetch menu categories from DB
        try {
          const apiUrl = process.env.REACT_APP_API_URL;
          // O-1: the route needs the login token (it answered 401 without it, and the list stayed on its fallback).
          const categoriesToken = localStorage.getItem('token');
          const response = await fetch(`${apiUrl}/menu/categories?businessId=${businessId}`, {
            headers: categoriesToken ? { Authorization: `Bearer ${categoriesToken}` } : undefined,
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              setAvailableCategories(data.data.map(cat => cat.name));
            }
          }
        } catch (e) {
          console.warn('Failed to load menu categories:', e);
          // Fallback to common categories
          setAvailableCategories(['Main Course', 'Appetizers', 'Desserts', 'Beverages', 'Sides']);
        }

        const unitOptionsData = await UnitMappingService.getUnitOptions();
        // Build a minimal, global base list only (do NOT include custom Count units)
        // Keep mapping dropdown clean; ingredient-specific tools are merged later per item.
        // Base units (global for all)
        const allowedBase = ['g', 'kg', 'ml', 'L'];
        const bySymbol = new Map();
        for (const group of ['kitchen', 'supplier', 'container']) {
          for (const u of unitOptionsData[group] || []) {
            const sym = (u.unit_symbol || '').trim();
            if (!sym) continue;
            if (!bySymbol.has(sym)) bySymbol.set(sym, u);
          }
        }
        const baseUnits = allowedBase
          .filter(sym => sym && (bySymbol.has(sym) || true))
          .map(sym => ({ value: sym, label: sym }));
        // Add the "add" option at the end of the dropdown
        baseUnits.push({ value: 'add', label: 'Add Kitchen Unit' });
        setUnitOptions(baseUnits);

        // Fetch ingredient-scoped kitchen conversions and build a lookup for per-ingredient extra options
        try {
          const convs = await UnitMappingService.getIngredientKitchenUnits(businessId);
          // convs: [{ item, kitchen_tool, unit, quantity }]
          const byItem = {};
          const byItemConversions = {};
          for (const r of convs || []) {
            const itemName = (r.item || '').trim();
            const toolSym = (r.kitchen_tool || '').trim();
            const outUnit = (r.unit || '').trim();
            const qty = Number(r.quantity);
            if (!itemName || !toolSym || !outUnit) continue;
            // Represent tool symbol itself as a selectable unit in mapping UI
            const key = itemName.toLowerCase();
            const normalizedTool = normalizeKitchenTool(toolSym);
            if (!normalizedTool.symbol) continue;
            if (!byItem[key]) byItem[key] = new Map();
            // Avoid duplicates; prefer lowercase label
            if (!byItem[key].has(normalizedTool.symbol)) {
              byItem[key].set(normalizedTool.symbol, { value: normalizedTool.symbol, label: normalizedTool.label });
            }
            if (!byItemConversions[key]) byItemConversions[key] = [];
            if (Number.isFinite(qty) && qty > 0) {
              byItemConversions[key].push({
                tool: normalizedTool.symbol,
                unit: outUnit,
                quantity: qty
              });
            }
          }
          // Convert maps to arrays
          const finalized = {};
          Object.keys(byItem).forEach(k => finalized[k] = Array.from(byItem[k].values()));
          setIngredientToolUnitsMap(finalized);
          setIngredientToolConversionsMap(byItemConversions);
        } catch (e) {
          console.warn('Failed to load ingredient kitchen units (optional):', e);
        }

        // Fetch inventory items to know each item's standard unit (e.g., ml/L for liquids)
        try {
          const inv = await InventoryService.getInventoryItems(businessId, 500);
          const map = {};
          const names = [];
          for (const it of inv.data || []) {
            if (it?.name) {
              map[it.name.trim().toLowerCase()] = it.standardUnit || null;
              names.push(it.name.trim());
            }
          }
          setInventoryStdUnitMap(map);
          setInventoryItemNames(names);
        } catch (e) {
          console.warn('Failed to fetch inventory items for standard units', e);
        }
        
        // 🆕 Load complimentary recipes library (advanced system)
        await loadComplimentaryRecipesLibrary();
        
      } catch (error) {
        console.error('Failed to load data:', error);
        setError('Failed to load recipes. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Auto-select recipe if menuItemId is stored (e.g., from TSR navigation)
  useEffect(() => {
    if (!autoSelectAttempted && recipes.length > 0) {
      const autoSelectId = localStorage.getItem('autoSelectRecipeId');
      const autoSelectName = localStorage.getItem('autoSelectRecipeName');

      
      if (autoSelectId || autoSelectName) {
        
        // Try to find by ID first
        let recipeToSelect = autoSelectId ? recipes.find(r => 
          String(r.id) === String(autoSelectId) || 
          String(r.menu_item_id) === String(autoSelectId) ||
          String(r.menuItemId) === String(autoSelectId) ||
          String(r.recipe_id) === String(autoSelectId)
        ) : null;
        
        // Fallback: try to find by name if ID matching failed (case-insensitive, trimmed)
        if (!recipeToSelect && autoSelectName) {
          const searchName = autoSelectName.toLowerCase().trim();
          recipeToSelect = recipes.find(r => 
            r.name && r.name.toLowerCase().trim() === searchName
          );
          if (recipeToSelect) {
            debugLog(' Found recipe by name:', recipeToSelect.name);
          } else {
            // Try partial match as last resort
            recipeToSelect = recipes.find(r => 
              r.name && (
                r.name.toLowerCase().includes(searchName) ||
                searchName.includes(r.name.toLowerCase())
              )
            );
          }
        }
        
        if (recipeToSelect) {
          setSelectedRecipe({
            ...recipeToSelect,
            image: recipeToSelect.image_url || recipeToSelect.image
          });
        }
        
        // Clear the localStorage flags
        localStorage.removeItem('autoSelectRecipeId');
        localStorage.removeItem('autoSelectRecipeName');
      }
      setAutoSelectAttempted(true);
    }
  }, [recipes, autoSelectAttempted]);

  // Generate image when a recipe without an image is selected
  useEffect(() => {
    if (!selectedRecipe) return;
    
    // Skip if already has an image or if we're already generating
    if (selectedRecipe.image || selectedRecipe.imageGenerating) {
      return;
    }
    
    // Skip if this is a newly created recipe (backend is generating the image)
    if (selectedRecipe.skipAutoImageGeneration) {
      return;
    }
    
    const generateRecipeImage = async () => {
      try {
        
        // Show loading state
        setSelectedRecipe(prev => ({
          ...prev,
          imageGenerating: true
        }));
        
        // Call backend API to generate image
        const apiUrl = process.env.REACT_APP_API_URL;
        const fullUrl = `${apiUrl}/api/menu-scan/generate-single-image`;
        
        const response = await fetch(fullUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dishName: selectedRecipe.name,
            businessId: UnitMappingService.getBusinessId()
          })
        });
        
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ API Error: ${errorText}`);
          throw new Error(`Failed to generate image: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success && data.imageUrl) {
          // Update the selected recipe with the new image
          setSelectedRecipe(prev => ({
            ...prev,
            image: data.imageUrl,
            image_url: data.imageUrl,
            fallbackImage: data.imageUrl,
            imageGenerating: false
          }));
          
          // Also update the recipe in the recipes list
          setRecipes(prevRecipes => 
            prevRecipes.map(r => 
              r.id === selectedRecipe.id ? { ...r, image: data.imageUrl, image_url: data.imageUrl } : r
            )
          );
          
          showToast('Image generated successfully!');
        } else {
          throw new Error('No image URL in response');
        }
      } catch (error) {
        console.error('❌ Failed to generate recipe image:', error);
        setSelectedRecipe(prev => ({
          ...prev,
          imageGenerating: false
        }));
        // Show placeholder but log error
        console.warn('Using placeholder image due to generation failure');
      }
    };
    
    generateRecipeImage();
  }, [selectedRecipe?.id, selectedRecipe?.image]); // Run when ID changes OR when image status changes

  // Load ingredients for selected recipe
  const loadRecipeIngredients = async (recipeId) => {
    try {
      const ingredients = await RecipesService.getRecipeIngredients(recipeId, UnitMappingService.getBusinessId());
      return ingredients;
    } catch (error) {
      console.error('Failed to load recipe ingredients:', error);
      return [];
    }
  };

  // 🆕 Fetch customizations and complimentary items - MenuItems first, then RecipeLibrary fallback
  useEffect(() => {
    const fetchCustomizationData = async () => {
      if (!selectedRecipe || !selectedRecipe.name) {
        return;
      }
      
      // Reset selections when switching recipes
      setSelectedCustomizations([]);
      setSelectedComplimentaryItems([]);
      
      try {
        const businessId = UnitMappingService.getBusinessId();
        const localReviewDraft = getRecipeReviewDraft(selectedRecipe, businessId);
        const menuItemId = selectedRecipe.menu_item_id || selectedRecipe.id;
        
        // 🆕 LOAD ADVANCED COMPLIMENTARY SYSTEM DATA
        await loadLinkedComplimentaryItems(menuItemId);
        
        // STEP 1: Try to fetch from MenuItems (business-specific)
        const menuItemData = await RecipesService.getRecipe(menuItemId, businessId);
        
        let complimentaryData = [];
        let addOnsData = [];
        let needsTemplateData = false;
        
        // Check if MenuItems has data
        if (menuItemData.data) {
          const item = menuItemData.data;
          
          if (item.complimentary_items && Array.isArray(item.complimentary_items) && item.complimentary_items.length > 0) {
            complimentaryData = normalizeComplimentaryItems(item.complimentary_items);
          } else {
            needsTemplateData = true;
          }
          
          if (item.add_ons && Array.isArray(item.add_ons) && item.add_ons.length > 0) {
            addOnsData = normalizeAddOns(item.add_ons);
          } else {
            needsTemplateData = true;
          }
        } else {
          needsTemplateData = true;
        }
        
        // STEP 2: If MenuItems doesn't have data, fallback to RecipeLibrary template
        if (needsTemplateData) {
          const templateData = await RecipeLibraryService.getRecipe(selectedRecipe.name, businessId);
          
          if (templateData) {
            if (!complimentaryData.length && templateData.complimentary_items && Array.isArray(templateData.complimentary_items)) {
              complimentaryData = normalizeComplimentaryItems(templateData.complimentary_items);
            }
            
            if (!addOnsData.length && templateData.add_ons && Array.isArray(templateData.add_ons)) {
              addOnsData = normalizeAddOns(templateData.add_ons);
            }
            
            // STEP 3: Copy template data to MenuItems for future use
            if (complimentaryData.length > 0 || addOnsData.length > 0) {
              try {
                await RecipesService.updateRecipeCustomizations(menuItemId, {
                  complimentary_items: complimentaryData,
                  add_ons: addOnsData
                }, businessId);
              } catch (copyError) {
                console.warn('⚠️ Failed to copy template to MenuItems:', copyError);
              }
            }
          }
        }
        
        // Set state
        setComplimentaryItems(complimentaryData);
        setCustomizations(addOnsData);
        setSelectedComplimentaryItems(
          Array.isArray(localReviewDraft?.selectedComplimentaryItems)
            ? localReviewDraft.selectedComplimentaryItems
            : complimentaryData
        );
        setSelectedCustomizations(
          Array.isArray(localReviewDraft?.selectedCustomizations)
            ? localReviewDraft.selectedCustomizations
            : addOnsData
        );
        if (typeof localReviewDraft?.descriptionText === 'string') {
          setDescriptionText(localReviewDraft.descriptionText);
        }
        
      } catch (error) {
        console.error('❌ Failed to fetch customization data:', error);
        console.error('Error details:', error.message, error.stack);
        // Set empty arrays as fallback
        setCustomizations([]);
        setComplimentaryItems([]);
      }
    };
    
    fetchCustomizationData();
  }, [selectedRecipe?.id, selectedRecipe?.menu_item_id]);

  // Reactively fetch ingredients whenever the selected recipe changes
  useEffect(() => {
    const fetchIngredients = async () => {
      if (selectedRecipe && selectedRecipe.id) {
        try {
          const businessId = UnitMappingService.getBusinessId();
          const localReviewDraft = getRecipeReviewDraft(selectedRecipe, businessId);

          if (Array.isArray(localReviewDraft?.ingredientList)) {
            const draftList = localReviewDraft.ingredientList.length > 0
              ? localReviewDraft.ingredientList
              : [{ name: 'New Ingredient', unit: '', qty10: 0, qty100: 0 }];

            setIngredientList(JSON.parse(JSON.stringify(draftList)));
            originalIngredientListRef.current = JSON.parse(JSON.stringify(draftList));
            setPlateStep(1);
            const composites = await checkForComposites(draftList);
            setDetectedComposites(composites);
            return;
          }

          const ingredients = await RecipesService.getRecipeIngredients(selectedRecipe.id, businessId);

          const list = (ingredients.length > 0
            ? ingredients.map(ing => {
              const ingredientName = ing.ingredient_name || ing.name || '';
              const nameKey = ingredientName.trim().toLowerCase();
              const std = inventoryStdUnitMap[nameKey];
              const backendUnit = String(ing.unit || '').trim();
              let unitSym = backendUnit || std || 'g';
              let perPlateQty = ing.quantity; // backend per-plate (now standard)

              // Prefer ingredient-specific kitchen conversion for display (custom unit)
              const convs = ingredientToolConversionsMap[nameKey] || [];
              const matchedConv = convs.find(c => {
                const outUnit = (c.unit || '').trim();
                return outUnit && outUnit.toLowerCase() === String(unitSym || '').trim().toLowerCase();
              });
              if (matchedConv && matchedConv.quantity > 0) {
                unitSym = matchedConv.tool;
                perPlateQty = Number((perPlateQty / matchedConv.quantity).toFixed(4));
              } else {
                // Trust backend unit when present. Only fall back to inventory standard unit
                // for legacy rows where unit may be missing.
                if (!backendUnit) {
                  if ((ing.unit === 'g' || ing.unit === 'kg') && (std === 'ml' || std === 'L')) {
                    unitSym = std;
                    perPlateQty = convertQuantity(ing.quantity, ing.unit || 'g', std);
                  }
                  if ((ing.unit === 'ml' || ing.unit === 'L') && (std === 'g' || std === 'kg')) {
                    unitSym = std;
                    perPlateQty = convertQuantity(ing.quantity, ing.unit || 'ml', std);
                  }
                }
              }

              const tenQty = Number((perPlateQty * 10).toFixed(4));
              // SP9 sacred numbers: what was saved is what you see — no auto-scaling on load.
              const norm = { value: tenQty, unit: unitSym };

              return {
                name: ingredientName,
                unit: norm.unit,
                qty10: norm.value,
                qty100: 0,
                inventoryId: ing.inventory_id || null,  // Fixed: backend returns inventory_id not inventoryId
                id: ing.id || null  // Fixed: backend returns id (recipe_ingredient_id)
              };
            })
            : [{ name: 'New Ingredient', unit: '', qty10: 0, qty100: 0 }]);

          setIngredientList(list);
          // Store original for revert on cancel
          originalIngredientListRef.current = JSON.parse(JSON.stringify(list));
          setPlateStep(1); // Always start at 1 plate
          
          // Check for composite ingredients in this recipe (but don't auto-open editor)
          const composites = await checkForComposites(list);
          // Display composite warning inline, but don't auto-open editor
          setDetectedComposites(composites);
        } catch (error) {
          console.error(`Failed to load ingredients for recipe ${selectedRecipe.id}:`, error);
          showToast('Error loading ingredients');
          setIngredientList([]);
          originalIngredientListRef.current = [];
          setDetectedComposites([]); // Clear composites on error
        }
      } else {
        setIngredientList([]); // Clear ingredients if no recipe is selected
        originalIngredientListRef.current = [];
        setDetectedComposites([]); // Clear composites when no recipe selected
      }
    };

    fetchIngredients();
  }, [selectedRecipe?.id]);

  // 🆕 Load custom multipliers for this business from DATABASE
  useEffect(() => {
    const loadCustomMultipliers = async () => {
      try {
        const businessId = UnitMappingService.getBusinessId();
        const token = localStorage.getItem('token');
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/portion-multipliers`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'x-business-id': businessId
          }
        });
        
        const data = await response.json();
        
        if (data.success && data.multipliers) {
          setCustomMultipliers(data.multipliers);
        }
      } catch (error) {
        console.error('Failed to load custom multipliers from DB:', error);
        // Fallback to localStorage if DB fails
        try {
          const businessId = UnitMappingService.getBusinessId();
          const storageKey = `customMultipliers_${businessId}`;
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            const parsed = JSON.parse(stored);
            setCustomMultipliers(parsed);
          }
        } catch (localError) {
          console.error('Fallback to localStorage also failed:', localError);
        }
      }
    };
    
    loadCustomMultipliers();
  }, []);

  // 🆕 Save custom multipliers to DATABASE
  const saveCustomMultipliers = async (multipliers) => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/portion-multipliers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        },
        body: JSON.stringify({ multipliers })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCustomMultipliers(multipliers);
        showToast(' Multipliers saved to database successfully!');
        
        // Also save to localStorage as backup
        const storageKey = `customMultipliers_${businessId}`;
        localStorage.setItem(storageKey, JSON.stringify(multipliers));
      } else {
        throw new Error(data.error || 'Failed to save multipliers');
      }
    } catch (error) {
      console.error('Failed to save custom multipliers to DB:', error);
      showToast('❌ Failed to save multipliers to database');
      throw error;
    }
  };

  // Delete recipe handler
  const deleteRecipe = async (id) => {
    setRecipeToDelete(id);
    setShowDeleteConfirmModal(true);
  };
  
  const handleConfirmDelete = async () => {
    const id = recipeToDelete;
    setShowDeleteConfirmModal(false);
    setRecipeToDelete(null);
    
    if (!id) return;
    
    try {
      const businessId = UnitMappingService.getBusinessId();
      const result = await RecipesService.deleteRecipe(id, businessId);
      clearRecipeReviewDraft({ id }, businessId);
      
      // Refresh recipe list from server to get updated data
      const refreshedRecipes = await RecipesService.getRecipes(businessId);
      setRecipes(applyLocalReviewDrafts(refreshedRecipes));
      
      if (result.softDelete) {
        setDeleteModalMessage('Item Removed Successfully');
        setShowDeleteSuccessModal(true);
      } else {
        setDeleteModalMessage('Item Deleted Successfully');
        setShowDeleteSuccessModal(true);
      }
    } catch (error) {
      console.error('❌ Failed to delete recipe:', error);
      
      try {
        const businessId = UnitMappingService.getBusinessId();
        const refreshedRecipes = await RecipesService.getRecipes(businessId);
        setRecipes(applyLocalReviewDrafts(refreshedRecipes));
      } catch (refreshError) {
        console.error('Failed to refresh recipe list:', refreshError);
      }
      
      if (error.response?.data?.constraint) {
        setDeleteModalMessage('Item Removed Successfully');
        setShowDeleteSuccessModal(true);
      } else {
        setDeleteModalMessage('Failed To Delete Recipe');
        setShowDeleteSuccessModal(true);
      }
    }
  };

  // 🆕 Get multiplier for a portion keyword (custom or default)
  const getMultiplierForKeyword = (keyword) => {
    const businessId = UnitMappingService.getBusinessId();
    const keyLower = keyword.toLowerCase();
    
    // Check if business has custom multiplier for this keyword
    if (customMultipliers[keyLower] !== undefined) {
      return customMultipliers[keyLower];
    }
    
    // Fall back to default multipliers
    return calculatePortionMultiplier(keyword);
  };

  const filteredRecipes = sortBySearchPriority(
    recipes.filter((recipe) => {
      const orig = editingOriginalsRef.current[recipe.id];
      const status = orig ? orig.status : recipe.status;
      const key = useMasterCategories ? 'master_category' : 'category';
      const category = orig ? orig[key] : recipe[key];

      if (filter === 'All') return true;
      if (filter === 'Pending') return status !== 'confirmed';
      if (filter === 'Under Review') return status === 'draft';
      return (category || 'Uncategorized') === filter;
    }),
    search,
    (recipe) => {
      const orig = editingOriginalsRef.current[recipe.id];
      return orig ? orig.name : (recipe?.name || '');
    },
    { sortWhenEmpty: true }
  );

  const categoryFilterOptions = ['All', ...categories.filter(c => !['Beverages', 'Sides'].includes(c)), 'Under Review', 'Pending'];
  const isCategoryCollapsed = hasCategoryChipOverflow && !showAllCategoryChips;
  const visibleCategoryOptions = isCategoryCollapsed
    ? categoryFilterOptions.slice(0, collapsedCategoryCount || categoryFilterOptions.length)
    : categoryFilterOptions;

  useEffect(() => {
    setShowAllCategoryChips(false);
  }, [categories]);

  useEffect(() => {
    const measureCategoryRows = () => {
      const measureContainer = categoryMeasureRef.current;
      if (!measureContainer) return;

      const chips = Array.from(measureContainer.querySelectorAll('[data-measure-chip="true"]'));
      const viewAllChip = measureContainer.querySelector('[data-measure-view-all="true"]');

      if (!chips.length || !viewAllChip) {
        setHasCategoryChipOverflow(false);
        setCollapsedCategoryCount(categoryFilterOptions.length);
        return;
      }

      const getRowCount = (elements) => {
        const rowTops = [];
        elements.forEach((el) => {
          if (el.style.display === 'none') return;
          const top = Math.round(el.offsetTop);
          if (!rowTops.some((existingTop) => Math.abs(existingTop - top) <= 2)) {
            rowTops.push(top);
          }
        });
        return rowTops.length;
      };

      chips.forEach((chip) => { chip.style.display = ''; });
      viewAllChip.style.display = 'none';

      const contentOverflowsTwoRows = getRowCount(chips) > 2;
      setHasCategoryChipOverflow((prev) => (prev === contentOverflowsTwoRows ? prev : contentOverflowsTwoRows));

      if (!contentOverflowsTwoRows) {
        setCollapsedCategoryCount((prev) => (prev === chips.length ? prev : chips.length));
        return;
      }

      let maxVisibleCount = 0;
      for (let count = chips.length; count >= 0; count -= 1) {
        chips.forEach((chip, idx) => {
          chip.style.display = idx < count ? '' : 'none';
        });
        viewAllChip.style.display = '';

        const rowsWithToggle = getRowCount([...chips, viewAllChip]);
        if (rowsWithToggle <= 2) {
          maxVisibleCount = count;
          break;
        }
      }

      setCollapsedCategoryCount((prev) => (prev === maxVisibleCount ? prev : maxVisibleCount));

      chips.forEach((chip) => { chip.style.display = ''; });
      viewAllChip.style.display = 'none';
    };

    const frameId = window.requestAnimationFrame(measureCategoryRows);
    const timeoutId = window.setTimeout(measureCategoryRows, 80);
    window.addEventListener('resize', measureCategoryRows);
    let resizeObserver;
    if (window.ResizeObserver && categoryMeasureRef.current) {
      resizeObserver = new window.ResizeObserver(measureCategoryRows);
      resizeObserver.observe(categoryMeasureRef.current);
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', measureCategoryRows);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [categoryFilterOptions.join('|')]);

  useEffect(() => {
    if (showAllCategoryChips || !hasCategoryChipOverflow) return;

    const frameId = window.requestAnimationFrame(() => {
      const visibleContainer = categoryChipsRef.current;
      if (!visibleContainer) return;

      const toggleChip = visibleContainer.querySelector('[data-category-toggle="true"]');
      if (!toggleChip) return;

      const visibleElements = Array.from(
        visibleContainer.querySelectorAll('[data-visible-category-chip="true"], [data-category-toggle="true"]')
      );

      const rowTops = [];
      visibleElements.forEach((el) => {
        const top = Math.round(el.offsetTop);
        if (!rowTops.some((existingTop) => Math.abs(existingTop - top) <= 2)) {
          rowTops.push(top);
        }
      });

      rowTops.sort((a, b) => a - b);

      const toggleTop = Math.round(toggleChip.offsetTop);
      const toggleRowIndex = rowTops.findIndex((rowTop) => Math.abs(rowTop - toggleTop) <= 2);
      const toggleRow = toggleRowIndex >= 0 ? toggleRowIndex + 1 : 1;

      if (toggleRow > 2 && collapsedCategoryCount > 0) {
        setCollapsedCategoryCount((prev) => Math.max(prev - 1, 0));
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [visibleCategoryOptions.length, hasCategoryChipOverflow, showAllCategoryChips, collapsedCategoryCount]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target.result);
        setShowNewRecipeForm(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const createNewRecipes = async () => {
    // Check for duplicate names in the new batch or against existing recipes
    const namesInNewBatch = recipeCards.map(c => c.name ? c.name.trim().toLowerCase() : '');
    const existingNames = recipes.map(r => r.name.trim().toLowerCase());
    const nonMockNames = namesInNewBatch.filter(Boolean);
    
    const hasDuplicatesInBatch = nonMockNames.some((val, idx) => nonMockNames.indexOf(val) !== idx);
    const hasExistingDuplicates = nonMockNames.some(name => existingNames.includes(name));
    
    if (hasDuplicatesInBatch || hasExistingDuplicates) {
      setShowDuplicateModal(true);
      return;
    }

    const errors = {};
    let hasError = false;
    recipeCards.forEach(card => {
      const nameInvalid = !card.name || !card.name.trim();
      const priceInvalid = card.price === '' || card.price === undefined || card.price === null || isNaN(parseFloat(card.price));
      const servingsInvalid = card.servings === '' || card.servings === undefined || card.servings === null || isNaN(parseInt(card.servings));
      
      if (nameInvalid || priceInvalid || servingsInvalid) {
        hasError = true;
        errors[card.id] = {
          name: nameInvalid,
          price: priceInvalid,
          servings: servingsInvalid
        };
      }
    });

    if (hasError) {
      setCreationErrors(errors);
      showToast('All fields (Item Name, Price, and Servings) are required for all items.', 'warning');
      return;
    }

    const validRecipes = recipeCards;

    let isWaitingForImage = false; // Flag to prevent finally from dismissing loading during image polling

    try {
      setIsCreatingRecipes(true);
      setRecipeCreationProgress({ current: 0, total: 6, message: 'Starting recipe creation...' });
      
      // If only one recipe, navigate directly to ingredient mapping with multilingual support
      if (validRecipes.length === 1) {
        isWaitingForImage = true; // Set flag since we'll be polling for image
        const card = validRecipes[0];
        const originalName = card.name; // Keep original language name for display
        
        
        setRecipeCreationProgress({ current: 1, total: 6, message: `Translating recipe name...` });
        
        // Detect language and translate to English for DB storage and image generation
        let englishName = originalName;
        let sourceLanguage = 'en';
        
        try {
          const apiUrl = process.env.REACT_APP_API_URL;
          const translateResponse = await fetch(`${apiUrl}/medium-priority/translate-to-english`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: originalName })
          });
          
          if (translateResponse.ok) {
            const translateData = await translateResponse.json();
            if (translateData.success) {
              englishName = translateData.translatedText;
              sourceLanguage = translateData.sourceLanguage;
            }
          }
        } catch (translateError) {
          console.warn('Translation failed, using original name:', translateError);
        }
        
        // Create recipe with English name in DB
        const recipeData = {
          name: englishName, // Store English name in DB
          displayName: originalName, // Store original name for display
          sourceLanguage: sourceLanguage, // Store detected language
          price: parseFloat(card.price) || 0,
          servings: parseInt(card.servings) || 1,
          ingredients: [],
          source: 'manual' // Flag for backend to generate image
        };
        
        
        setRecipeCreationProgress({ current: 2, total: 6, message: `Creating recipe: ${originalName}` });
        
        // Create the recipe with duplicate check
        let newRecipe;
        try {
          newRecipe = await RecipesService.createRecipe(recipeData, UnitMappingService.getBusinessId());
        } catch (createError) {
          const status = createError?.response?.status || createError?.status;
          // Handle invalid dish name error
          if (status === 400) {
            const errorData = createError?.response?.data || createError?.data;
            setIsCreatingRecipes(false);
            setRecipeCreationProgress({ current: 0, total: 0, message: '' });
            showToast(`❌ Invalid dish name: ${errorData?.details || 'Please enter a proper dish name.'}`, 'error');
            return;
          }
          // Handle duplicate item error
          if (status === 409) {
            const errorData = createError?.response?.data || createError?.data;
            setIsCreatingRecipes(false);
            setRecipeCreationProgress({ current: 0, total: 0, message: '' });
            showToast(`❌ Duplicate Item: An item named "${errorData?.existingItem?.name || englishName}" already exists.`, 'warning');
            return;
          }
          throw createError;
        }
        
        const businessId = UnitMappingService.getBusinessId();
        
        setRecipeCreationProgress({ current: 3, total: 6, message: 'Generating dish image...' });
        
        //  Image generation happens automatically in backend (fire-and-forget)
        
        // 🆕 AUTO-GENERATE INGREDIENTS (exactly like OCR scan flow)
        setRecipeCreationProgress({ current: 4, total: 6, message: '' });
        
        let generatedIngredients = [];
        let generatedComplimentaryItems = [];
        let generatedAddOns = [];
        let generatedDescription = '';
        try {
          const aiResponse = await fetch(`${process.env.REACT_APP_API_URL}/ai/generate-ingredients`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'x-business-id': businessId 
            },
            body: JSON.stringify({ 
              dishName: englishName, 
              plateCount: 10 
            })
          });
          
          const aiData = await aiResponse.json();
          if (aiData.success && aiData.ingredients && aiData.ingredients.length > 0) {
            
            // Convert AI format to per-plate format (divide by 10 for storage)
            generatedIngredients = aiData.ingredients.map(ing => ({
              name: ing.ingredient || ing.name,
              quantity: Number((parseFloat(ing.quantity_standard) / 10).toFixed(4)),
              unit: (ing.quantity_standard || '').replace(/[0-9.]/g, '').trim() || 'g'
            }));
            
            // Store complimentary items, add-ons, and description
            generatedComplimentaryItems = aiData.complimentary_items || [];
            generatedAddOns = aiData.add_ons || [];
            generatedDescription = aiData.description || '';
            
            // 🆕 UPDATE RECIPE WITH DESCRIPTION (before saving ingredients)
            if (generatedDescription) {
              try {
                await RecipesService.updateRecipe(newRecipe.id, {
                  description: generatedDescription
                }, businessId);
              } catch (descError) {
                console.warn('⚠️ Failed to save description:', descError);
              }
            }
            
            // 🆕 SAVE INGREDIENTS TO DATABASE (exactly like OCR scan flow)
            await RecipesService.updateRecipeIngredients(newRecipe.id, generatedIngredients, businessId);
            
            // Update local state for complimentary items and add-ons (no RecipeLibrary update)
            if (generatedComplimentaryItems.length > 0 || generatedAddOns.length > 0) {
              setComplimentaryItems(generatedComplimentaryItems);
              setCustomizations(generatedAddOns);
            }
            
          } else {
            console.warn(`⚠️ No recipe data generated for ${englishName}`);
          }
        } catch (aiError) {
          console.error('AI recipe generation failed:', aiError);
          console.warn('Continuing with empty recipe data');
        }
        
        // 🆕 REFRESH RECIPE LIST (to show newly created recipe with image)
        setRecipeCreationProgress({ current: 5, total: 6, message: 'Finalizing recipe...' });
        const refreshedRecipes = await RecipesService.getRecipes(businessId);
        setRecipes(applyLocalReviewDrafts(refreshedRecipes));

        // Reset form
        setShowNewRecipeForm(false);
        setRecipeCards([{ id: Date.now(), name: '', price: '', servings: '' }]);
        setCapturedImage(null);

        // 🆕 AUTO-SELECT THE NEW RECIPE (exactly like OCR scan flow)
        // Find the recipe in the refreshed list - use menu_item_id for matching
        const createdRecipeWithImage = refreshedRecipes.find(r => r.menu_item_id === newRecipe.id || r.id === newRecipe.id);
        if (createdRecipeWithImage) {
          // Set the recipe with proper structure so useEffect loads ingredients
          // Mark as newly created so frontend doesn't try to generate image (backend is doing it)
          setSelectedRecipe(withImageMeta({
            ...createdRecipeWithImage,
            id: createdRecipeWithImage.menu_item_id || createdRecipeWithImage.id,
            skipAutoImageGeneration: true // Backend is generating the image
          }));
        } else {
          // Fallback: if not found in list, create minimal structure
          setSelectedRecipe(withImageMeta({
            id: newRecipe.id,
            menu_item_id: newRecipe.id,
            name: englishName,
            displayName: originalName,
            sourceLanguage: sourceLanguage,
            image_url: newRecipe.image_url || null,
            image: newRecipe.image_url || null,
            skipAutoImageGeneration: true // Backend is generating the image
          }));
        }
        
        // 🆕 POLL FOR BACKEND-GENERATED IMAGE (check every 2 seconds for 30 seconds)
        setRecipeCreationProgress({ current: 5, total: 6, message: 'Waiting for dish image...' });
        
        let pollCount = 0;
        const maxPolls = 15;
        const pollInterval = setInterval(async () => {
          pollCount++;
          try {
            const updatedRecipes = await RecipesService.getRecipes(businessId);
            const updatedRecipe = updatedRecipes.find(r => r.menu_item_id === newRecipe.id || r.id === newRecipe.id);
            
            if (updatedRecipe?.image_url) {
              // Update both selected recipe and recipes list
              setSelectedRecipe(prev => withImageMeta({
                ...prev,
                image: updatedRecipe.image_url,
                image_url: updatedRecipe.image_url,
                skipAutoImageGeneration: false
              }));
              setRecipes(applyLocalReviewDrafts(updatedRecipes));
              clearInterval(pollInterval);
              
              // Dismiss loading after image is ready
              setRecipeCreationProgress({ current: 6, total: 6, message: 'Complete!' });
              setTimeout(() => {
                setIsCreatingRecipes(false);
                setRecipeCreationProgress({ current: 0, total: 0, message: '' });
              }, 500);
              
              showToast(' Recipe created with image!');
            } else if (pollCount >= maxPolls) {
              console.warn('⚠️ Backend image generation timeout');
              clearInterval(pollInterval);
              // Remove skip flag so user can manually trigger if needed
              setSelectedRecipe(prev => ({
                ...prev,
                skipAutoImageGeneration: false
              }));
              
              // Dismiss loading after timeout
              setIsCreatingRecipes(false);
              setRecipeCreationProgress({ current: 0, total: 0, message: '' });
              showToast('⚠️ Recipe created but image is still generating...');
            }
          } catch (error) {
            console.error('Error polling for image:', error);
          }
        }, 2000);
        
        // Don't show toast yet - wait for image to load
        
      } else {
        // Multiple recipes - process each one with COMPLETE OCR flow
        const businessId = UnitMappingService.getBusinessId();
        const createdRecipes = [];
        
        for (const card of validRecipes) {
          const originalName = card.name;
          let englishName = originalName;
          let sourceLanguage = 'en';
          
          // Translate to English if needed
          try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const translateResponse = await fetch(`${apiUrl}/medium-priority/translate-to-english`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text: originalName })
            });
            
            if (translateResponse.ok) {
              const translateData = await translateResponse.json();
              if (translateData.success) {
                englishName = translateData.translatedText;
                sourceLanguage = translateData.sourceLanguage;
              }
            }
          } catch (translateError) {
            console.warn('Translation failed for', originalName);
          }
          
          const recipeData = {
            name: englishName,
            displayName: originalName,
            sourceLanguage: sourceLanguage,
            price: parseFloat(card.price) || 0,
            servings: parseInt(card.servings) || 1,
            ingredients: [],
            source: 'manual'
          };
          
          // Create recipe with duplicate check
          let newRecipe;
          try {
            newRecipe = await RecipesService.createRecipe(recipeData, businessId);
          } catch (createError) {
            const status = createError?.response?.status || createError?.status;
            // Handle invalid dish name error
            if (status === 400) {
              const errorData = createError?.response?.data || createError?.data;
              setIsCreatingRecipes(false);
              setRecipeCreationProgress({ current: 0, total: 0, message: '' });
              showToast(`❌ Invalid dish name: ${errorData?.details || 'Please enter a proper dish name.'}`, 'error');
              return;
            }
            // Handle duplicate item error
            if (status === 409) {
              console.warn(`⚠️ Skipping duplicate: ${englishName} already exists`);
              showToast(`⚠️ Skipped "${originalName}" - already exists in menu`, 'warning');
              continue;
            }
            throw createError;
          }
          
          // Generate ingredients with AI (like OCR)
          try {
            const aiResponse = await fetch(`${process.env.REACT_APP_API_URL}/ai/generate-ingredients`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'x-business-id': businessId 
              },
              body: JSON.stringify({ 
                dishName: englishName, 
                plateCount: 10 
              })
            });
            
            const aiData = await aiResponse.json();
            if (aiData.success && aiData.ingredients && aiData.ingredients.length > 0) {
              
              // Convert AI format to per-plate format (divide by 10)
              const ingredients = aiData.ingredients.map(ing => ({
                name: ing.ingredient || ing.name,
                quantity: Number((parseFloat(ing.quantity_standard) / 10).toFixed(4)),
                unit: (ing.quantity_standard || '').replace(/[0-9.]/g, '').trim() || 'g'
              }));
              
              // Save ingredients to database (like OCR)
              await RecipesService.updateRecipeIngredients(newRecipe.id, ingredients, businessId);
              
              // Save complimentary items & add-ons to Recipe Library
              if (aiData.complimentary_items?.length > 0 || aiData.add_ons?.length > 0) {
                try {
                  await RecipeLibraryService.updateRecipe(englishName, {
                    complimentary_items: aiData.complimentary_items || [],
                    add_ons: aiData.add_ons || []
                  }, businessId);
                } catch (saveError) {
                  console.warn('⚠️ Failed to save to Recipe Library:', saveError);
                }
              }
            } else {
              console.warn(`⚠️ No recipe data generated for ${englishName}`);
            }
          } catch (aiError) {
            console.error(`Failed to generate recipe data for ${englishName}:`, aiError);
          }
          
          createdRecipes.push({ original: originalName, english: englishName, id: newRecipe.id });
        }

        // Refresh recipe list to show all new recipes with images
        const refreshedRecipes = await RecipesService.getRecipes(businessId);
        setRecipes(applyLocalReviewDrafts(refreshedRecipes));

        // Reset form
        setShowNewRecipeForm(false);
        setRecipeCards([{ id: Date.now(), name: '', price: '', servings: '' }]);
        setCapturedImage(null);
        
        // Auto-select first created recipe with proper structure
        if (createdRecipes.length > 0 && refreshedRecipes.length > 0) {
          const firstCreated = refreshedRecipes.find(r => r.menu_item_id === createdRecipes[0].id || r.id === createdRecipes[0].id);
          if (firstCreated) {
            setSelectedRecipe(withImageMeta({
              ...firstCreated,
              id: firstCreated.menu_item_id || firstCreated.id,
              skipAutoImageGeneration: true // Backend is generating images
            }));
          }
        }
        
        showToast(` Created ${validRecipes.length} recipes with AI ingredients! Images generating in background...`);
      }
    } catch (error) {
      console.error('Failed to create recipes:', error);
      const errorMessage = error.message || error.toString();
      showToast(`❌ Failed to create recipes: ${errorMessage}`, 'error');
    } finally {
      // Only dismiss loading if we're not waiting for image
      if (!isWaitingForImage) {
        setIsCreatingRecipes(false);
        setRecipeCreationProgress({ current: 0, total: 0, message: '' });
      }
    }
  };

  // Use a stable incremental id to avoid potential Date.now() collisions on rapid clicks
  const nextRecipeIdRef = useRef(Date.now());
  const addMoreRecipeCard = () => {
    nextRecipeIdRef.current += 1;
    setRecipeCards(prev => [
      ...prev,
      { id: nextRecipeIdRef.current, name: '', price: '', servings: '' }
    ]);
  };

  const removeRecipeCard = (id) => {
    if (recipeCards.length > 1) {
      setRecipeCards(recipeCards.filter(card => card.id !== id));
    }
  };

  const updateRecipeCard = (id, field, value) => {
    setRecipeCards(recipeCards.map(card =>
      card.id === id ? { ...card, [field]: value } : card
    ));
    if (creationErrors[id] && creationErrors[id][field]) {
      setCreationErrors(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: false
        }
      }));
    }
  };

  // Quick Add Menu Item - Create empty menu item without AI
  const createQuickMenuItem = async () => {
    if (!quickAddItemName.trim()) {
      setQuickAddError('Please enter a menu item name');
      return;
    }

    // Check for duplicate name
    const normalizedNewName = quickAddItemName.trim().toLowerCase();
    const isDuplicate = recipes.some(r => r.name.trim().toLowerCase() === normalizedNewName);
    if (isDuplicate) {
      setShowDuplicateModal(true);
      return;
    }

    try {
      setIsCreatingQuickItem(true);
      setQuickAddError('');
      
      const businessId = UnitMappingService.getBusinessId();
      const itemName = quickAddItemName.trim();
      
      debugLog(`📝 Creating quick menu item: "${itemName}"`);
      
      // Create menu item with minimal data (no AI generation)
      const recipeData = {
        name: itemName,
        price: 0, // Default price
        servings: 1, // Default servings
        ingredients: [], // Empty ingredients - user will add manually
        source: 'manual_quick' // Flag to skip AI generation
      };
      
      debugLog('📝 Creating quick item with data:', recipeData);
      
      // Create the recipe
      const newRecipe = await RecipesService.createRecipe(recipeData, businessId);
      debugLog(' Quick menu item created:', newRecipe);
      
      // Refresh recipe list
      const refreshedRecipes = await RecipesService.getRecipes(businessId);
      setRecipes(applyLocalReviewDrafts(refreshedRecipes));
      
      // Auto-select the newly created recipe
      const createdRecipe = refreshedRecipes.find(r => 
        r.menu_item_id === newRecipe.id || r.id === newRecipe.id
      );
      
      if (createdRecipe) {
        setSelectedRecipe(withImageMeta({
          ...createdRecipe,
          id: createdRecipe.menu_item_id || createdRecipe.id,
        }));
        debugLog(` Auto-selected recipe: ${createdRecipe.name} (ID: ${createdRecipe.menu_item_id || createdRecipe.id})`);
      }
      
      // Close modal and reset form
      setShowQuickAddModal(false);
      setQuickAddItemName('');
      setQuickAddError('');
      
      showToast(` Menu item "${itemName}" created! Add ingredients below.`, 'success');
      
    } catch (error) {
      console.error('Failed to create quick menu item:', error);
      const errorMessage = error.message || error.toString();
      setQuickAddError(`Failed to create menu item: ${errorMessage}`);
    } finally {
      setIsCreatingQuickItem(false);
    }
  };

  // Keep units as entered; no auto-conversion for clarity in plate entry
  const normalizeQty = (v) => {
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) {
      return Math.round(n * 100) / 100;
    }
    return 0;
  };

  // Close plate menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (plateMenuRef.current && !plateMenuRef.current.contains(e.target)) {
        setShowPlateMenu(false);
        setShowCustomPlateInput(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper: get the display quantity for an ingredient based on current plateStep
  // Derives missing values from whichever qty is available (qty10 is the primary stored value)
  const getDisplayQty = (item) => {
    const q10 = item.qty10 || 0;
    const q100 = item.qty100 || 0;
    const perPlate = q10 > 0 ? (q10 / 10) : (q100 > 0 ? (q100 / 100) : 0);
    
    let val = 0;
    if (plateStep === 1) val = perPlate;
    else if (plateStep === 10) val = perPlate * 10;
    else if (plateStep === 100) val = perPlate * 100;
    else if (perPlate > 0) val = perPlate * plateStep;
    
    return Number(val.toFixed(2));
  };

  // Helper: given a value entered for current plateStep, compute both qty10 and qty100
  const setQtyForPlateStep = (item, value) => {
    const v = Number(value) || 0;
    const step = Number(plateStep) > 0 ? Number(plateStep) : 1;
    if (step === 1) {
      return { ...item, qty10: Number((v * 10).toFixed(4)), qty100: Number((v * 100).toFixed(4)) };
    }
    if (step === 10) {
      return { ...item, qty10: v, qty100: Number((v * 10).toFixed(4)) };
    }
    if (step === 100) {
      return { ...item, qty100: v, qty10: Number((v / 10).toFixed(4)) };
    }
    const perPlate = v / step;
    return { ...item, qty10: Number((perPlate * 10).toFixed(4)), qty100: Number((perPlate * 100).toFixed(4)) };
  };

  // Unit conversion helper: supports g<->kg, ml<->L, and weight<->volume with 1:1 density fallback
  // Delegates to THE frontend converter (unitDisplay.convert). The old body assumed a
  // 1:1 density across g↔ml; cross-family now keeps the number unchanged and warns —
  // the server-side gate refuses any cross-family save, so the number never persists
  // in the wrong family.
  const convertQuantity = (value, fromUnit, toUnit) => {
    const numeric = Number(value) || 0;
    if (!fromUnit || !toUnit || fromUnit === toUnit) return numeric;
    const converted = convert(numeric, fromUnit, toUnit);
    if (converted === null) {
      console.warn(`⚠️ [UNITS] Cannot convert ${numeric} ${fromUnit} → ${toUnit} (different families); keeping the number unchanged`);
      return numeric;
    }
    return converted;
  };

  // SP9: auto-prettify deleted — your number is sacred (see itemmap.js note).


  const presetPlateSteps = [1, 10, 100];
  const isCustomPlateStep = !presetPlateSteps.includes(Number(plateStep));

  const applyPlateStepChange = (nextStep) => {
    const normalized = Number(nextStep);
    if (!Number.isFinite(normalized) || normalized <= 0) {
      showToast('Enter a valid plate count');
      return;
    }

    setIngredientList(prev => prev.map(item => {
      const q10 = item.qty10 || 0;
      const q100 = item.qty100 || 0;
      const effQ10 = q10 > 0 ? q10 : (q100 > 0 ? Number((q100 / 10).toFixed(4)) : 0);
      const effQ100 = q100 > 0 ? q100 : (q10 > 0 ? Number((q10 * 10).toFixed(4)) : 0);
      // SP9 sacred numbers: a plate-view switch scales the DISPLAY from the stored
      // per-plate value — it never flips the row's unit.
      return { ...item, qty10: effQ10, qty100: effQ100 };
    }));

    setPlateStep(normalized);
    setShowCustomPlateInput(false);
    setShowPlateMenu(false);
    showToast(`Showing for ${normalized} ${normalized === 1 ? 'plate' : 'plates'}`);
  };

  const applyCustomPlateStep = () => {
    const parsed = Number(customPlateStep);
    if (!Number.isFinite(parsed) || parsed <= 0 || !Number.isInteger(parsed)) {
      showToast('Enter a valid custom plate count');
      return;
    }
    applyPlateStepChange(parsed);
  };

  const updateQuantity = (index, value, recordHistory = false) => {
    const prevList = cloneIngredientList(ingredientList);
    const updated = [...ingredientList];
    const qty = normalizeQty(value);
    
    // Check if per plate quantity is rounded off to zero
    const step = Number(plateStep) > 0 ? Number(plateStep) : 1;
    const perPlateVal = qty / step;
    if (qty > 0 && perPlateVal <= 0.0001 && recordHistory) {
      setShowRoundOffModal(true);
      showToast("Round off to zero. Costing cannot be applied.", "warning");
    }

    // SP9 sacred numbers: the typed value is stored as typed — no auto-scaling, no unit
    // flip under the user's fingers.
    const computed = setQtyForPlateStep(updated[index], qty);
    updated[index].qty10 = computed.qty10;
    updated[index].qty100 = computed.qty100;
    setIngredientList(updated);
    if (recordHistory) {
      pushIngredientHistory('update', prevList, cloneIngredientList(updated));
    }
  };

  const handleRowTouchStart = (index, event) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    const target = event.target;
    const isControl = target.closest(
      '.controls-inline, .number-input, .text-input, .square-btn, [role="button"], input, select, button, .suggestions'
    );
    if (isControl) return;
    const touch = event.touches[0];
    setSwipingIndex(index);
    swipeStateRef.current = {
      index,
      startX: touch.clientX,
      startY: touch.clientY,
      endX: touch.clientX,
      endY: touch.clientY
    };
  };

  const handleRowTouchMove = (index, event) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    if (swipeStateRef.current.index !== index) return;
    const touch = event.touches[0];
    swipeStateRef.current.endX = touch.clientX;
    swipeStateRef.current.endY = touch.clientY;
    const deltaX = touch.clientX - swipeStateRef.current.startX;
    const bounded = Math.max(-120, Math.min(120, deltaX));
    setSwipeOffsets(prev => ({ ...prev, [index]: bounded }));
  };

  const handleRowTouchEnd = (index) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    const swipe = swipeStateRef.current;
    if (swipe.index !== index) return;
    const deltaX = swipe.endX - swipe.startX;
    const deltaY = swipe.endY - swipe.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const shouldDelete = absX > 60 && absX > absY * 1.5;
    if (shouldDelete && editingIngredientIndex !== index) {
      const exitOffset = deltaX > 0 ? 160 : -160;
      setSwipeOffsets(prev => ({ ...prev, [index]: exitOffset }));
      setTimeout(() => {
        deleteIngredient(index);
        setSwipeOffsets(prev => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
      }, 120);
    } else {
      setSwipeOffsets(prev => ({ ...prev, [index]: 0 }));
    }
    setSwipingIndex(null);
    swipeStateRef.current = {};
  };

  const startEditingIngredient = async (index) => {
    if (editingIngredientIndex !== null && editingIngredientIndex !== undefined && editingIngredientIndex !== index) {
      const hasName = formData.name && formData.name.trim() && formData.name.trim() !== 'New Ingredient';
      if (hasName) {
        await saveIngredient({ silent: true });
      } else {
        cancelEditing();
      }
    }
    const ingredient = ingredientList[index];
    if (!ingredient) return;
    setEditingIngredientIndex(index);
    const qty = getDisplayQty(ingredient);
    const ingredientName = ingredient.name === 'New Ingredient' ? '' : (ingredient.name || '');
    setFormData({ name: ingredientName, quantity: qty ?? 0, unit: ingredient.unit || 'g' });
    // Open suggestions immediately so typed names always get dropdown matches.
    setShowNameSuggestions(true);
  };

  // Autocomplete suggestions for inventory items, saved preparations, and menu items
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [preparationSuggestions, setPreparationSuggestions] = useState([]);
  const [menuSuggestions, setMenuSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showImportConfirmModal, setShowImportConfirmModal] = useState(false);
  const [selectedMenuItemForImport, setSelectedMenuItemForImport] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  
  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!editingIngredientIndex && editingIngredientIndex !== 0) return;
      
      const term = formData.name?.trim();
      const businessId = UnitMappingService.getBusinessId();
      const localInventorySuggestions = (inventoryItemNames || []).map((name) => {
        const key = String(name || '').trim().toLowerCase();
        return {
          id: `local-${key}`,
          name,
          standardUnit: inventoryStdUnitMap[key] || null
        };
      });
      
      // Map saved preparations to the suggestion format
      const savedPrepSuggestions = (savedPreparations || []).map(prep => {
        const name = prep.composite_name || prep.name || '';
        const standardUnit = prep.total_weight_unit_symbol || prep.total_weight_unit_name || 'g';
        return {
          id: prep.id ? `prep-${prep.id}` : `prep-${name}`,
          name: name,
          standardUnit: standardUnit
        };
      });
      
      // If empty or 'New Ingredient', show all inventory items and menu items
      if (!term || term === 'New Ingredient') {
        try {
          const localRanked = sortBySearchPriority(
            localInventorySuggestions,
            '',
            (item) => item?.name || '',
            { limit: 50 }
          );
          let mergedItems = localRanked;

          if (businessId) {
            const items = await InventoryService.searchInventoryItems('', businessId, 50);
            const apiRanked = sortBySearchPriority(items || [], '', (item) => item?.name || '', { limit: 50 });
            const byName = new Map();
            [...localRanked, ...apiRanked].forEach((item) => {
              const key = (item?.name || '').trim().toLowerCase();
              if (key && !byName.has(key)) byName.set(key, item);
            });
            mergedItems = Array.from(byName.values()).slice(0, 50);
          }

          const prepItems = sortBySearchPriority(savedPrepSuggestions, '', (prep) => prep?.name || '', { limit: 50 });
          const menuItems = sortBySearchPriority(recipes, '', (recipe) => recipe?.name || '', { limit: 50 });
          if (active) {
            setNameSuggestions(mergedItems);
            setPreparationSuggestions(prepItems);
            setMenuSuggestions(menuItems);
          }
        } catch (e) {
          console.warn('Failed to load suggestions', e);
        }
        return;
      }
      
      // Search with term
      try {
        const localRanked = sortBySearchPriority(
          localInventorySuggestions,
          term,
          (item) => item?.name || '',
          { limit: 50 }
        );
        let mergedItems = localRanked;

        if (businessId) {
          const items = await InventoryService.searchInventoryItems(term, businessId, 200);
          const apiRanked = sortBySearchPriority(items || [], term, (item) => item?.name || '', { limit: 50 });
          const byName = new Map();
          [...localRanked, ...apiRanked].forEach((item) => {
            const key = (item?.name || '').trim().toLowerCase();
            if (key && !byName.has(key)) byName.set(key, item);
          });
          mergedItems = Array.from(byName.values()).slice(0, 50);
        }

        const prepItems = sortBySearchPriority(savedPrepSuggestions, term, (prep) => prep?.name || '', { limit: 50 });
        const menuItems = sortBySearchPriority(recipes, term, (recipe) => recipe?.name || '', { limit: 50 });
        if (active) {
          setNameSuggestions(mergedItems);
          setPreparationSuggestions(prepItems);
          setMenuSuggestions(menuItems);
        }
      } catch (e) {
        console.warn('Search failed', e);
      }
    };
    run();
    return () => { active = false; };
  }, [formData.name, editingIngredientIndex, recipes, inventoryItemNames, inventoryStdUnitMap, savedPreparations]);

  const pickNameSuggestion = (s) => {
    setFormData(prev => ({ ...prev, name: s.name }));
    if (s.standardUnit) setFormData(prev => ({ ...prev, unit: s.standardUnit }));
    setShowNameSuggestions(false);
    setTimeout(() => {
      if (quantityInputRef.current) {
        quantityInputRef.current.focus();
        quantityInputRef.current.select();
      }
    }, 100);
  };
  
  const pickMenuItemSuggestion = async (menuItem) => {
    setSelectedMenuItemForImport(menuItem);
    setShowImportConfirmModal(true);
  };
  
  const confirmDeleteAllIngredients = () => {
    if (ingredientList.length === 0) {
      showToast('No ingredients to delete', 'warning');
      setShowDeleteAllModal(false);
      return;
    }

    // Save current state to history before deleting all
    const newHistory = ingredientHistory.slice(0, historyIndex + 1);
    newHistory.push({ 
      action: 'deleteAll', 
      previousList: [...ingredientList]
    });
    setIngredientHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);

    // Clear all ingredients
    setIngredientList([]);
    showToast(' All ingredients deleted', 'success');
    setShowDeleteAllModal(false);
  };
  
  const confirmImportIngredients = async () => {
    if (!selectedMenuItemForImport) return;
    
    try {
      showToast('Importing ingredients...');
      const businessId = UnitMappingService.getBusinessId();
      
      // Fetch ingredients from the selected menu item
      const ingredients = await RecipesService.getRecipeIngredients(selectedMenuItemForImport.id, businessId);
      
      if (!ingredients || ingredients.length === 0) {
        showToast('No ingredients found in selected item', 'warning');
        setShowImportConfirmModal(false);
        setSelectedMenuItemForImport(null);
        return;
      }
      
      
      // Convert imported ingredients to the format used by ingredientList
      const importedList = ingredients.map(ing => {
        const ingredientName = ing.ingredient_name || ing.name || '';
        const nameKey = ingredientName.trim().toLowerCase();
        const std = inventoryStdUnitMap[nameKey];
        let unitSym = ing.unit || std || 'g';
        let perPlateQty = ing.quantity; // backend per-plate quantity
        
        // Apply unit conversions if needed
        if ((ing.unit === 'g' || ing.unit === 'kg') && (std === 'ml' || std === 'L')) {
          unitSym = std;
          perPlateQty = convertQuantity(ing.quantity, ing.unit || 'g', std);
        }
        if ((ing.unit === 'ml' || ing.unit === 'L') && (std === 'g' || std === 'kg')) {
          unitSym = std;
          perPlateQty = convertQuantity(ing.quantity, ing.unit || 'ml', std);
        }
        
        const tenQty = Number((perPlateQty * 10).toFixed(4));
        // SP9 sacred numbers: what was saved is what you see — no auto-scaling on load.
        const norm = { value: tenQty, unit: unitSym };
        
        return {
          name: ingredientName,
          unit: norm.unit,
          qty10: norm.value,
          qty100: 0,
          inventoryId: ing.inventory_id || null,
          id: ing.id || null
        };
      });
      
      const normalizeIngredientName = (value) => String(value || '').trim().toLowerCase();

      // The row the user typed the name into is only a placeholder for this import.
      // Drop it while it is still blank so it cannot shadow an imported ingredient
      // that happens to share its name.
      const baseList = ingredientList.filter((ing, idx) => {
        if (idx !== editingIngredientIndex) return true;
        return Number(ing.qty10 || 0) > 0 || Number(ing.qty100 || 0) > 0;
      });

      const existingNames = new Set(
        baseList.map((ing) => normalizeIngredientName(ing.name)).filter(Boolean)
      );

      // Append only ingredients that are not already mapped. Anything already in
      // the list keeps the quantity the user entered — an import never rewrites it.
      const addedRows = [];
      const skippedNames = [];

      importedList.forEach((ing) => {
        const key = normalizeIngredientName(ing.name);
        if (!key) return;
        if (existingNames.has(key)) {
          skippedNames.push(ing.name);
          return;
        }
        existingNames.add(key);
        addedRows.push({ ...ing });
      });

      const finalList = [...baseList, ...addedRows];

      debugLog(`✓ Import: ${addedRows.length} added, ${skippedNames.length} skipped as already mapped`);
      addedRows.forEach((ing) => debugLog(`  + ${ing.name}: ${ing.qty10}${ing.unit}`));
      skippedNames.forEach((name) => debugLog(`  = ${name} (kept existing quantity)`));

      if (addedRows.length === 0) {
        // Nothing new — leave the list, the plate step and the undo stack alone.
        showToast(
          skippedNames.length > 0
            ? 'All imported ingredients are already mapped — nothing added'
            : 'No new ingredients to import',
          'warning'
        );
      } else {
        // Save current state to history before updating
        const newHistory = ingredientHistory.slice(0, historyIndex + 1);
        newHistory.push({
          action: 'import',
          previousList: [...ingredientList],
          newList: finalList
        });
        setIngredientHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);

        // Update ingredient list
        setIngredientList(finalList);
        setPlateStep(1);

        showToast(
          skippedNames.length > 0
            ? `Added ${addedRows.length}, skipped ${skippedNames.length} already mapped`
            : `Added ${addedRows.length} ingredient${addedRows.length === 1 ? '' : 's'}`,
          'success'
        );
      }
      
      // Close modal and reset
      setShowImportConfirmModal(false);
      setSelectedMenuItemForImport(null);
      setShowNameSuggestions(false);
      setEditingIngredientIndex(null);
      setFormData({ name: '', quantity: 0, unit: '' });
      
    } catch (error) {
      console.error('Failed to import ingredients:', error);
      showToast(`❌ Failed to import ingredients: ${error.message}`, 'error');
      setShowImportConfirmModal(false);
      setSelectedMenuItemForImport(null);
    }
  };

  // Price auto-calculation removed per new flow

  const saveIngredient = async (opts = {}) => {
    const { silent = false, returnUpdatedList = false } = opts;
    // Validate all required fields
    if (!formData.name.trim() || formData.name.trim() === 'New Ingredient') {
      if (!silent) showToast('Please enter an ingredient name', 'warning');
      return returnUpdatedList ? null : false;
    }
    
    if (!formData.quantity || formData.quantity <= 0) {
      if (!silent) showToast('Please enter a valid quantity', 'warning');
      return returnUpdatedList ? null : false;
    }
    
    if (!formData.unit || formData.unit.trim() === '') {
      if (!silent) showToast('Please select a unit', 'warning');
      return returnUpdatedList ? null : false;
    }

    // Keep edit changes local; inventory rows are ensured only on final Confirm save.

    // Respect the user-selected unit while editing; only fallback when unit is empty.
    const nameKey = formData.name.trim().toLowerCase();
    const std = inventoryStdUnitMap[nameKey];
    let unitToUse = formData.unit || std || 'g';

    const updated = [...ingredientList];
    const base = { name: formData.name, unit: unitToUse };
    const qty = normalizeQty(formData.quantity);
    const computed = setQtyForPlateStep({ ...base, qty10: updated[editingIngredientIndex]?.qty10 ?? 0, qty100: updated[editingIngredientIndex]?.qty100 ?? 0 }, qty);
    updated[editingIngredientIndex] = computed;
    setIngredientList(updated);
    setEditingIngredientIndex(null);
    setFormData({ name: '', quantity: 0, unit: '' });
    setShowNameSuggestions(false);
    return returnUpdatedList ? updated : true;
  };

  const cancelEditing = () => {
    // If we're cancelling a freshly added placeholder row, remove it instead of showing "New Ingredient"
    if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
      const idx = editingIngredientIndex;
      const ing = ingredientList[idx];
      if (ing && ing.name === 'New Ingredient') {
        const updated = [...ingredientList];
        updated.splice(idx, 1);
        setIngredientList(updated);
      }
    }
    setEditingIngredientIndex(null);
    setFormData({ name: '', quantity: 0, unit: '' });
    setShowNameSuggestions(false);
  };

  const closeIngredientEditor = async () => {
    if (editingIngredientIndex === null || editingIngredientIndex === undefined) {
      setShowNameSuggestions(false);
      return;
    }

    const hasName = formData.name && formData.name.trim() && formData.name.trim() !== 'New Ingredient';
    const hasQty = Number(formData.quantity) > 0;
    const hasUnit = formData.unit && String(formData.unit).trim() !== '';

    if (hasName && hasQty && hasUnit) {
      const saved = await saveIngredient({ silent: true });
      if (saved) return;
    }

    cancelEditing();
  };

  useEffect(() => {
    if (editingIngredientIndex === null || editingIngredientIndex === undefined) return;

    const handleEditorOutsideClick = (e) => {
      const target = e.target;
      if (!target) return;

      if (ingredientEditorRef.current && ingredientEditorRef.current.contains(target)) return;
      closeIngredientEditor();
    };

    document.addEventListener('mousedown', handleEditorOutsideClick);
    document.addEventListener('touchstart', handleEditorOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleEditorOutsideClick);
      document.removeEventListener('touchstart', handleEditorOutsideClick);
    };
  }, [editingIngredientIndex, formData.name, formData.quantity, formData.unit]);

  // Delete ingredient with history tracking
  const deleteIngredient = (index) => {
    const currentList = [...ingredientList];
    const deletedItem = currentList[index];
    
    // Save current state to history
    const newHistory = ingredientHistory.slice(0, historyIndex + 1);
    newHistory.push({ action: 'delete', item: deletedItem, index, list: currentList });
    setIngredientHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    
    // Remove the ingredient
    currentList.splice(index, 1);
    setIngredientList(currentList);
    setEditingIngredientIndex(null);
  };

  // Undo last action
  const handleUndo = () => {
    if (historyIndex < 0) {
      return;
    }
    
    const action = ingredientHistory[historyIndex];
    if (action.action === 'delete') {
      // Restore deleted item
      const restored = [...ingredientList];
      restored.splice(action.index, 0, action.item);
      setIngredientList(restored);
    } else if (action.action === 'update') {
      setIngredientList([...action.previousList]);
    } else if (action.action === 'import') {
      // Restore previous list before import
      setIngredientList([...action.previousList]);
    } else if (action.action === 'deleteAll') {
      // Restore all deleted ingredients
      setIngredientList([...action.previousList]);
    }
    
    setHistoryIndex(historyIndex - 1);
  };

  // Redo last undone action
  const handleRedo = () => {
    if (historyIndex >= ingredientHistory.length - 1) {
      return;
    }
    
    const nextIndex = historyIndex + 1;
    const action = ingredientHistory[nextIndex];
    
    if (action.action === 'delete') {
      // Re-delete the item
      const updated = [...ingredientList];
      updated.splice(action.index, 1);
      setIngredientList(updated);
    } else if (action.action === 'update') {
      setIngredientList([...action.newList]);
    } else if (action.action === 'import') {
      // Re-apply the import
      setIngredientList([...action.newList]);
    } else if (action.action === 'deleteAll') {
      // Re-delete all ingredients
      setIngredientList([]);
    }
    
    setHistoryIndex(nextIndex);
  };

  // Handle complimentary item click - navigate to dedicated page
  const handleComplimentaryItemClick = async (item, event) => {
    // Handle both string and object formats for backward compatibility
    const itemName = typeof item === 'string' ? item : item.name;
    
    // If clicking on checkbox area, open confirmation modal for linked items
    if (event?.target?.type === 'checkbox' || event?.target?.closest?.('.checkbox-wrapper')) {
      if (event?.stopPropagation) event.stopPropagation();
      if (event?.preventDefault) event.preventDefault();

      const menuItemId = selectedRecipe?.menu_item_id || selectedRecipe?.id;
      if (item?.comp_recipe_id && menuItemId) {
        const isLinked = linkedComplimentaryItems.some(linked => linked.comp_recipe_id === item.comp_recipe_id);
        requestComplimentaryLinkToggle(item, isLinked, event);
        return;
      }

      setSelectedComplimentaryItems(prev => {
        if (prev.includes(itemName)) {
          return prev.filter(i => i !== itemName);
        }
        return [...prev, itemName];
      });
    } else {
      // If clicking on the item itself (not checkbox), navigate to details page
      setSelectedComplimentaryItem(itemName);
      setShowComplimentaryDropdown(false);
      setShowComplimentaryItemPage(true);
    }
  };

  // Open complimentary item for editing from dropdown row label
  const handleOpenComplimentaryEditor = (item) => {
    const nextItem = (typeof item === 'string') ? { name: item } : item;
    setSelectedComplimentaryItem(nextItem);
    setShowComplimentaryDropdown(false);
    setShowComplimentaryItemPage(true);
  };

  // Back from complimentary item page
  const handleBackFromComplimentary = () => {
    setShowComplimentaryItemPage(false);
    setSelectedComplimentaryItem(null);
  };

  // Save complimentary item ingredients
  const saveComplimentaryIngredients = async (ingredients) => {
    const itemName = typeof selectedComplimentaryItem === 'string' ? selectedComplimentaryItem : selectedComplimentaryItem.name;
    showToast(` Saved ${itemName} ingredients!`);
    // Here you can add API call to save to backend
    setShowComplimentaryItemPage(false);
    setSelectedComplimentaryItem(null);
  };

  // Handle add new complimentary item
  const handleAddNewComplimentaryItem = () => {
    setShowComplimentaryDropdown(false);
    setShowAddComplimentaryModal(true);
    setNewComplimentaryForm({ name: '', price: 0, servings: 1 });
  };

  const closeAddComplimentaryModal = () => {
    setShowAddComplimentaryModal(false);
    setNewComplimentaryForm({ name: '' });
  };

  // 🆕 Load all available complimentary recipes (library)
  const loadComplimentaryRecipesLibrary = async () => {
    try {
      setLoadingComplimentaryLibrary(true);
      const result = await FeaturesService.getComplimentaryRecipes(true); // activeOnly=true
      if (result.success && result.complimentaryRecipes) {
        setComplimentaryRecipesLibrary(result.complimentaryRecipes);
      }
    } catch (error) {
      console.error('❌ Failed to load complimentary recipes library:', error);
    } finally {
      setLoadingComplimentaryLibrary(false);
    }
  };

  // 🆕 Load linked complimentary items for a specific recipe
  const loadLinkedComplimentaryItems = async (menuItemId) => {
    try {
      const result = await FeaturesService.getLinkedComplimentaryItems(menuItemId);
      if (result.success && result.complimentaryItems) {
        setLinkedComplimentaryItems(result.complimentaryItems);
        return result.complimentaryItems;
      }
      return [];
    } catch (error) {
      console.error('❌ Failed to load linked complimentary items:', error);
      return [];
    }
  };

  // 🆕 Link/unlink complimentary recipe to current recipe
  const toggleComplimentaryLink = async (compRecipeId, currentlyLinked, itemName = '') => {
    if (!selectedRecipe) {
      throw new Error('Please select a recipe before updating complimentary items.');
    }

    const menuItemId = selectedRecipe.menu_item_id || selectedRecipe.id;
    const normalizedItemName = itemName.trim();

    if (currentlyLinked) {
      const link = linkedComplimentaryItems.find(item => item.comp_recipe_id === compRecipeId);
      if (!link?.link_id) {
        throw new Error('This complimentary item is not currently attached to the selected recipe.');
      }

      const result = await FeaturesService.unlinkComplimentaryItem(link.link_id);
      if (!result.success) {
        throw new Error(result.error || 'Failed to remove complimentary item.');
      }
    } else {
      const result = await FeaturesService.linkComplimentaryToRecipe(menuItemId, {
        compRecipeId: compRecipeId,
        scalingType: 'SCALED',
        quantityMultiplier: 1.0
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to add complimentary item.');
      }
    }

    await loadLinkedComplimentaryItems(menuItemId);

    if (normalizedItemName) {
      setSelectedComplimentaryItems(prev => (
        currentlyLinked
          ? prev.filter(entry => entry !== normalizedItemName)
          : prev.includes(normalizedItemName)
            ? prev
            : [...prev, normalizedItemName]
      ));
    }
  };

  const confirmComplimentaryLinkChange = async () => {
    const modalItem = complimentaryLinkModal.item;

    if (!modalItem?.comp_recipe_id) {
      setComplimentaryLinkModal(prev => ({
        ...prev,
        error: 'This complimentary item is not available for updating right now.'
      }));
      return;
    }

    setComplimentaryLinkModal(prev => ({
      ...prev,
      isSubmitting: true,
      error: ''
    }));

    try {
      await toggleComplimentaryLink(
        modalItem.comp_recipe_id,
        complimentaryLinkModal.currentlyLinked,
        getComplimentaryItemName(modalItem)
      );
      closeComplimentaryLinkModal();
    } catch (error) {
      console.error('❌ Failed to toggle complimentary link:', error);
      setComplimentaryLinkModal(prev => ({
        ...prev,
        isSubmitting: false,
        error: error?.message || 'Failed to update complimentary item.'
      }));
    }
  };

  // Create new complimentary item using advanced ComplimentaryRecipes system
  const createNewComplimentaryItem = async () => {
    if (!newComplimentaryForm.name.trim()) {
      showToast('⚠️ Please enter item name');
      return;
    }
    
    if (!selectedRecipe || !selectedRecipe.name) {
      showToast('⚠️ No recipe selected');
      return;
    }
    
    try {
      const businessId = UnitMappingService.getBusinessId();
      const menuItemId = selectedRecipe.menu_item_id || selectedRecipe.id;
      const newItemName = newComplimentaryForm.name.trim();
      
      
      // Step 1: Create ComplimentaryRecipe in database
      const createResult = await FeaturesService.createComplimentaryRecipe({
        name: newItemName,
        description: `Complimentary item for ${selectedRecipe.name}`,
        portionSize: '1.0', // VARCHAR field - send as string
        ingredients: [], // Start with empty ingredients, user will add via comps editor
        displayOrder: 0
      });
      
      if (!createResult.success) {
        throw new Error(createResult.error || 'Failed to create complimentary recipe');
      }
      
      const compRecipeId = createResult.data.comp_recipe_id;
      
      // Step 2: Link the complimentary recipe to the main recipe (default: FIXED scaling)
      const linkResult = await FeaturesService.linkComplimentaryToRecipe(menuItemId, {
        compRecipeId: compRecipeId,
        scalingType: 'SCALED', // Default to SCALED, quantities multiply with sold items
        quantityMultiplier: 1.0
      });
      
      if (!linkResult.success) {
        throw new Error(linkResult.error || 'Failed to link complimentary recipe');
      }
      
      
      // Step 3: Reload linked items for current recipe
      await loadLinkedComplimentaryItems(menuItemId);
      
      // Step 4: Reload complimentary recipes library
      await loadComplimentaryRecipesLibrary();
      
      showToast(` Created ${newItemName} and linked to recipe!`);
      closeAddComplimentaryModal();
      
      // Step 5: Open comps editor so user can add ingredients
      setSelectedComplimentaryItem({ 
        comp_recipe_id: compRecipeId,
        name: newItemName 
      });
      setShowComplimentaryItemPage(true);
      
    } catch (error) {
      console.error('❌ Failed to create complimentary recipe:', error);
      showToast(`❌ Failed: ${error.message}`);
    }
  };

  // Customization handlers
  const handleCustomizationClick = (customization) => {
    setSelectedCustomizations(prev => {
      if (prev.includes(customization)) {
        const updated = prev.filter(item => item !== customization);
        return updated;
      } else {
        return [...prev, customization];
      }
    });
  };

  const handleAddNewCustomization = () => {
    setShowCustomizationsDropdown(false);
    setShowAddCustomizationModal(true);
    setNewCustomizationName('');
  };

  const createNewCustomization = async () => {
    if (!newCustomizationName.trim()) {
      showToast('⚠️ Please enter customization name');
      return;
    }
    
    if (!selectedRecipe || !selectedRecipe.name) {
      showToast('⚠️ No recipe selected');
      return;
    }
    
    try {
      const businessId = UnitMappingService.getBusinessId();
      const menuItemId = selectedRecipe.menu_item_id || selectedRecipe.id;
      const newCustomization = newCustomizationName.trim();
      const updatedCustomizations = [...customizations, newCustomization];
      
      // Save to MenuItems (business-specific) instead of RecipeLibrary (global)
      await RecipesService.updateRecipeCustomizations(menuItemId, {
        add_ons: updatedCustomizations
      }, businessId);
      
      setCustomizations(updatedCustomizations);
      showToast(` Added ${newCustomization} to customizations!`);
      setShowAddCustomizationModal(false);
      setNewCustomizationName('');
      
    } catch (error) {
      console.error('Failed to save customization:', error);
      showToast('❌ Failed to save customization');
    }
  };

  // Handle OCR upload - show selected images and enable batch processing
  const handleOCRUpload = (e) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) {
      console.warn('⚠️ No files selected');
      return;
    }

    // Add new files to existing selection (accumulate multiple uploads)
    setSelectedMenuImages(prev => {
      const newImages = [...prev, ...files];
      return newImages;
    });
    
    // Clear the input so same file can be selected again
    e.target.value = '';
    
  };

  // Process menu images directly (for auto-process after upload)
  const processMenuImagesDirectly = async (images) => {
    try {
      setOcrProcessing(true);
      setMenuProcessingProgress({ current: 0, total: images.length });
      setMenuScanProgress(0);
      setScannedMenuItems([]);
      
      let allMenuItems = [];

      for (let i = 0; i < images.length; i++) {
        const file = images[i];
        setMenuProcessingProgress({ current: i + 1, total: images.length });
        

        try {
          const data = { success: false, error: "OCR Removed" };

          if (data.jobId) {

            setMenuScanJob(data.jobId);
            
            const menuItems = data.menuItems.map(item => ({ 
              name: typeof item === 'string' ? item : item.name, 
              price: typeof item === 'object' ? item.price : 0,
              status: 'pending',
              sourceFile: file.name
            }));

            setOcrResults(menuItems);

            // Start polling for this job and wait for completion
            const completedItems = await new Promise((resolve, reject) => {
              const pollInterval = setInterval(async () => {
                try {
                  const pollData = { success: false, error: "OCR Removed" };

                  if (pollData.job) {
                    const { status, progress, menuItems: polledItems, processedItems, totalItems } = pollData.job;

                    setMenuScanProgress(progress);
                    setOcrResults(polledItems.map(item => ({
                      name: item.name,
                      price: item.price || 0,
                      status: item.status,
                      ingredients: item.ingredients,
                      imageUrl: item.imageUrl,
                      error: item.error,
                      sourceFile: file.name
                    })));


                    // Check if completed or failed
                    if (status === 'completed' || status === 'failed') {
                      clearInterval(pollInterval);

                      const completed = polledItems
                        .filter(item => item.status === 'completed')
                        .map(item => ({
                          ...item,
                          sourceFile: file.name
                        }));
                      
                      resolve(completed);
                    }
                  }
                } catch (error) {
                  console.error('Polling error:', error);
                  clearInterval(pollInterval);
                  reject(error);
                }
              }, 3000);

              // Timeout after 10 minutes
              setTimeout(() => {
                clearInterval(pollInterval);
                reject(new Error('Menu scan timeout'));
              }, 600000);
            });

            allMenuItems = [...allMenuItems, ...completedItems];
            setScannedMenuItems(allMenuItems);

          } else {
            throw new Error('No job ID returned from menu scan');
          }
        } catch (error) {
          console.error(`Error processing menu image ${file.name}:`, error);
          showToast(`Failed to process ${file.name}: ${error.message}`);
        }
      }

      setOcrProcessing(false);

    } catch (error) {
      console.error('Error in processMenuImagesDirectly:', error);
      setOcrProcessing(false);
      showToast('Failed to process menu images');
    }
  };

  // Remove a selected menu image
  const removeMenuImage = (index) => {
    setSelectedMenuImages(prev => prev.filter((_, i) => i !== index));
  };

  // Clear all selected images
  const clearAllMenuImages = () => {
    setSelectedMenuImages([]);
    setMenuProcessingProgress({ current: 0, total: 0 });
  };

  // Process all selected menu images
  const processAllMenuImages = async () => {
    
    if (selectedMenuImages.length === 0) {
      console.warn('⚠️ No images selected');
      showToast('Please select at least one menu image');
      return;
    }

    try {
      setOcrProcessing(true);
      setMenuProcessingProgress({ current: 0, total: selectedMenuImages.length });
      setMenuScanProgress(0);
      setScannedMenuItems([]);
      
      let allMenuItems = [];

      for (let i = 0; i < selectedMenuImages.length; i++) {
        const file = selectedMenuImages[i];
        setMenuProcessingProgress({ current: i + 1, total: selectedMenuImages.length });
        

        try {
          const data = { success: false, error: "OCR Removed" };

          if (data.jobId) {

            setMenuScanJob(data.jobId);
            
            const menuItems = data.menuItems.map(item => ({ 
              name: typeof item === 'string' ? item : item.name, 
              price: typeof item === 'object' ? item.price : 0,
              status: 'pending',
              sourceFile: file.name
            }));

            setOcrResults(menuItems);

            // Start polling for this job and wait for completion
            const completedItems = await new Promise((resolve, reject) => {
              const pollInterval = setInterval(async () => {
                try {
                  const pollData = { success: false, error: "OCR Removed" };

                  if (pollData.job) {
                    const { status, progress, menuItems: polledItems, processedItems, totalItems } = pollData.job;

                    setMenuScanProgress(progress);
                    setOcrResults(polledItems.map(item => ({
                      name: item.name,
                      price: item.price || 0,
                      status: item.status,
                      ingredients: item.ingredients,
                      imageUrl: item.imageUrl,
                      error: item.error,
                      sourceFile: file.name
                    })));


                    // Check if completed or failed
                    if (status === 'completed' || status === 'failed') {
                      clearInterval(pollInterval);

                      const completed = polledItems
                        .filter(item => item.status === 'completed')
                        .map(item => ({
                          ...item,
                          sourceFile: file.name
                        }));
                      
                      resolve(completed);
                    }
                  }
                } catch (error) {
                  console.error('Polling error:', error);
                  clearInterval(pollInterval);
                  reject(error);
                }
              }, 3000);

              // Timeout after 10 minutes
              setTimeout(() => {
                clearInterval(pollInterval);
                reject(new Error('Menu scan timeout'));
              }, 600000);
            });

            allMenuItems = [...allMenuItems, ...completedItems];

          } else {
            console.error(`Failed to process ${file.name}: ${data.error || 'Unknown error'}`);
            showToast(`Failed to process ${file.name}. Please ensure the image contains a clear menu.`, 'error');
          }
        } catch (error) {
          console.error(`❌ Error processing menu ${i + 1}:`, error);
          showToast(`Error processing ${file.name}. Please try again.`, 'error');
        }
      }

      // Update scanned items with all results
      if (allMenuItems.length > 0) {
        setScannedMenuItems(allMenuItems);
      }

      // Clear selected images after successful processing
      setSelectedMenuImages([]);

    } catch (error) {
      console.error('❌ Error processing menu images:', error);
      showToast('Error processing menu images. Please try again.', 'error');
    } finally {
      setOcrProcessing(false);
      setMenuProcessingProgress({ current: 0, total: 0 });
      setMenuScanProgress(0);
    }
  };

  // Poll for menu scan job progress with detailed statistics
  const startMenuScanPolling = (jobId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        const data = { success: false, error: "OCR Removed" };

        if (data.job) {
          const { status, progress, menuItems, processedItems, totalItems, currentItem, stats } = data.job;

          setMenuScanProgress(progress);
          setOcrResults(menuItems.map(item => ({
            name: item.name,
            price: item.price || 0,  // Include price from backend
            status: item.status,
            ingredients: item.ingredients,
            imageUrl: item.imageUrl,
            error: item.error
          })));

          // Enhanced progress logging with statistics
          const statusEmoji = currentItem ? '🎨' : '⏳';
          const currentMsg = currentItem ? ` | Current: ${currentItem}` : '';

          // Check if completed or failed
          if (status === 'completed' || status === 'failed') {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;

            // Store scanned items for adding to recipes
            const completedItems = menuItems.filter(item => item.status === 'completed');
            setScannedMenuItems(completedItems);

            showToast(`🎉 Generated ingredients for ${completedItems.length} menu items!`);
          }
        }
      } catch (error) {
        console.error('❌ Polling error:', error);
        // Stop polling on error to prevent infinite loops
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }, 1500); // Poll every 1.5 seconds for faster UI updates
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Helper function to detect portion size variants from menu items
  const detectVariantGroups = (menuItems) => {
    // 🇮🇳 Indian Restaurant Standard Portion Keywords
    // Based on Indian food service standards - optimized for Biryani, Pizza, Fast Food, Thali, etc.
    const portionKeywords = [
      'single', 'solo', 'personal', 'mini', 'quarter',

      'half', 'regular', 'medium', 'full',

      'small', 'starter',

      'large', 'jumbo', 'king', 'xl',

      'double', 'family', 'sharing', 'bonanza', 'mega'
    ];
    
    const groups = {};
    const standalone = [];

    for (const item of menuItems) {
      const nameLower = item.name.toLowerCase();
      
      // Check if item has a portion keyword
      let foundKeyword = null;
      let baseName = item.name;
      
      for (const keyword of portionKeywords) {
        const regex = new RegExp(`\\b${keyword}\\b`, 'i');
        if (regex.test(nameLower)) {
          foundKeyword = keyword;
          // Remove the portion keyword to get base name
          baseName = item.name.replace(regex, '').trim().replace(/\s+/g, ' ');
          break;
        }
      }

      if (foundKeyword) {
        // Normalize base name for grouping
        const groupKey = baseName.toLowerCase().trim();
        
        if (!groups[groupKey]) {
          groups[groupKey] = {
            baseName: baseName,
            variants: []
          };
        }
        
        groups[groupKey].variants.push({
          ...item,
          detectedPortionSize: foundKeyword,
          detectedBaseName: baseName
        });
      } else {
        standalone.push(item);
      }
    }

    // Filter to only keep groups with 2+ variants
    const validGroups = Object.entries(groups)
      .filter(([_, group]) => group.variants.length >= 2)
      .map(([key, group]) => ({
        groupKey: key,
        baseName: group.baseName,
        variants: group.variants
      }));

    // Add standalone items back for single variants
    Object.entries(groups)
      .filter(([_, group]) => group.variants.length === 1)
      .forEach(([_, group]) => {
        standalone.push(...group.variants);
      });

    return { validGroups, standalone };
  };

  // Helper function to calculate portion multiplier based on detected portion size
  // 🇮🇳 INTELLIGENT PORTION MULTIPLIERS - INDIA FOOD STANDARDS
  // Optimized for Indian cuisine: Biryani, Pizza, Fast Food, Thali, Curries, etc.
  const calculatePortionMultiplier = (portionSize) => {
    const multipliers = {
      // INDIVIDUAL SERVINGS (250-300g approx)
      'mini': 0.5,        // Kids meal, small appetizer (150g)
      'quarter': 0.25,    // Quarter portions (common in sweets/mithai)
      'solo': 0.75,       // Personal single serving
      'personal': 1.0,    // Standard personal size
      
      // STANDARD SERVINGS (350-450g - BASE SIZE)
      'single': 1.0,      // Standard Indian single serve (Biryani: 350g)
      'regular': 1.0,     // Regular plate/bowl (Thali standard)
      'medium': 1.0,      // Medium pizza (10"), medium curry bowl
      'full': 1.0,        // Full portion/plate
      'half': 0.5,        // Half plate (common in India)
      
      // SMALL SIZES (200-300g)
      'small': 0.6,       // Small pizza (8"), small bowl
      'starter': 0.4,     // Starter/appetizer portion
      
      // LARGE SERVINGS (550-700g)
      'large': 1.5,       // Large pizza (12"), large bowl (serves 1-2)
      'jumbo': 2.0,       // Jumbo size (Biryani: 700g, serves 2)
      'king': 2.0,        // King size burger/pizza
      'xl': 2.0,          // Extra large single portions
      
      // FAMILY/SHARING SIZES (900g-1.5kg - VERY COMMON IN INDIA)
      'double': 2.0,      // Double portion (serves 2)
      'family': 2.5,      // Family pack (serves 3-4) - Most popular in Indian restaurants
      'sharing': 2.5,     // Sharing platter
      'bonanza': 3.0,     // Bonanza/feast pack (serves 4-5) - Popular in fast food
      'mega': 3.5         // Mega/party size (serves 5-6)
    };

    return multipliers[portionSize.toLowerCase()] || 1.0;
  };

  const addOCRIngredientsToRecipe = async () => {
    try {

      if (scannedMenuItems.length === 0) {
        showToast('No menu items ready. Please wait for AI processing to complete.', 'warning');
        return;
      }

      setIsCreatingRecipes(true);
      setRecipeCreationProgress({ current: 0, total: scannedMenuItems.length, message: 'Preparing recipes...' });

      const businessId = UnitMappingService.getBusinessId();

      //  DETECT VARIANT GROUPS
      const { validGroups, standalone } = detectVariantGroups(scannedMenuItems);
      

      // 🆕 If variant groups detected, process directly with default multipliers
      if (validGroups.length > 0) {
        
        // Prepare editable multipliers with suggestions
        const initialMultipliers = {};
        
        validGroups.forEach(group => {
          group.variants.forEach(variant => {
            const keyword = variant.detectedPortionSize.toLowerCase();
            
            // Get current multiplier (custom or default)
            initialMultipliers[keyword] = getMultiplierForKeyword(keyword);
          });
        });
        
        // Detect which item should be the base (multiplier = 1.0)
        validGroups.forEach(group => {
          // Find the base variant (usually 'single', 'regular', 'medium', 'full')
          const baseVariant = group.variants.find(v => 
            ['single', 'regular', 'medium', 'full'].includes(v.detectedPortionSize.toLowerCase())
          ) || group.variants[0];
          
          // Mark it on each variant
          group.variants.forEach(v => {
            v.isBaseVariant = (v === baseVariant);
          });
        });
        
        setEditableMultipliers(initialMultipliers);
        setPendingVariantGroups({ validGroups, standalone });
        
        // 🚀 DIRECTLY PROCESS WITHOUT SHOWING EDITOR - Pass data directly
        await processVariantGroupsWithMultipliers(validGroups, standalone, initialMultipliers);
        return;
      }

      // If no variant groups, process standalone items directly
      await processStandaloneItems(standalone);

    } catch (error) {
      console.error('❌ Error in addOCRIngredientsToRecipe:', error);
      showToast('❌ Error processing menu items. Please try again.', 'error');
    } finally {
      setIsCreatingRecipes(false);
      setRecipeCreationProgress({ current: 0, total: 0, message: '' });
    }
  };

  // 🆕 Process items after multipliers are confirmed
  const processVariantGroupsWithMultipliers = async (variantGroups = null, standaloneItems = null, multipliers = null) => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      
      // Use passed parameters if available, otherwise fall back to state (for manual button clicks)
      const validGroups = variantGroups || pendingVariantGroups.validGroups || [];
      const standalone = standaloneItems || pendingVariantGroups.standalone || [];
      const multiplierMap = multipliers || editableMultipliers || {};
      
      const createdRecipes = [];

      // Process variant groups first
      let processedCount = 0;
      for (const group of validGroups) {
        processedCount++;
        setRecipeCreationProgress({ 
          current: processedCount, 
          total: validGroups.length + standalone.length, 
          message: `Creating variant group: ${group.baseName}` 
        });
        
        try {
          // Find the base variant (marked during detection)
          const baseVariant = group.variants.find(v => v.isBaseVariant) || group.variants[0];
          try {
            // Build API URL - if REACT_APP_API_URL already includes /api, don't add it again
            const baseUrl = process.env.REACT_APP_API_URL;
            const apiUrl = baseUrl.endsWith('/api') ? `${baseUrl}/ai/generate-ingredients` : `${baseUrl}/api/ai/generate-ingredients`;
            
            const response = await fetch(apiUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-business-id': businessId },
              body: JSON.stringify({ dishName: baseVariant.name, plateCount: 10 })
            });
            const data = await response.json();
            
            if (data.success && data.ingredients) {
              baseVariant.ingredients = data.ingredients;
              baseVariant.description = data.description || ''; // Save description
              
              
              // Save complimentary items & add-ons to Recipe Library
              if (data.complimentary_items?.length > 0 || data.add_ons?.length > 0) {
                try {
                  await RecipeLibraryService.updateRecipe(baseVariant.name, {
                    complimentary_items: data.complimentary_items || [],
                    add_ons: data.add_ons || []
                  }, businessId);
                } catch (e) {
                  console.warn('Failed to save to Recipe Library:', e);
                }
              }
            } else {
              console.warn(`⚠️ AI fallback failed for ${baseVariant.name}, using empty ingredients`);
              baseVariant.ingredients = [];
              baseVariant.description = '';
            }
          } catch (aiError) {
            console.error(`❌ AI fetch error for ${baseVariant.name}:`, aiError);
            baseVariant.ingredients = baseVariant.ingredients || [];
            baseVariant.description = '';
          }

          // Create parent recipe (base variant)
          let parentRecipe;
          let parentAlreadyExists = false;
          try {
            parentRecipe = await RecipesService.createRecipe({
              name: baseVariant.name,
              businessId,
              category_id: selectedCategory,
              is_parent: true,
              parent_id: null,
              price: baseVariant.price || 0
            });
          } catch (createError) {
            // Handle duplicate item error - fetch existing recipe instead
            if (createError.response?.status === 409 || createError.message?.includes('Duplicate')) {
              console.warn(`⚠️ Parent recipe "${baseVariant.name}" already exists, fetching existing...`);
              parentAlreadyExists = true;
              
              // Fetch all recipes and find the matching one
              try {
                const allRecipes = await RecipesService.getRecipes(businessId);
                parentRecipe = allRecipes.find(r => r.name === baseVariant.name);
                
                if (!parentRecipe) {
                  console.error(`❌ Could not find existing recipe: ${baseVariant.name}`);
                  showToast(`❌ Failed to find existing recipe "${baseVariant.name}"`, 'error');
                  continue;
                }
                
              } catch (fetchError) {
                console.error(`❌ Error fetching existing recipe:`, fetchError);
                showToast(`❌ Failed to fetch existing recipe "${baseVariant.name}"`, 'error');
                continue;
              }
            } else {
              throw createError;
            }
          }

          // Save description if available
          if (baseVariant.description) {
            try {
              await RecipesService.updateRecipe(parentRecipe.id, {
                description: baseVariant.description
              }, businessId);
            } catch (descError) {
              console.warn('Failed to save description:', descError);
            }
          }

          // Save ingredients for parent (only if newly created, not if already exists)
          let ingredients = [];
          if (!parentAlreadyExists) {
          
            ingredients = baseVariant.ingredients.map(ing => {
              // Handle multiple backend formats:
              // 1. Recipe Library format: {name, qty10, unit}
              // 2. Menu scan format: {ingredient, quantity_standard, quantity_metric}
              const ingredientName = ing.name || ing.ingredient;
              let quantityValue = 0;
              let unit = 'g';
              
              if (ing.qty10) {
                quantityValue = parseFloat(ing.qty10) / 10;
                unit = ing.unit || 'g';
              } else if (ing.quantity) {
                quantityValue = parseFloat(ing.quantity) / 10;
                unit = ing.unit || 'g';
              } else if (ing.quantity_standard) {
                // Parse "1.5 kg" or "150 g" format - extract number
                const match = String(ing.quantity_standard).match(/([\d.]+)/);
                quantityValue = match ? parseFloat(match[1]) / 10 : 0; // Divide by 10 since it's for 10 plates
                // Extract unit from quantity_standard
                unit = (ing.quantity_standard || '').replace(/[0-9.\s]/g, '').trim() || 'g';
              }
              
              const processed = {
                name: ingredientName,
                quantity: Number(quantityValue.toFixed(4)),
                unit: unit
              };
              
             
              return processed;
            });

            await RecipesService.updateRecipeIngredients(parentRecipe.id, ingredients, businessId);
          } else {
            // Parent already exists - fetch its ingredients for copying to variants
            try {
              ingredients = await RecipesService.getRecipeIngredients(parentRecipe.id);
            } catch (fetchIngredientsError) {
              console.error(`❌ Error fetching ingredients:`, fetchIngredientsError);
              ingredients = []; // Continue with empty ingredients
            }
          }

          createdRecipes.push(parentRecipe);

          // Create variant recipes (excluding base) and copy ingredients
          for (const variant of group.variants) {
            if (variant === baseVariant) continue;

            // 🔑 USE CUSTOM MULTIPLIER from multiplierMap parameter
            const keyword = variant.detectedPortionSize.toLowerCase();
            const multiplier = multiplierMap[keyword] || 1.0;

            let variantRecipe;
            let variantAlreadyExists = false;
            
            try {
              variantRecipe = await RecipesService.createRecipe({
                name: variant.name,
                businessId,
                category_id: selectedCategory,
                is_parent: false,
                parent_id: parentRecipe.id,
                portion_multiplier: multiplier,
                price: variant.price || 0
              });
            } catch (variantCreateError) {
              // Handle duplicate variant error - fetch existing instead
              if (variantCreateError.response?.status === 409 || variantCreateError.message?.includes('Duplicate')) {
                console.warn(`⚠️ Variant "${variant.name}" already exists, fetching existing...`);
                variantAlreadyExists = true;
                
                try {
                  const allRecipes = await RecipesService.getRecipes(businessId);
                  variantRecipe = allRecipes.find(r => r.name === variant.name);
                  
                  if (!variantRecipe) {
                    console.error(`❌ Could not find existing variant: ${variant.name}`);
                    showToast(`⚠️ Skipped variant "${variant.name}" - could not find existing`, 'warning');
                    continue;
                  }
                  
                } catch (fetchError) {
                  console.error(`❌ Error fetching existing variant:`, fetchError);
                  showToast(`⚠️ Skipped variant "${variant.name}"`, 'warning');
                  continue;
                }
              } else {
                // For other errors, log but continue
                console.error(`❌ Error creating variant ${variant.name}:`, variantCreateError);
                showToast(`❌ Failed to create variant "${variant.name}"`, 'error');
                continue;
              }
            }

            const variantRecipeId = variantRecipe.id;

            // Copy description from parent to variant
            if (baseVariant.description) {
              try {
                await RecipesService.updateRecipe(variantRecipeId, {
                  description: baseVariant.description
                }, businessId);
              } catch (descError) {
                console.warn(`      ⚠️ Failed to save description for ${variantRecipe.name}:`, descError);
              }
            }

            // Copy ingredients from parent and adjust quantities based on multiplier
            const adjustedIngredients = ingredients.map(ing => ({
              ...ing,
              quantity: Math.round(ing.quantity * multiplier * 100) / 100 // Apply multiplier and round to 2 decimals
            }));

            try {
              // Save ingredients with businessId
              await RecipesService.updateRecipeIngredients(variantRecipeId, adjustedIngredients, businessId);
            } catch (ingredientsSaveError) {
              console.error(`      ❌ Failed to save ingredients for ${variantRecipe.name}:`, ingredientsSaveError);
              showToast(`⚠️ Created "${variantRecipe.name}" but failed to save ingredients`, 'warning');
            }

            createdRecipes.push(variantRecipe);
          }

        } catch (error) {
          // Only show toast for non-duplicate errors
          if (error.response?.status !== 409 && !error.message?.includes('Duplicate')) {
            console.error(`❌ Error processing variant group ${group.baseName}:`, error);
            showToast(`❌ Failed to create variants for "${group.baseName}"`, 'error');
          }
        }
      }

      // Process standalone items
      await processStandaloneItems(standalone);

      // Close modal and reset state
      setShowMultiplierEditor(false);
      setPendingVariantGroups([]);
      setEditableMultipliers({});
      setShowOCRModal(false);
      setOcrResults([]);
      setScannedMenuItems([]);
      setMenuScanJob(null);
      setMenuScanProgress(0);

      // Show summary
      let message = ` Created ${createdRecipes.length + standalone.length} recipes from menu scan!`;
      if (validGroups.length > 0) {
        message += `\n🍽️ Processed ${validGroups.length} variant group${validGroups.length > 1 ? 's' : ''} with your custom multipliers!`;
      }
      showToast(message);

      // Refresh recipe list
      await new Promise(resolve => setTimeout(resolve, 500));
      const refreshedRecipes = await RecipesService.getRecipes(businessId);
      setRecipes(applyLocalReviewDrafts(refreshedRecipes));

      // Auto-open first recipe
      if (refreshedRecipes.length > 0) {
        const firstRecipe = refreshedRecipes.find(r => r.name === createdRecipes[0]?.name) || refreshedRecipes[0];
        setSelectedRecipe(withImageMeta(firstRecipe));
      }

    } catch (error) {
      console.error('❌ Error in processVariantGroupsWithMultipliers:', error);
      showToast('❌ Failed to process recipes. Please try again.', 'error');
    }
  };

  // 🆕 Process standalone items (no variants)
  const processStandaloneItems = async (standalone) => {
    if (!standalone || standalone.length === 0) return;

    const businessId = UnitMappingService.getBusinessId();

    for (let i = 0; i < standalone.length; i++) {
      const item = standalone[i];
      setRecipeCreationProgress({ 
        current: i + 1, 
        total: standalone.length, 
        message: `Creating recipe: ${item.name}` 
      });
      try {

        // AI fallback for complete recipe data if needed
        if (!item.ingredients || item.ingredients.length === 0) {
          const baseUrl = process.env.REACT_APP_API_URL || '';
          const apiUrl = baseUrl.endsWith('/api') ? `${baseUrl}/ai/generate-ingredients` : `${baseUrl}/api/ai/generate-ingredients`;
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-business-id': businessId },
            body: JSON.stringify({ dishName: item.name, plateCount: 10 })
          });
          const data = await response.json();
          if (data.success && data.ingredients) {
            item.ingredients = data.ingredients;
            
            // Save complimentary items & add-ons to Recipe Library
            if (data.complimentary_items?.length > 0 || data.add_ons?.length > 0) {
              try {
                await RecipeLibraryService.updateRecipe(item.name, {
                  complimentary_items: data.complimentary_items || [],
                  add_ons: data.add_ons || []
                }, businessId);
              } catch (e) {
                console.warn('Failed to save to Recipe Library:', e);
              }
            }
          } else {
            console.warn(`⚠️ No recipe data for ${item.name}, skipping...`);
            continue;
          }
        }

        // Fetch complimentary items and add_ons from Recipe Library
        let complimentaryItems = [];
        let addOns = [];
        
        try {
          const recipeLibraryData = await RecipeLibraryService.getRecipe(item.name, businessId);
          if (recipeLibraryData) {
            complimentaryItems = recipeLibraryData.complimentary_items || [];
            addOns = recipeLibraryData.add_ons || [];
          }
        } catch (error) {
          console.warn(`⚠️ Could not fetch Recipe Library data for ${item.name}:`, error.message);
        }

        // Create recipe with price from OCR and customizations from Recipe Library
        let createdRecipe;
        try {
          createdRecipe = await RecipesService.createRecipe({
            name: item.name,
            businessId,
            category_id: selectedCategory,
            is_parent: false,
            parent_id: null,
            price: item.price || 0  // Include price from OCR scan
          });
          // Neither complimentary_items nor add_ons is accepted by POST /recipes — it ignores
          // unknown fields, so the Recipe Library's customizations were silently lost on every
          // scanned item. /customizations is the only endpoint that persists them.
          if (createdRecipe?.id
            && ((Array.isArray(complimentaryItems) && complimentaryItems.length > 0)
              || (Array.isArray(addOns) && addOns.length > 0))) {
            try {
              await RecipesService.updateRecipeCustomizations(createdRecipe.id, {
                complimentary_items: complimentaryItems || [],
                add_ons: addOns || []
              }, businessId);
            } catch (compError) {
              console.warn(`⚠️ Created "${item.name}" but could not save its customizations:`, compError?.message || compError);
            }
          }
        } catch (createError) {
          // Handle duplicate item error
          if (createError.response?.status === 409) {
            console.warn(`⚠️ Skipping duplicate: ${item.name} already exists`);
            showToast(`⚠️ Skipped "${item.name}" - already exists in menu`, 'warning');
            continue;
          }
          throw createError;
        }

        const recipeId = createdRecipe.id;
        const ingredients = item.ingredients.map(ing => {
          // Handle multiple backend formats:
          // 1. Recipe Library format: {name, qty10, unit}
          // 2. Menu scan format: {ingredient, quantity_standard, quantity_metric}
          const ingredientName = ing.name || ing.ingredient;
          let quantityValue = 0;
          
          if (ing.qty10) {
            quantityValue = parseFloat(ing.qty10) / 10;
          } else if (ing.quantity) {
            quantityValue = parseFloat(ing.quantity) / 10;
          } else if (ing.quantity_standard) {
            // Parse "1.5 kg" format
            const match = String(ing.quantity_standard).match(/([\d.]+)/);
            quantityValue = match ? parseFloat(match[1]) : 0;
          }
          
          const mappedIng = {
            name: ingredientName,
            quantity: Number(quantityValue.toFixed(4)),
            unit: ing.unit || 'g'
          };
          
          
          return mappedIng;
        });

        // Check for composites (disabled auto-popup - show inline warning only)
        const composites = await checkForComposites(ingredients);
        
        if (composites.length > 0) {
          setDetectedComposites(composites);
          // Don't auto-open popup - let user manually configure if needed
          // setShowCompositePopup(true);
          
          // window.pendingRecipeSave = {
          //   recipeId,
          //   ingredients,
          //   businessId,
          //   recipeName: item.name
          // };
          
          // continue;
        }

        await RecipesService.updateRecipeIngredients(recipeId, ingredients, businessId);

      } catch (error) {
        console.error(`❌ Error processing ${item.name}:`, error);
        // Only show alert for non-duplicate errors
        if (error.response?.status !== 409) {
          console.warn(`⚠️ Error occurred but continuing: ${error.message}`);
          // Don't show alert - recipe might have been created successfully despite the error
          // The error is often a transient issue that resolves itself
        }
      }
    }
    
    // Close modal and refresh recipes after all items processed
    setShowOCRModal(false);
    setScannedMenuItems([]);
    setOcrResults([]);
    setMenuScanJob(null);
    setMenuScanProgress(0);
    
    // Refresh the recipes list
    const refreshedRecipes = await RecipesService.getRecipes(businessId);
    setRecipes(applyLocalReviewDrafts(refreshedRecipes));
    showToast(` Added ${standalone.length} recipes successfully!`);
  };

  const handleSaveClick = async () => {
    setSummaryOpenDropdown('ingredients');
    
    // 🆕 DETECT COMPOSITES FROM CURRENT INGREDIENT LIST (for manual entry)
    const currentIngredients = ingredientList
      .filter(i => (i.name || '').trim() && i.name !== 'New Ingredient')
      .map(i => ({
        name: i.name,
        quantity: i.qty10 || i.qty100 || 0,
        unit: i.unit || 'g'
      }));
    
    const detectedComps = await checkForComposites(currentIngredients);
    
    if (detectedComps.length > 0) {
      setDetectedComposites(detectedComps);
      
      // Open composite editor first
      const firstComposite = detectedComps[0];
      
      setSelectedComposite(firstComposite);
      setShowCompositeEditor(true);
      
      showToast(`🔍 Configure decomposition for ${firstComposite.name}`);
    } else {
      // No composites, show confirmation modal directly
      setDetectedComposites([]);
      setShowSaveConfirmationModal(true);
    }
  };

  const handleConfirmClick = () => {
    const hasZeroPreps = summarySavedPreparationItems.length === 0;
    const hasZeroComplimentary = summaryComplimentaryItems.length === 0;

    if (hasZeroPreps || hasZeroComplimentary) {
      let msg = '';
      if (hasZeroPreps && hasZeroComplimentary) {
        msg = 'Please verify that there is no Saved Preparations and no Complimentary Items for this item. Would you like to continue?';
      } else if (hasZeroPreps) {
        msg = 'Please verify that there is no Saved Preparations for this item. Would you like to continue?';
      } else {
        msg = 'Please verify that there is no Complimentary Items for this item. Would you like to continue?';
      }
      setVerifyModalMessage(msg);
      setShowVerifyModal(true);
    } else {
      confirmAndSave();
    }
  };

  const confirmAndSave = async () => {
    if (savingIngredients) return;
    setSummaryOpenDropdown(null);
    setShowSaveConfirmationModal(false);

    let ingredientListForSave = ingredientList;
    if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
      const savedList = await saveIngredient({ silent: true, returnUpdatedList: true });
      if (!savedList) {
        showToast('Please complete the current ingredient before confirming');
        return;
      }
      ingredientListForSave = savedList;
    }

    const normalizedPlateCount = Number(plateStep) > 0 ? Number(plateStep) : 1;
    await savePerPlateToDB(ingredientListForSave, normalizedPlateCount);
  };

  const formatSummaryTitle = (value) => {
    return String(value || '')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatSummaryNumber = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '0';
    if (Number.isInteger(numeric)) return String(numeric);
    return String(Number(numeric.toFixed(2)));
  };

  const normalizeSummaryUnit = (unit) => {
    const key = String(unit || '').trim().toLowerCase();
    const aliases = {
      kg: 'kg', kilogram: 'kg', kilograms: 'kg',
      g: 'g', gram: 'g', grams: 'g',
      mg: 'mg', milligram: 'mg', milligrams: 'mg',
      l: 'l', litre: 'l', litres: 'l', liter: 'l', liters: 'l',
      ml: 'ml', milliliter: 'ml', milliliters: 'ml', millilitre: 'ml', millilitres: 'ml'
    };
    return aliases[key] || key;
  };

  const formatShortUnitLabel = (unit) => {
    const normalized = normalizeSummaryUnit(unit);
    if (normalized === 'l') return 'L';
    return normalized;
  };

  const formatCompactQtyUnit = (value, unit) => {
    const qty = formatSummaryNumber(value);
    const shortUnit = formatShortUnitLabel(unit);
    return `${qty}${shortUnit || ''}`;
  };

  const convertSummaryQuantity = (value, fromUnit, toUnit) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;

    const from = normalizeSummaryUnit(fromUnit);
    const to = normalizeSummaryUnit(toUnit);
    if (!from || !to || from === to) return numeric;

    const weightToGram = { mg: 0.001, g: 1, kg: 1000 };
    const volumeToMl = { ml: 1, l: 1000 };

    if (weightToGram[from] && weightToGram[to]) {
      return (numeric * weightToGram[from]) / weightToGram[to];
    }

    if (volumeToMl[from] && volumeToMl[to]) {
      return (numeric * volumeToMl[from]) / volumeToMl[to];
    }

    return numeric;
  };

  const getScaledPreparationIngredientRows = (prepItem, prepMeta) => {
    const rows = Array.isArray(prepMeta?.components) ? prepMeta.components : [];
    const totalQty = Number(prepMeta?.total_weight);
    const totalUnit = prepMeta?.total_weight_unit_symbol || prepMeta?.total_weight_unit_name || prepItem?.unit;
    const qty10 = Number(prepItem?.qty10) || 0;
    const qty100 = Number(prepItem?.qty100) || 0;
    const usedPerPlateRaw = qty100 > 0 ? (qty100 / 100) : (qty10 > 0 ? (qty10 / 10) : 0);
    const step = Number(plateStep) > 0 ? Number(plateStep) : 1;
    const usedForStepInTotalUnit = convertSummaryQuantity(usedPerPlateRaw * step, prepItem?.unit, totalUnit);
    const scaleFactor = totalQty > 0 ? (usedForStepInTotalUnit / totalQty) : 0;

    return rows.map((ing) => {
      const sourceQty = Number(ing?.weight ?? ing?.quantity ?? 0);
      const sourceUnit = String(ing?.unit_symbol || ing?.unit_name || '').trim();

      // Deliberately NOT pre-rounded here. A component stored in a high unit scales to a
      // sub-1 value for a single plate (1 kg of a 2 kg batch used 5 g per plate -> 0.0025 kg);
      // rounding that to 2dp before it reaches the formatter turns a real quantity into "0".
      // formatQuantity auto-scales kg->g / L->ml at render, so the raw value is what it needs.
      return {
        itemName: String(ing?.item_name || '').trim(),
        quantity: sourceQty * scaleFactor,
        unit: sourceUnit || 'g'
      };
    });
  };

  const summaryComplimentaryItems = (linkedComplimentaryItems?.length > 0
    ? linkedComplimentaryItems
    : (selectedComplimentaryItems || []).map((entry) => {
      const itemName = typeof entry === 'string' ? entry : entry?.name;
      return { name: itemName };
    })
  ).filter((item) => String(item?.name || '').trim());

  const normalizeSummaryKey = (value) => String(value || '').trim().toLowerCase();

  const summaryActiveItems = ingredientList.filter(
    (i) => (i.name || '').trim() && i.name !== 'New Ingredient' && (i.qty10 > 0 || i.qty100 > 0)
  );

  const savedPreparationByName = new Map(
    (savedPreparations || [])
      .filter((prep) => normalizeSummaryKey(prep?.composite_name))
      .map((prep) => [normalizeSummaryKey(prep.composite_name), prep])
  );

  const summarySavedPreparationItems = summaryActiveItems.filter((item) =>
    savedPreparationByName.has(normalizeSummaryKey(item?.name))
  );

  const summaryIngredientItems = summaryActiveItems.filter(
    (item) => !savedPreparationByName.has(normalizeSummaryKey(item?.name))
  );

  const getComplimentarySummaryMeta = (item) => {
    const ingredients = Array.isArray(item?.ingredients) ? item.ingredients : [];
    const parsedCount = Number(item?.ingredient_count);
    const ingredientCount = Number.isFinite(parsedCount) ? parsedCount : ingredients.length;
    const scalingType = String(item?.scaling_type || '').toUpperCase();
    const parsedMultiplier = Number(item?.quantity_multiplier);
    const quantityMultiplier = Number.isFinite(parsedMultiplier) ? parsedMultiplier : 1;

    let scalingLabel = 'Association saved';
    if (scalingType === 'FIXED') {
      scalingLabel = `Fixed x${formatSummaryNumber(quantityMultiplier)}`;
    } else if (scalingType === 'SCALED') {
      scalingLabel = `Scaled x${formatSummaryNumber(quantityMultiplier)}`;
    }

    const measurementPreview = ingredients.length > 0
      ? ingredients
          .slice(0, 3)
          .map((ing) => {
            const qty = formatSummaryNumber(ing?.quantity);
            const unit = String(ing?.unit_symbol || '').trim();
            const ingName = String(ing?.item_name || '').trim();
            return `${qty}${unit ? ` ${unit}` : ''}${ingName ? ` ${ingName}` : ''}`;
          })
          .join(', ')
      : 'No measurements yet';

    const moreSuffix = ingredients.length > 3 ? ` +${ingredients.length - 3} more` : '';

    return {
      ingredientCount,
      scalingLabel,
      measurementText: `${measurementPreview}${moreSuffix}`
    };
  };

  const handleMarkAsReview = async () => {
    if (!selectedRecipe?.id) return;

    let ingredientListForDraft = ingredientList;

    if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
      const savedList = await saveIngredient({ silent: true, returnUpdatedList: true });
      if (!savedList) {
        showToast('Please complete the current ingredient before marking as review');
        return;
      }
      ingredientListForDraft = savedList;
    }

    try {
      persistLocalReviewDraft(ingredientListForDraft);
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      setAutoSaveStatus(null);
      setShowUnsavedChangesModal(false);
      setIngredientHistory([]);
      setHistoryIndex(-1);
      setRecipes(prev => applyLocalReviewDrafts(prev));
      setSelectedRecipe(null);
      setEditingIngredientIndex(null);
      showToast('Marked as under review. Changes saved locally.');
    } catch (error) {
      console.error('Failed to mark as review:', error);
      showToast('Failed to mark as review');
    }
  };

  const saveDescription = async () => {
    if (!selectedRecipe || !selectedRecipe.id) return;
    
    try {
      const businessId = UnitMappingService.getBusinessId();
      await RecipesService.updateRecipe(selectedRecipe.id, {
        description: descriptionText
      }, businessId);
      
      setEditingDescription(false);
      showToast('Description saved successfully!');
      
      // Update local state
      setSelectedRecipe(prev => ({ ...prev, description: descriptionText }));
    } catch (error) {
      console.error('Failed to save description:', error);
      showToast('Failed to save description');
    }
  };

  // Internal save status: Just show UI feedback (NO DATABASE SAVE)
  const showInternalSaveStatus = () => {
    setAutoSaveStatus('saving');
    setTimeout(() => {
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 1500);
    }, 300);
  };

  // Check if there are unsaved changes in QRitemmap
  const hasUnsavedChanges = () => {
    const current = JSON.stringify(ingredientList.map(i => ({
      name: String(i?.name || '').trim(),
      qty10: Number(Number(i?.qty10 || 0).toFixed(4)),
      qty100: Number(Number(i?.qty100 || 0).toFixed(4)),
      unit: String(i?.unit || '').trim()
    })));
    const original = JSON.stringify((originalIngredientListRef.current || []).map(i => ({
      name: String(i?.name || '').trim(),
      qty10: Number(Number(i?.qty10 || 0).toFixed(4)),
      qty100: Number(Number(i?.qty100 || 0).toFixed(4)),
      unit: String(i?.unit || '').trim()
    })));
    return current !== original;
  };

  const exitRecipeEditorWithoutSaving = () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    setShowUnsavedChangesModal(false);
    setAutoSaveStatus(null);
    setIngredientList([]);
    setEditingIngredientIndex(null);
    setFormData({ name: '', quantity: 0, unit: '' });
    setShowNameSuggestions(false);
    setIngredientHistory([]);
    setHistoryIndex(-1);
    setShowDuplicateModal(false);
    setDuplicateConflictData(null);
    setDuplicateOverrides({});
    setDuplicateForceReason('Intentional Variant');
    pendingSaveListRef.current = null;
    pendingSavePlateCountRef.current = null;
    pendingConfirmListRef.current = null;
    setSelectedRecipe(null);
  };

  const discardUnsavedChangesAndExitEditor = () => {
    setIngredientList(JSON.parse(JSON.stringify(originalIngredientListRef.current || [])));
    exitRecipeEditorWithoutSaving();
  };

  // Handle back button: revert changes if not saved
  const handleRecipeBackButton = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesModal(true);
      return;
    }
    exitRecipeEditorWithoutSaving();
  };

  const ensureInventoryItemsForIngredients = async (ingredients) => {
    const businessId = UnitMappingService.getBusinessId();
    if (!businessId) throw new Error('Missing business_id. Please log in again.');

    const existing = new Set(Object.keys(inventoryStdUnitMap || {}).map(k => String(k).toLowerCase()));

    for (const ingredient of ingredients) {
      const ingredientName = String(ingredient?.name || '').trim();
      if (!ingredientName) continue;

      const key = ingredientName.toLowerCase();
      if (existing.has(key)) continue;

      try {
        await InventoryService.createInventoryItem({
          name: ingredientName,
          unit_symbol: ingredient.unit || 'g',
          business_id: businessId,
          source: 'ingredient_mapping'
        });

        existing.add(key);
        setInventoryStdUnitMap(prev => ({ ...prev, [key]: ingredient.unit || 'g' }));
      } catch (error) {
        const status = error?.status || error?.response?.status;
        const data = error?.data || error?.response?.data || {};
        const message = String(data?.error || data?.message || data?.details || error?.message || '').toLowerCase();
        if (status === 409 || message.includes('already') || message.includes('exists') || message.includes('duplicate') || message.includes('unique constraint')) {
          existing.add(key);
          continue;
        }
        console.warn(`Failed to create inventory item \"${ingredientName}\" during confirm save. Continuing with recipe save.`, error);
      }
    }
  };

  const savePerPlateToDB = async (listForSave = ingredientList, plateCountOverride, explicitOverrides) => {
    if (savingIngredientsRef.current) return; // prevent concurrent saves (ref-based — no stale React state)
    savingIngredientsRef.current = true;
    setSavingIngredients(true);
    try {
      let recipeDetailsSaved = true;
      const sourceList = Array.isArray(listForSave) ? listForSave : ingredientList;
      const normalizedPlateCount = Number(plateCountOverride) > 0
        ? Number(plateCountOverride)
        : (Number(plateStep) > 0 ? Number(plateStep) : 1);
      pendingSavePlateCountRef.current = normalizedPlateCount;

      // Same builder as itemmap.js: per-plate at 4 dp, kg/L → g/ml, same-family merge.
      // QR used to send raw kitchen units ("tbsp") and merge across families; it no longer does.
      const { ingredients: merged, roundedToZero } = buildPerPlatePayload(sourceList, normalizedPlateCount, { getDisplayQty });
      if (roundedToZero.length > 0) {
        setShowRoundOffModal(true);
        showToast("Round off to zero. Costing cannot be applied.", "warning");
      }
      if (merged.length === 0) {
        showToast('Add at least one ingredient quantity before confirming');
        return;
      }

      // Pre-flight (gate + full duplicate scan, nothing written). Blockers are shown as a
      // toast here; the unified screen (SP7) renders them under the rows.
      try {
        const verdict = await RecipesService.validateRecipeIngredients(selectedRecipe.id, {
          ingredients: merged,
          duplicate_overrides: explicitOverrides || duplicateOverridesRef.current,
        });
        if (!verdict.ok) {
          showToast(verdict.blockers.map((b) => b.message).join(' • '), 'warning');
          return;
        }
        if (verdict.conflicts.length > 0) {
          pendingSaveListRef.current = listForSave;
          setDuplicateConflictData(verdict.conflicts[0]);
          setDuplicateForceReason('Intentional Variant');
          setShowDuplicateModal(true);
          return;
        }
      } catch (validateError) {
        showToast(`Could not validate ingredients: ${validateError?.message || 'Unknown error'}`, 'warning');
        return;
      }

      // Create missing inventory items only at final Confirm save
      // (Removed ensureInventoryItemsForIngredients — backend natively handles auto-creation and semantic duplicate checks)
      
      // Update local autocomplete maps eagerly
      merged.forEach(ing => {
        const key = (ing.name || '').trim().toLowerCase();
        if (key) {
          setInventoryStdUnitMap(prev => ({ ...prev, [key]: prev[key] || ing.unit || 'g' }));
          setInventoryItemNames(prev => prev.includes(ing.name.trim()) ? prev : [...prev, ing.name.trim()]);
        }
      });
      // Save ingredients (with duplicate_overrides if any)
      // SP9 (C3): status rides in the same request/transaction as the rows it describes.
      // Recipe Book only: the plate count the quantities were entered for (e.g. 25), kept so
      // the Recipe Book PDF can list this dish on its Custom Plates page at that count. The
      // ingredients above are still saved per 1 plate exactly as before; null clears it.
      const savePayload = {
        ingredients: merged,
        status: 'confirmed',
        custom_plate_count: normalizedPlateCount > 1 ? normalizedPlateCount : null
      };
      // Use explicitly passed overrides (from retry) or fall back to ref (always up-to-date)
      const activeOverrides = explicitOverrides || duplicateOverridesRef.current;
      if (Object.keys(activeOverrides).length > 0) {
        savePayload.duplicate_overrides = activeOverrides;
      }

      try {
        const businessId = UnitMappingService.getBusinessId();
        await http.put(`/recipes/${selectedRecipe.id}/ingredients`, savePayload);

        // 🆕 Save selected complimentary items and customizations to MenuItems table
        if (selectedRecipe && selectedRecipe.id) {
          try {
            // Split deliberately: PUT /recipes/:id knows `description` and nothing about
            // complimentary_items or add_ons — it silently discarded both while reporting
            // success. Only /customizations writes menu_complimentary_items and the add-on
            // rows. Sent together so one failure fails the save rather than half-saving.
            await Promise.all([
              RecipesService.updateRecipe(
                selectedRecipe.id,
                { description: descriptionText || '' },
                businessId
              ),
              RecipesService.updateRecipeCustomizations(
                selectedRecipe.id,
                {
                  complimentary_items: selectedComplimentaryItems || [],
                  add_ons: selectedCustomizations || []
                },
                businessId
              )
            ]);
          } catch (customError) {
            recipeDetailsSaved = false;
            console.error('Failed to save customizations to MenuItems:', customError);
          }
        }

        // Persist confirmed status and refresh list
        // SP9 (C3): status is folded into the ingredients PUT — no separate request.
        if (recipeDetailsSaved) {
          clearRecipeReviewDraft(selectedRecipe, businessId);
        }
        const refreshed = await RecipesService.getRecipes(businessId);
        setRecipes(applyLocalReviewDrafts(refreshed));

        // Update original ref so we don't get "unsaved changes" alert
        originalIngredientListRef.current = JSON.parse(JSON.stringify(sourceList));
        setShowUnsavedChangesModal(false);

        // Close recipe view after successful save
        setSelectedRecipe(null);
        setEditingIngredientIndex(null);

        // Clear undo/redo history + duplicate state after successful save
        setIngredientHistory([]);
        setHistoryIndex(-1);
        setShowDuplicateModal(false);
        setDuplicateConflictData(null);
        setDuplicateOverrides({});
        duplicateOverridesRef.current = {};
        setDuplicateForceReason('Intentional Variant');
        pendingSaveListRef.current = null;
        pendingSavePlateCountRef.current = null;

        const pendingTSRData = localStorage.getItem('pendingTSRData');
        if (pendingTSRData) {
          showToast('Recipe saved. Returning to TSR...');
          setTimeout(() => {
            goTo && goTo('todays-sales-report');
          }, 300);
        } else {
          showToast('Recipe saved successfully!');
        }
      } catch (saveError) {
        // ── DUPLICATE PREVENTION: Intercept 409 (batched shape; one at a time until SP7) ──
        if (saveError?.status === 409 && saveError?.data?.error === 'duplicate_detected') {
          const list = Array.isArray(saveError.data.duplicates)
            ? saveError.data.duplicates
            : (saveError.data.duplicate ? [saveError.data.duplicate] : []);
          debugLog('🛡️ [DUP-PREVENTION] Conflict detected:', list[0]);
          pendingSaveListRef.current = listForSave;
          setDuplicateConflictData(list[0] || null);
          setDuplicateForceReason('Intentional Variant');
          setShowDuplicateModal(true);
          return; // Don't show generic error — modal handles it
        }
        // A race that slipped past validate: the gate refused rows at save time.
        if (saveError?.status === 400 && saveError?.data?.error === 'validation_failed') {
          showToast((saveError.data.blockers || []).map((b) => b.message).join(' • ') || 'Some ingredients were refused', 'warning');
          return;
        }
        throw saveError; // Re-throw other errors
      }
    } catch (error) {
      console.error('Failed to save per-plate ingredients:', error);
      showToast(`Failed to save recipe changes: ${error?.message || 'Unknown error'}`);
    } finally {
      savingIngredientsRef.current = false;
      setSavingIngredients(false);
    }
  };

  // ── Duplicate Modal handlers ──
  const handleDuplicateUseExisting = async () => {
    if (!duplicateConflictData) return;
    const updatedOverrides = {
      ...duplicateOverridesRef.current,
      [duplicateConflictData.attempted_name]: {
        action: 'use_existing',
        existing_item_id: duplicateConflictData.existing_item_id
      }
    };
    duplicateOverridesRef.current = updatedOverrides; // update ref immediately (no React batching delay)
    setDuplicateOverrides(updatedOverrides);
    setShowDuplicateModal(false);
    setDuplicateConflictData(null);
    showToast(`Using existing item: ${duplicateConflictData.existing_item_name}`);
    // Pass overrides explicitly to avoid stale closure reading old React state
    setTimeout(() => savePerPlateToDB(pendingSaveListRef.current, pendingSavePlateCountRef.current, updatedOverrides), 150);
  };

  const handleDuplicateForceCreate = async () => {
    if (!duplicateConflictData) return;
    if (!duplicateForceReason) {
      showToast('Select a reason before creating a separate item.', 'warning');
      return;
    }
    const reason = duplicateForceReason;
    const updatedOverrides = {
      ...duplicateOverridesRef.current,
      [duplicateConflictData.attempted_name]: {
        action: 'force_create',
        matched_item_id: duplicateConflictData.existing_item_id,
        matched_item_name: duplicateConflictData.existing_item_name,
        reason
      }
    };
    duplicateOverridesRef.current = updatedOverrides; // update ref immediately (no React batching delay)
    setDuplicateOverrides(updatedOverrides);
    setShowDuplicateModal(false);
    setDuplicateConflictData(null);
    // Pass overrides explicitly to avoid stale closure reading old React state
    setTimeout(() => savePerPlateToDB(pendingSaveListRef.current, pendingSavePlateCountRef.current, updatedOverrides), 150);
  };

  if (selectedRecipe) {
    return (
      <>
        {showRoundOffModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px'
          }}>
            <div style={{
              background: '#fff', borderRadius: '16px', padding: '28px',
              maxWidth: '380px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              textAlign: 'center',
              animation: 'fadeIn 0.2s ease'
            }}>
              <div style={{ fontSize: '44px', marginBottom: '16px' }}>⚠️</div>
              <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#111' }}>
                Round off to zero
              </h3>
              <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
                Costing cannot be applied.
              </p>
              <button
                onClick={() => setShowRoundOffModal(false)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}
        {/* ── Duplicate Prevention Modal ── */}
        {showDuplicateModal && duplicateConflictData && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px'
          }}>
            <div style={{
              background: '#fff', borderRadius: '16px', padding: '28px',
              maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              animation: 'fadeIn 0.2s ease'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '40px', marginBottom: '8px' }}>⚠️</div>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#111' }}>
                  Possible Duplicate Detected
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
                  {duplicateConflictData.message || `"${duplicateConflictData.attempted_name}" looks similar to "${duplicateConflictData.existing_item_name}"`}
                </p>
              </div>

              <div style={{
                background: 'transparent', borderRadius: '10px', padding: '14px',
                marginBottom: '16px', border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Match Details
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                  <span style={{ color: '#6b7280' }}>You typed:</span>
                  <span style={{ fontWeight: 600, color: '#111' }}>{duplicateConflictData.attempted_name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                  <span style={{ color: '#6b7280' }}>Existing item:</span>
                  <span style={{ fontWeight: 600, color: '#1e3a5f' }}>{duplicateConflictData.existing_item_name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span style={{ color: '#6b7280' }}>Confidence:</span>
                  <span style={{ fontWeight: 600, color: duplicateConflictData.confidence >= 85 ? '#dc2626' : '#d97706' }}>
                    {duplicateConflictData.confidence}% ({duplicateConflictData.layer})
                  </span>
                </div>
              </div>

              {/* Reason input for force create */}
              <NiceSelect
                value={duplicateForceReason}
                onChange={(val) => setDuplicateForceReason(val)}
                options={DUPLICATE_OVERRIDE_OPTIONS}
                placeholder="Select override reason"
                fullWidth={true}
                className="duplicate-override-select"
                style={{
                  marginBottom: '16px'
                }}
              />

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleDuplicateUseExisting}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '10px',
                    background: '#000', color: '#fff', border: 'none',
                    fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#1a1a1a'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#000'}
                >
                  ✓ Use Existing
                </button>
                <button
                  onClick={handleDuplicateForceCreate}
                  disabled={!duplicateForceReason}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '10px',
                    background: duplicateForceReason ? '#fff' : '#f3f4f6',
                    color: duplicateForceReason ? '#000' : '#9ca3af',
                    border: `2px solid ${duplicateForceReason ? '#000' : '#d1d5db'}`,
                    fontSize: '14px', fontWeight: 700, cursor: duplicateForceReason ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s'
                  }}
                >
                  Force Create New
                </button>
              </div>

              <button
                onClick={() => { setShowDuplicateModal(false); setDuplicateConflictData(null); setDuplicateForceReason('Intentional Variant'); pendingSaveListRef.current = null; pendingSavePlateCountRef.current = null; pendingConfirmListRef.current = null; }}
                style={{
                  width: '100%', marginTop: '10px', padding: '10px',
                  background: 'transparent', border: 'none', color: '#9ca3af',
                  fontSize: '13px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {/* Complimentary Item Page - Use dedicated component */}
        {showComplimentaryItemPage && selectedComplimentaryItem && (
          <ComplimentaryItemsPage 
            item={selectedComplimentaryItem}
            onBack={handleBackFromComplimentary}
            onSave={saveComplimentaryIngredients}
          />
        )}

        {/* Original Recipe Editor - Hide when showing complimentary page */}
        {!showComplimentaryItemPage && (
        <div className="viewport-wrapper">
        <div className="itemmap-container editor">
          {/* Top header with back button and page title */}
          <div className="itemmap-header">
            <button
              type="button"
              className="back-arrow-btn"
              aria-label="Back to Item Mapping"
              onClick={handleRecipeBackButton}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h3 className="page-title">Ingredient Mapping</h3>
          </div>

          <div className="recipe-titlebar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="recipe-title" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {formatSummaryTitle(selectedRecipe.name)}
            </div>
            <div ref={plateMenuRef} style={{ position: 'relative', display: 'inline-block', marginLeft: '8px', flexShrink: 0 }}>
              <button
                onClick={() => {
                  const next = !showPlateMenu;
                  setShowPlateMenu(next);
                  if (!next) setShowCustomPlateInput(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  marginTop:'1rem',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#111',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
              >
                <span style={{ fontSize: '14px', lineHeight: 1 }}>☰</span>
                <span>{plateStep} {plateStep === 1 ? 'plate' : 'plates'}</span>
              </button>
              {showPlateMenu && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  minWidth: '180px',
                  textAlign: 'left'
                }}>
                  {[1, 10, 100].map(size => (
                    <div
                      key={size}
                      onClick={() => applyPlateStepChange(size)}
                      style={{
                        padding: '12px 20px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: plateStep === size ? '700' : '400',
                        background: '#ffffff',
                        color: '#111',
                        borderBottom: size !== 100 ? '1px solid #f3f4f6' : 'none',
                        transition: 'background 0.15s',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#ffffff'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                    >
                      <span>{size} {size === 1 ? 'plate' : 'plates'}</span>
                      {plateStep === size && (
                        <FontAwesomeIcon icon={faCheck} style={{ marginLeft: '6px', color: '#16a34a' }} />
                      )}
                    </div>
                  ))}
                  <div
                    onClick={() => {
                      setShowCustomPlateInput(true);
                      if (isCustomPlateStep) {
                        setCustomPlateStep(String(plateStep));
                      }
                    }}
                    style={{
                      padding: showCustomPlateInput ? '10px 20px 4px 20px' : '12px 20px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: isCustomPlateStep ? '700' : '400',
                      background: '#ffffff',
                      color: '#111',
                      borderTop: '1px solid #f3f4f6',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                  >
                    <span>{isCustomPlateStep ? `${plateStep} plates (custom)` : 'Custom plates'}</span>
                    {isCustomPlateStep && (
                      <FontAwesomeIcon icon={faCheck} style={{ marginLeft: '6px', color: '#16a34a' }} />
                    )}
                  </div>
                  {showCustomPlateInput && (
                    <div style={{ padding: '4px 16px 12px 16px', borderTop: 'none', background: '#ffffff' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={customPlateStep}
                          onChange={(e) => setCustomPlateStep(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') applyCustomPlateStep();
                          }}
                          placeholder="Enter plates"
                          style={{
                            flex: 1,
                            height: '32px',
                            padding: '0 10px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '13px',
                            fontWeight: '600'
                          }}
                        />
                        <button
                          type="button"
                          onClick={applyCustomPlateStep}
                          style={{
                            height: '32px',
                            padding: '0 12px',
                            borderRadius: '6px',
                            border: '1px solid #111',
                            background: '#111',
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer'
                          }}
                        >
                          Set
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="qrb-recipe-hero-shell">
            <img
              src={selectedRecipe.image || selectedRecipe.fallbackImage || 'https://via.placeholder.com/300x150?text=Generating+Image...'}
              alt="Dish"
              className="recipe-hero"
              style={{ opacity: selectedRecipe.imageGenerating ? 0.6 : 1 }}
            />
            
            {/* Custom Image Upload Modal Component */}
            <CustomImageUploadModal
              isOpen={!!selectedRecipe}
              recipeId={selectedRecipe?.id}
              recipeName={selectedRecipe?.name}
              currentImageUrl={selectedRecipe?.image}
              imageSource={selectedRecipe?.image_source}
              showCameraButton={!selectedRecipe.imageGenerating}
              onUploadSuccess={(data) => {
                // Update selected recipe
                if (data.imageUrl) {
                  setSelectedRecipe(prev => ({
                    ...prev,
                    image: data.imageUrl,
                    image_url: data.imageUrl,
                    image_source: 'custom_upload'
                  }));
                  
                  // Update recipes list
                  setRecipes(prevRecipes => 
                    prevRecipes.map(r => 
                      r.id === selectedRecipe.id 
                        ? { ...r, image: data.imageUrl, image_url: data.imageUrl, image_source: 'custom_upload' }
                        : r
                    )
                  );
                  
                  showToast('Custom image uploaded successfully!');
                }
                
                // Handle variants update
                if (data.variantsUpdated && data.count > 0) {
                  showToast(`Image applied to ${data.count} variant(s)`);
                  // Refresh recipes
                  const businessId = UnitMappingService.getBusinessId();
                  RecipesService.getRecipes(businessId).then(refreshedRecipes => {
                    setRecipes(applyLocalReviewDrafts(refreshedRecipes));
                  });
                }
              }}
              onRevertToAI={() => {
                // Update selected recipe to trigger AI regeneration
                setSelectedRecipe(prev => ({
                  ...prev,
                  image: null,
                  image_url: null,
                  image_source: 'ai_generated',
                  skipAutoImageGeneration: false
                }));
                
                // Update recipes list
                setRecipes(prevRecipes => 
                  prevRecipes.map(r => 
                    r.id === selectedRecipe.id 
                      ? { ...r, image: null, image_url: null, image_source: 'ai_generated' }
                      : r
                  )
                );
                
                showToast(' Custom image removed. AI image will regenerate...');
              }}
            />
            
            {selectedRecipe.imageGenerating && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                🎨 Generating image...
              </div>
            )}
          </div>

          {/* Recipe Description - Editable */}
          {selectedRecipe && (
            <div>
              <div style={{
                padding: '12px 16px',
                background: '#f9fafb',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid #e5e7eb',
                transition: 'all 0.2s',
                cursor: editingDescription ? 'default' : 'pointer',
                minHeight: '80px',
                position: 'relative'
              }}
              onMouseEnter={(e) => { if (!editingDescription) e.currentTarget.style.borderColor = '#d1d5db'; }}
              onMouseLeave={(e) => { if (!editingDescription) e.currentTarget.style.borderColor = '#e5e7eb'; }}
              >
                <div style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: 'left'
                }}>
                  Recipe Description
                </div>
                
                {editingDescription ? (
                  <div>
                    <textarea
                      value={descriptionText}
                      onChange={(e) => setDescriptionText(e.target.value)}
                      autoFocus
                      style={{
                        width: '100%',
                        minHeight: '100px',
                        padding: '12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        outline: 'none',
                        transition: 'border 0.2s'
                      }}
                      placeholder="Enter recipe description..."
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button
                        onClick={saveDescription}
                        style={{
                          background: '#000000',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#1f1f1f'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#000000'}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingDescription(false);
                          setDescriptionText(selectedRecipe.description || '');
                        }}
                        style={{
                          background: '#ffffff',
                          color: '#000000',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => setEditingDescription(true)}
                    style={{
                      fontSize: '14px',
                      color: descriptionText ? '#374151' : '#9ca3af',
                      lineHeight: '1.6',
                      textAlign: 'left'
                    }}>
                    {descriptionText || 'Click to add description...'}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Composite detection runs silently in background - no UI display */}

          <div className="panel" style={{ marginTop: 16, overflow: 'visible', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px', position: 'relative', zIndex: 60, overflow: 'visible' }}>
              <h4 className="panel-title" style={{ textAlign: 'left', fontSize: '1.4rem', margin: 0 }}>Ingredients</h4>
              
              {/* Undo/Redo buttons - moved to right side */}
              <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
                <button
                  onClick={handleUndo}
                  disabled={historyIndex < 0}
                  style={{
                    background: historyIndex < 0 ? '#f3f4f6' : '#ffffff',
                    color: historyIndex < 0 ? '#9ca3af' : '#000000',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: historyIndex < 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                  title="Undo"
                >
                  <FontAwesomeIcon icon={faUndo} style={{ fontSize: 12 }} />
                </button>
                <button
                  onClick={handleRedo}
                  disabled={historyIndex >= ingredientHistory.length - 1}
                  style={{
                    background: historyIndex >= ingredientHistory.length - 1 ? '#f3f4f6' : '#ffffff',
                    color: historyIndex >= ingredientHistory.length - 1 ? '#9ca3af' : '#000000',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: historyIndex >= ingredientHistory.length - 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                  title="Redo"
                >
                  <FontAwesomeIcon icon={faRedo} style={{ fontSize: 12 }} />
                </button>
                <button
                  onClick={() => setShowDeleteAllModal(true)}
                  disabled={ingredientList.length === 0}
                  style={{
                    background: ingredientList.length === 0 ? '#f3f4f6' : '#ffffff',
                    color: ingredientList.length === 0 ? '#9ca3af' : '#dc2626',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: ingredientList.length === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                  title="Delete All Ingredients"
                >
                  <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
                </button>
              </div>
              
              {/* Hide Customizations and Complimentary Items buttons for Sides category */}
              {selectedRecipe?.category?.toLowerCase() !== 'sides' && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap', position: 'relative', zIndex: 70, overflow: 'visible' }}>
                {/* Customizations Dropdown */}
                <div ref={customizationsDropdownRef} style={{ position: 'relative', flex: '1 1 auto', zIndex: showCustomizationsDropdown ? 5000 : 1, overflow: 'visible' }}>
                  <button
                    onClick={() => setShowCustomizationsDropdown(!showCustomizationsDropdown)}
                    className="itemmap-dropdown-btn"
                    style={{
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s',
                      position: 'relative',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      justifyContent: 'center'
                    }}
                  >
                    Customizations
                    <FaChevronDown style={{ 
                      fontSize: '12px',
                      transform: showCustomizationsDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }} />
                  </button>
                  
                  {showCustomizationsDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      marginTop: '8px',
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      width: '200px',
                      maxWidth: '85vw',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      zIndex: 5001,
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }}>
                      <style>{`
                        div::-webkit-scrollbar {
                          display: none;
                        }
                      `}</style>
                      {customizations.map((customization, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '12px 16px',
                            borderBottom: index < customizations.length - 1 ? '1px solid #e5e7eb' : 'none',
                            color: '#000000',
                            cursor: 'pointer',
                            transition: 'background 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            textAlign: 'left'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <span 
                            className="complimentary-item-name"
                            onClick={() => handleCustomizationClick(customization)}
                            style={{ flex: 1, cursor: 'pointer', textAlign: 'left' }}
                          >
                            {customization}
                          </span>
                          <div 
                            className="checkbox-wrapper"
                            onClick={(e) => handleCustomizationClick(customization)}
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedCustomizations.includes(customization)}
                              onChange={() => {}}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                accentColor: '#000000'
                              }}
                            />
                          </div>
                        </div>
                      ))}
                      <div
                        onClick={handleAddNewCustomization}
                        style={{
                          padding: '12px 16px',
                          background: '#f9fafb',
                          color: '#000000',
                          fontWeight: '600',
                          textAlign: 'center',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#f9fafb'}
                      >
                        + Add Custom
                      </div>
                    </div>
                  )}
                </div>

                {/* Complimentary Items Dropdown */}
                <div ref={complimentaryDropdownRef} style={{ position: 'relative', flex: '1 1 auto', zIndex: showComplimentaryDropdown ? 5000 : 1, overflow: 'visible' }}>
                  <button
                    onClick={() => setShowComplimentaryDropdown(!showComplimentaryDropdown)}
                    className="itemmap-dropdown-btn"
                    style={{
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s',
                      position: 'relative',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      justifyContent: 'center'
                    }}
                  >
                    Complimentary Items
                    <FaChevronDown style={{ 
                      fontSize: '12px',
                      transform: showComplimentaryDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }} />
                  </button>
                
                {showComplimentaryDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    right: '0',
                    marginTop: '8px',
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    minWidth: '200px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    zIndex: 5001
                  }}>
                    {complimentaryRecipesLibrary.filter(item => {
                      const itemName = item?.name;
                      return itemName && itemName.trim();
                    }).map((item, index, filteredArray) => {
                      const itemName = item.name;
                      return (
                        <div
                          key={item.comp_recipe_id || index}
                          style={{
                            padding: '12px 16px',
                            borderBottom: index < filteredArray.length - 1 ? '1px solid #e5e7eb' : 'none',
                            cursor: 'pointer',
                            transition: 'background 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            textAlign: 'left'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <span 
                            className="complimentary-item-name"
                            onClick={() => handleOpenComplimentaryEditor(item)}
                            style={{ 
                              flex: 1, 
                              cursor: 'pointer', 
                              textAlign: 'left',
                              color: '#000000',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                          >
                            {itemName}
                          </span>
                          <div 
                            className="checkbox-wrapper"
                            onClick={(e) => handleComplimentaryItemClick(item, e)}
                            style={{ display: 'flex', alignItems: 'center', marginLeft: '12px' }}
                          >
                            <input
                              type="checkbox"
                              checked={linkedComplimentaryItems.some(linked => linked.comp_recipe_id === item.comp_recipe_id)}
                              onChange={(e) => e.preventDefault()}
                              onClick={(e) => e.preventDefault()}
                              readOnly
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                accentColor: '#000000',
                                pointerEvents: 'none'
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    <div
                      onClick={handleAddNewComplimentaryItem}
                      style={{
                        padding: '12px 16px',
                        background: '#f9fafb',
                        color: '#000000',
                        fontWeight: '600',
                        textAlign: 'center',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#f9fafb'}
                    >
                      + Add New Item
                    </div>
                  </div>
                )}
              </div>
              </div>
              )}
            </div>

            {ingredientList.map((item, idx) => (
              <div
                key={idx}
                className={`ingredient-row ${editingIngredientIndex === idx ? 'editing' : ''} ${ (typeof window !== 'undefined' && window.innerWidth <= 640 && editingIngredientIndex !== idx)
                  ? (((item.name || '').trim().length <= 18) ? 'row-inline' : 'row-stacked')
                  : ''} ${Math.abs(swipeOffsets[idx] || 0) > 10 ? 'swipe-active' : ''}`}
                style={{
                  transform: Math.abs(swipeOffsets[idx] || 0) > 0 ? `translateX(${swipeOffsets[idx]}px)` : undefined,
                  transition: swipingIndex === idx ? 'none' : 'transform 0.12s ease',
                  zIndex: openUnitIndex === idx ? 220 : (editingIngredientIndex === idx ? 150 : 1)
                }}
                onTouchStart={(e) => handleRowTouchStart(idx, e)}
                onTouchMove={(e) => handleRowTouchMove(idx, e)}
                onTouchEnd={() => handleRowTouchEnd(idx)}
              >
                {editingIngredientIndex === idx ? (
                  <div
                    ref={editingIngredientIndex === idx ? ingredientEditorRef : null}
                    className="itemmap-ingredient-editing-wrapper"
                    style={{ position: 'relative', width: '100%' }}
                  >
                    {/* Action icons at absolute top right - Order: Tick, Cross, Dustbin */}
                    <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 10, alignItems: 'center', zIndex: 10 }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeIngredientEditor();
                        }}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          margin: 0,
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                        title="Close"
                      >
                        <FontAwesomeIcon icon={faTimes} style={{ fontSize: 15, color: '#ef4444', fontWeight: '700' }} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteIngredient(idx);
                        }}
                        className="itemmap-edit-icon--delete"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          margin: 0,
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: 15, color: '#666', fontWeight: '700' }} />
                      </button>
                    </div>
                    
                    {/* Main content below */}
                    <div className="itemmap-ingredient-editing-main" style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
                    {/* Name on the left, same as normal row */}
                    <div className="itemmap-ingredient-name-input" style={{ flex: 1, minWidth: 0, position: 'relative', textAlign: 'left' }}>
                      <input
                        ref={editNameInputRef}
                        type="text"
                        value={formData.name}
                        onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setShowNameSuggestions(true); }}
                        onFocus={() => setShowNameSuggestions(true)}
                        onBlur={() => {
                          // Live-sync typed name into ingredientList so auto-save picks it up
                          const typed = formData.name?.trim();
                          if (typed && typed !== 'New Ingredient' && editingIngredientIndex !== null) {
                            setIngredientList(prev => {
                              const updated = [...prev];
                              const row = updated[editingIngredientIndex];
                              if (row && row.name !== typed) {
                                updated[editingIngredientIndex] = { ...row, name: typed };
                              }
                              return updated;
                            });
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const typed = formData.name?.trim();
                            if (typed && typed !== 'New Ingredient' && editingIngredientIndex !== null) {
                              setIngredientList(prev => {
                                const updated = [...prev];
                                const row = updated[editingIngredientIndex];
                                if (row && row.name !== typed) {
                                  updated[editingIngredientIndex] = { ...row, name: typed };
                                }
                                return updated;
                              });
                            }
                            setShowNameSuggestions(false);
                            if (quantityInputRef.current) {
                              quantityInputRef.current.focus();
                              quantityInputRef.current.select();
                            }
                          }
                        }}
                        placeholder="Search Ingredient Name"
                        className="text-input"
                        style={{ fontWeight: 500, width: '100%', textAlign: 'left', fontSize: '1rem', lineHeight: '1.2', padding: 0, border: 'none', background: 'transparent' }}
                        visibleCount={4}
                      />
                      {showNameSuggestions && (
                        (() => {
                          const isLastRow = idx === ingredientList.length - 1;
                          return (
                        <div className="suggestions" style={{ 
                          position: 'absolute',
                          top: isLastRow ? 'auto' : '100%',
                          bottom: isLastRow ? '100%' : 'auto',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          maxHeight: 400,
                          overflowY: 'auto',
                          zIndex: 3000,
                          marginTop: 4,
                          minWidth: '280px',
                          width: '100%'
                        }}>
                          {/* Ingredient Names Section */}
                          {nameSuggestions?.length > 0 && (
                            <>
                              <div style={{
                                padding: '8px 12px',
                                background: '#f9fafb',
                                borderBottom: '1px solid #e5e7eb',
                                fontWeight: 600,
                                fontSize: 12,
                                color: '#666',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Ingredient Names
                              </div>
                              {nameSuggestions.map(s => (
                                <div 
                                  key={`ing-${s.id || s.name}`} 
                                  onClick={() => pickNameSuggestion(s)} 
                                  className="suggestion-item"
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f3f4f6',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'background 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                  <span style={{ fontWeight: 500 }}>{s.name}</span>
                                  {s.standardUnit && (
                                    <span className="suggestion-unit" style={{
                                      background: '#f3f4f6',
                                      padding: '2px 8px',
                                      borderRadius: 4,
                                      fontSize: 12,
                                      color: '#666'
                                    }}>{s.standardUnit}</span>
                                  )}
                                </div>
                              ))}
                            </>
                          )}
                          
                          {/* Saved Preparations Section */}
                          {preparationSuggestions?.length > 0 && (
                            <>
                              <div style={{
                                padding: '8px 12px',
                                background: '#f9fafb',
                                borderBottom: '1px solid #e5e7eb',
                                fontWeight: 600,
                                fontSize: 12,
                                color: '#666',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginTop: nameSuggestions?.length > 0 ? 4 : 0
                              }}>
                                Saved Preparations
                              </div>
                              {preparationSuggestions.map(s => (
                                <div 
                                  key={`prep-${s.id || s.name}`} 
                                  onClick={() => pickNameSuggestion(s)} 
                                  className="suggestion-item"
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f3f4f6',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'background 0.2s',
                                    background: 'white'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                  <span style={{ fontWeight: 500 }}>{s.name}</span>
                                  {s.standardUnit && (
                                    <span className="suggestion-unit" style={{
                                      background: '#f3f4f6',
                                      padding: '2px 8px',
                                      borderRadius: 4,
                                      fontSize: 12,
                                      color: '#666'
                                    }}>{s.standardUnit}</span>
                                  )}
                                </div>
                              ))}
                            </>
                          )}
                          
                          {/* Menu Items Section */}
                          {menuSuggestions?.length > 0 && (
                            <>
                              <div style={{
                                padding: '8px 12px',
                                background: '#f9fafb',
                                borderBottom: '1px solid #e5e7eb',
                                fontWeight: 600,
                                fontSize: 12,
                                color: '#666',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginTop: (nameSuggestions?.length > 0 || preparationSuggestions?.length > 0) ? 4 : 0
                              }}>
                                Menu Items (Import All Ingredients)
                              </div>
                              {menuSuggestions.map(m => (
                                <div 
                                  key={`menu-${m.id || m.name}`} 
                                  onClick={() => pickMenuItemSuggestion(m)} 
                                  className="suggestion-item"
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f3f4f6',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'background 0.2s',
                                    background: 'white'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                  <span style={{ fontWeight: 500 }}>{m.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}</span>
                                  <span style={{
                                    background: '#f3f4f6',
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    fontSize: 11,
                                    color: '#666',
                                    fontWeight: 600
                                  }}>IMPORT</span>
                                </div>
                              ))}
                            </>
                          )}
                          
                          {/* No Results Message */}
                          {nameSuggestions?.length === 0 && preparationSuggestions?.length === 0 && menuSuggestions?.length === 0 && (
                            <div style={{ 
                              padding: '16px', 
                              textAlign: 'center', 
                              color: '#999',
                              fontSize: 14
                            }}>
                              {formData.name?.trim() ? 
                                `No items found. "${formData.name}" will be created as new ingredient.` : 
                                'Start typing to search ingredients or menu items'
                              }
                            </div>
                          )}
                        </div>
                          );
                        })()
                      )}
                    </div>
                    {/* Controls on the right with small actions tucked below the quantity/unit cluster */}
                    <div className="itemmap-ingredient-quantity-row" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <div className="controls-inline">
                        <button
                          className="square-btn"
                          onClick={() => setFormData(prev => ({ ...prev, quantity: normalizeQty((prev.quantity || 0) - 1) }))}
                        >
                          −
                        </button>
                        <input
                          ref={quantityInputRef}
                          type="number"
                          step="any"
                          className="number-input"
                          value={formData.quantity}
                          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                          onFocus={(e) => {
                            if (Number(formData.quantity) === 0) {
                              setFormData(prev => ({ ...prev, quantity: '' }));
                            } else {
                              e.target.select();
                            }
                          }}
                          onBlur={() => {
                            // SP9 sacred numbers: blur commits exactly what was typed.
                            setFormData(prev => ({ ...prev, quantity: normalizeQty(prev.quantity) }));
                          }}
                          onWheel={(e) => e.target.blur()}
                        />
                        <button
                          className="square-btn"
                          onClick={() => setFormData(prev => ({ ...prev, quantity: normalizeQty((prev.quantity || 0) + 1) }))}
                        >
                          ＋
                        </button>
                        <SearchableSelect
                          value={formData.unit}
                          onChange={(newUnit) => {
                            const sameQty = formData.quantity;
                            setFormData({ ...formData, unit: newUnit, quantity: sameQty });
                          }}
                          onAddClick={() => { setKitchenUnitForm(prev => ({ ...prev, item: formData.name || '' })); setShowKitchenUnitModal(true); }}
                          openUpward={idx === ingredientList.length - 1}
                          options={[
                            { value: 'g', label: 'g' },
                            { value: 'kg', label: 'kg' },
                            { value: 'ml', label: 'ml' },
                            { value: 'L', label: 'L' },
                            { value: 'add', label: 'add', className: 'unit-add-option' }
                          ]}
                          placeholder="Search unit..."
                          style={{ width: 68 }}
                        />
                      </div>
                    </div>
                    {/* end right-side column */}
                    </div>
                    {/* end main content */}
                  </div>
                ) : (
                  <>
                    <div
                      style={{ fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, cursor: 'pointer', color: '#000000' }}
                      className="ingredient-name clickable"
                      onClick={() => startEditingIngredient(idx)}
                    >{item.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}</div>
                    {item.name !== 'New Ingredient' && (
                      <div className="controls-inline">
                        <button 
                          onClick={() => {
                            setLocalInputValues(prev => {
                              const updated = { ...prev };
                              delete updated[idx];
                              return updated;
                            });
                            updateQuantity(idx, (getDisplayQty(item) || 0) - 1, true);
                          }} 
                          className="square-btn"
                        >
                          −
                        </button>
                        <input 
                          type="number" 
                          step="any"
                          className="number-input" 
                          value={localInputValues[idx] !== undefined ? localInputValues[idx] : (getDisplayQty(item) || '')} 
                          onChange={(e) => {
                            const val = e.target.value;
                            setLocalInputValues(prev => ({ ...prev, [idx]: val }));
                            updateQuantity(idx, val);
                          }} 
                          onFocus={(e) => {
                            const val = e.target.value;
                            if (Number(val) === 0) {
                              setLocalInputValues(prev => ({ ...prev, [idx]: '' }));
                            } else {
                              e.target.select();
                            }
                          }} 
                          onBlur={(e) => {
                            const val = e.target.value;
                            setLocalInputValues(prev => {
                              const updated = { ...prev };
                              delete updated[idx];
                              return updated;
                            });
                            updateQuantity(idx, val, true);
                          }}
                          onWheel={(e) => e.target.blur()}
                          visibleCount={4}
                        />
                        <button 
                          onClick={() => {
                            setLocalInputValues(prev => {
                              const updated = { ...prev };
                              delete updated[idx];
                              return updated;
                            });
                            updateQuantity(idx, (getDisplayQty(item) || 0) + 1, true);
                          }} 
                          className="square-btn"
                        >
                          ＋
                        </button>
                        <SearchableSelect
                          value={item.unit || 'g'}
                          onChange={(newUnit) => {
                            const prevList = cloneIngredientList(ingredientList);
                            const updated = [...ingredientList];
                            const curQty = Number(getDisplayQty(updated[idx]) || 0);
                            const withNewQty = setQtyForPlateStep(updated[idx], curQty);
                            updated[idx].qty10 = withNewQty.qty10;
                            updated[idx].qty100 = withNewQty.qty100;
                            updated[idx].unit = newUnit;
                            setIngredientList(updated);
                            pushIngredientHistory('update', prevList, cloneIngredientList(updated));
                          }}
                          onAddClick={() => { setKitchenUnitForm(prev => ({ ...prev, item: item.name || '' })); setShowKitchenUnitModal(true); }}
                          openUpward={idx === ingredientList.length - 1}
                          isOpen={openUnitIndex === idx}
                          onToggle={(isOpen) => setOpenUnitIndex(prev => isOpen ? idx : (prev === idx ? null : prev))}
                          options={(() => {
                            // Merge base units with ingredient-specific kitchen tools for this item only
                            const rawName = (item.name || '').trim();
                            const nameKey = rawName.toLowerCase();
                            let extras = ingredientToolUnitsMap[nameKey] || [];
                            if (!extras.length) {
                              const normRaw = normalizeName(rawName);
                              const candidates = Object.keys(ingredientToolUnitsMap || {});
                              let best = { key: null, dist: Infinity };
                              for (const k of candidates) {
                                const d = levenshtein(normRaw, normalizeName(k));
                                if (d < best.dist) best = { key: k, dist: d };
                              }
                              if (best.key && (best.dist <= 2 || normalizeName(best.key).includes(normalizeName(rawName)) || normalizeName(rawName).includes(normalizeName(best.key)))) {
                                extras = ingredientToolUnitsMap[best.key] || [];
                              }
                            }
                            const base = unitOptions || [];
                            const seen = new Set();
                            const merged = [];
                            for (const u of [...base, ...extras]) {
                              const v = (u.value || '').toLowerCase();
                              if (!v || seen.has(v)) continue;
                              seen.add(v);
                              merged.push(u);
                            }
                            return merged.length > 0 ? merged : [{ value: 'g', label: 'g' }];
                          })()}
                          placeholder="Search unit..."
                          style={{ width: 68 }}
                        />
                        <button
                          className="ingredient-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteIngredient(idx);
                          }}
                          title="Delete"
                          type="button"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="add-ingredient-button" onClick={async () => {
                // Auto-save current ingredient if it has valid data
                if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
                  const hasValidName = formData.name && formData.name.trim() && formData.name.trim() !== 'New Ingredient';
                  if (hasValidName) {
                    const saved = await saveIngredient({ silent: true });
                    if (!saved) {
                      showToast('Please complete the current ingredient (check name, quantity, and unit)', 'warning');
                      return;
                    }
                  } else {
                    // Remove the unsaved "New Ingredient" placeholder
                    const cleaned = ingredientList.filter(ing => ing.name !== 'New Ingredient');
                    setIngredientList(cleaned);
                    setEditingIngredientIndex(null);
                    setFormData({ name: '', quantity: 0, unit: '' });
                  }
                }
                
                // Use latest ingredientList (after potential save/clean above)
                setTimeout(() => {
                  setIngredientList(prev => {
                    const defaults = setQtyForPlateStep({}, 0);
                    const newIngredient = { name: 'New Ingredient', unit: '', qty10: defaults.qty10, qty100: defaults.qty100 };
                    const updatedIngredients = [...prev, newIngredient];
                    setEditingIngredientIndex(updatedIngredients.length - 1);
                    setFormData({ name: '', quantity: 0, unit: '' });
                    return updatedIngredients;
                  });
                }, 50);
              }} style={{ flex: 1, fontSize: '1.1rem' }}>
                Add Ingredient
              </button>
              {/* Scan Receipt removed as requested */}
            </div>
          </div>
          <div className="footer-actions" style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn--save"
              onClick={handleMarkAsReview}
              style={{ flex: 1, background: '#e5e7eb', color: '#111', border: '1px solid #d1d5db' }}
            >
              Mark as Review
            </button>
            <button
              className="btn--save"
              onClick={handleSaveClick}
              style={{ flex: 1, background: '#000', color: '#fff', border: '1px solid #000' }}
            >
              Confirm
            </button>
          </div>
          {showKitchenUnitModal && (
            <div className="modal-overlay" style={{
              position: 'fixed', left: 0, right: 0, top: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
            }}>
              <div className="modal-card" style={{
                width: '100%', maxWidth: 520, background: '#fff', borderRadius: 12, padding: 20,
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
              }}>
                {/* Item (searchable dropdown) */}
                <div style={{ marginBottom: 12, position: 'relative' }}>
                  <div style={{ color: '#111', fontWeight: 'bold', marginBottom: 6 }}>Item</div>
                  <div
                    onClick={() => { setModalItemOpen(o => !o); setModalToolOpen(false); setModalUnitOpen(false); }}
                    className="nice-field"
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {kitchenUnitForm.item || formData?.name || 'Select item'}
                    </span>
                    <FaChevronDown />
                  </div>
                  {modalItemOpen && (
                    <div className="popup-panel" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 8px)' }}>
                      <div className="popup-search">
                        <input
                          className="popup-search-input"
                          value={modalItemSearch}
                          onChange={e => setModalItemSearch(e.target.value)}
                          placeholder="Search item..."
                        />
                      </div>
                      <div className="popup-list" style={{ maxHeight: 220 }}>
                        {sortBySearchPriority(inventoryItemNames || [], modalItemSearch, (itemName) => itemName || '')
                          .map((n, i) => (
                            <div
                              key={i}
                              onClick={() => { setKitchenUnitForm(prev => ({ ...prev, item: n })); setModalItemOpen(false); setModalItemSearch(''); }}
                              className="popup-item"
                              style={{ fontWeight: 'bold' }}
                            >{n}</div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Row: Kitchen Tool, Quantity, Unit */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {/* Kitchen tool dropdown (searchable) */}
                  <div style={{ flex: 1, position: 'relative' }}>
                    <div
                      onClick={() => { setModalToolOpen(o => !o); setModalUnitOpen(false); setModalShowAddTool(false); }}
                      className="nice-field"
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{kitchenUnitForm.kitchenTool || 'Select Tool'}</span>
                      <FaChevronDown />
                    </div>
                    {modalToolOpen && (
                      <div className="popup-panel" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 8px)' }}>
                        <div className="popup-search">
                          <input
                            className="popup-search-input"
                            value={modalToolSearch}
                            onChange={e => setModalToolSearch(e.target.value)}
                            placeholder="Search kitchen tool..."
                          />
                        </div>
                        <div className="popup-list" style={{ maxHeight: 180 }}>
                          {sortBySearchPriority(modalKitchenTools, modalToolSearch, (tool) => tool || '')
                            .map((t) => (
                              <div
                                key={t}
                                onClick={() => { setKitchenUnitForm(prev => ({ ...prev, kitchenTool: t })); setModalToolOpen(false); setModalToolSearch(''); }}
                                className="popup-item"
                              >{t}</div>
                            ))}
                        </div>
                        <div className="popup-footer">
                          {!modalShowAddTool ? (
                            <button
                              onClick={() => setModalShowAddTool(true)}
                              style={{ width: '100%', padding: '10px', background: '#000', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 'bold', cursor: 'pointer' }}
                            >
                              + Add Tool
                            </button>
                          ) : (
                            <div>
                              <input
                                placeholder="Enter Tool Name..."
                                value={modalNewToolName}
                                onChange={(e) => setModalNewToolName(e.target.value)}
                                maxLength={15}
                                style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6, marginBottom: 8 }}
                              />
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                  onClick={() => {
                                    const name = (modalNewToolName || '').trim();
                                    if (!name) return;
                                    // avoid duplicates (case-insensitive)
                                    if (!modalKitchenTools.some(t => t.toLowerCase() === name.toLowerCase())) {
                                      setModalKitchenTools(prev => [...prev, name]);
                                    }
                                    setKitchenUnitForm(prev => ({ ...prev, kitchenTool: name }));
                                    setModalNewToolName('');
                                    setModalShowAddTool(false);
                                    setModalToolOpen(false);
                                  }}
                                  style={{ flex: 1, padding: '10px', background: '#000', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => { setModalShowAddTool(false); setModalNewToolName(''); }}
                                  style={{ flex: 1, padding: '10px', background: '#f5f5f5', color: '#000', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quantity */}
                  <div style={{
                    display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: 10, overflow: 'hidden',
                    height: 44
                  }}>
                    <button
                      onClick={() => setKitchenUnitForm(prev => ({ ...prev, quantity: String(Math.max(0, Number(prev.quantity) - 1)) }))}
                      style={{ width: 36, height: '100%', border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer' }}
                    >−</button>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={kitchenUnitForm.quantity}
                      onChange={(e) => {
                        const v = e.target.value; if (/^\d*$/.test(v)) setKitchenUnitForm(prev => ({ ...prev, quantity: v }));
                      }}
                      style={{ width: 60, border: 'none', outline: 'none', textAlign: 'center', fontSize: 16 }}
                    />
                    <button
                      onClick={() => setKitchenUnitForm(prev => ({ ...prev, quantity: String((Number(prev.quantity) || 0) + 1) }))}
                      style={{ width: 36, height: '100%', border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer' }}
                    >+</button>
                  </div>

                  {/* Unit dropdown (searchable) */}
                  <div style={{ flex: 1, position: 'relative' }}>
                    <div
                      onClick={() => { setModalUnitOpen(o => !o); setModalToolOpen(false); }}
                      className="nice-field"
                    >
                      <span>{kitchenUnitForm.unit || 'Select unit'}</span>
                      <FaChevronDown />
                    </div>
                    {modalUnitOpen && (
                      <div className="popup-panel" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 8px)' }}>
                        <div className="popup-search">
                          <input
                            className="popup-search-input"
                            value={modalUnitSearch}
                            onChange={e => setModalUnitSearch(e.target.value)}
                            placeholder="Search unit..."
                          />
                        </div>
                        <div className="popup-list" style={{ maxHeight: 180 }}>
                          {sortBySearchPriority(baseUnitOptions, modalUnitSearch, (unitOption) => unitOption.label || unitOption.value || '')
                            .map(u => (
                              <div
                                key={u.value}
                                onClick={() => { setKitchenUnitForm(prev => ({ ...prev, unit: u.value })); setModalUnitOpen(false); setModalUnitSearch(''); }}
                                className="popup-item"
                              >{u.label}</div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                  <button
                    onClick={() => setShowKitchenUnitModal(false)}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: '#ffdddd', color: '#d00', border: 'none', borderRadius: 10, fontWeight: 'bold', cursor: 'pointer'
                    }}
                  >
                    Remove Kitchen Unit
                  </button>
                  <button
                    onClick={handleSaveKitchenUnit}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: '#000', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 'bold', cursor: 'pointer'
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add New Complimentary Item Modal */}
          {showAddComplimentaryModal && (
            <div
              className="qrb-comp-create-modal__backdrop"
              onClick={(event) => {
                if (event.target === event.currentTarget) {
                  closeAddComplimentaryModal();
                }
              }}
              role="presentation"
            >
              <div
                className="qrb-comp-create-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="qrb-comp-create-modal-title"
              >
                <form
                  className="qrb-comp-create-modal__form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    createNewComplimentaryItem();
                  }}
                >
                  <div className="qrb-comp-create-modal__header">
                    <h3 id="qrb-comp-create-modal-title" className="qrb-comp-create-modal__title">
                      Create New Item
                    </h3>
                    <button
                      type="button"
                      className="qrb-comp-create-modal__close"
                      onClick={closeAddComplimentaryModal}
                      aria-label="Close create recipe modal"
                    >
                      ×
                    </button>
                  </div>

                  <div className="qrb-comp-create-modal__body">
                    <label htmlFor="qrb-comp-create-modal-input" className="qrb-comp-create-modal__label">
                      Item Name
                    </label>
                    <input
                      id="qrb-comp-create-modal-input"
                      type="text"
                      value={newComplimentaryForm.name}
                      onChange={(e) => setNewComplimentaryForm({ ...newComplimentaryForm, name: e.target.value })}
                      placeholder="Type item name"
                      className="qrb-comp-create-modal__input"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                    />
                  </div>

                  <div className="qrb-comp-create-modal__actions">
                    <button
                      type="button"
                      onClick={closeAddComplimentaryModal}
                      className="qrb-comp-create-modal__button qrb-comp-create-modal__button--cancel"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="qrb-comp-create-modal__button qrb-comp-create-modal__button--confirm"
                      disabled={!newComplimentaryForm.name.trim()}
                    >
                      Create Item
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Unsaved Changes Modal */}
          {showUnsavedChangesModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '0.5rem',
                maxWidth: '450px',
                width: '90%',
                padding: '28px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <h3 style={{
                    margin: '0 0 12px 0',
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#111'
                  }}>Leave Without Saving?</h3>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    color: '#666',
                    lineHeight: '1.5'
                  }}>
                    You have unsaved changes. If you leave now, all ingredient edits on <strong style={{ color: '#000' }}>"{formatSummaryTitle(selectedRecipe?.name || 'This Recipe')}"</strong> will be lost.
                  </p>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => setShowUnsavedChangesModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#e5e7eb',
                      color: '#000',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#d1d5db'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={discardUnsavedChangesAndExitEditor}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1f1f1f'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#000000'}
                  >
                    Leave
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Save Confirmation Modal */}
          {showSaveConfirmationModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                maxWidth: '500px',
                width: '90%',
                padding: '24px',
                height: 'auto',
                maxHeight: '50%',
                overflowY: 'auto'
              }}>
                <h3 style={{ 
                  margin: '0 0 20px 0', 
                  fontSize: '22px', 
                  fontWeight: '700',
                  color: '#111',
                  textAlign: 'center'
                }}>Item Summary</h3>

                {/* Ingredients Count with Dropdown */}
                <div style={{
                  background: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  position: 'relative'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setSummaryOpenDropdown((prev) => prev === 'ingredients' ? null : 'ingredients');
                  }}>
                    <span style={{ fontWeight: '600', color: '#000000', fontSize: '16px' }}>
                      Number of Ingredients
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        color: 'black',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: '700',
                        fontSize: '1rem'
                      }}>
                        {summaryIngredientItems.length}
                      </span>
                      <FaChevronDown style={{
                        fontSize: '12px',
                        color: '#000',
                        transform: summaryOpenDropdown === 'ingredients' ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.22s ease'
                      }} />
                    </div>
                  </div>
                  
                  {/* Ingredients Dropdown */}
                  <div id="ingredients-dropdown-standard" style={{
                    display: 'block',
                    position: 'relative',
                    marginTop: summaryOpenDropdown === 'ingredients' ? '8px' : '0px',
                    background: '#ffffff',
                    border: summaryOpenDropdown === 'ingredients' ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '8px',
                    width: '100%',
                    maxHeight: summaryOpenDropdown === 'ingredients' ? '14rem' : '0px',
                    overflowY: summaryOpenDropdown === 'ingredients' ? 'auto' : 'hidden',
                    overflowX: 'hidden',
                    opacity: summaryOpenDropdown === 'ingredients' ? 1 : 0,
                    boxShadow: summaryOpenDropdown === 'ingredients' ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                    transition: 'max-height 0.28s ease, opacity 0.2s ease, margin-top 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
                    pointerEvents: summaryOpenDropdown === 'ingredients' ? 'auto' : 'none'
                  }}>
                    {/* Dropdown Header */}
                    <div style={{
                      padding: '12px 16px',
                      borderBottom: '2px solid #e5e7eb',
                      background: '#f9fafb',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#000',
                      textAlign: 'left'
                    }}>
                      Ingredients Per {plateStep} Plate{Number(plateStep) === 1 ? '' : 's'}
                    </div>
                    
                    {summaryIngredientItems
                      .map((ingredient, index) => {
                        // Calculate per-plate quantity from 10 and 100 plate values
                        const qty10 = ingredient.qty10 || 0;
                        const qty100 = ingredient.qty100 || 0;
                        const rawPerPlate = qty10 > 0 && qty100 > 0 ? (qty100 - qty10) / 90 : qty10 / 10;
                        const step = Number(plateStep) > 0 ? Number(plateStep) : 1;
                        const rawScaled = rawPerPlate * step;
                        // SP9 sacred numbers: show the stored unit, never a rescaled one.
                        const perPlate = Number(rawScaled.toFixed(2));
                        const displayUnit = ingredient.unit || 'g';
                        
                        return (
                          <div
                            key={index}
                            style={{
                              padding: '10px 16px',
                              borderBottom: index < summaryIngredientItems.length - 1 ? '1px solid #e5e7eb' : 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '12px'
                            }}
                          >
                            <span style={{ color: '#000', fontSize: '14px', fontWeight: '500', flex: 1, textAlign: 'left' }}>
                              {ingredient.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                            </span>
                            <span style={{
                              color: '#000',
                              fontSize: '14px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap'
                            }}>
                              {perPlate} {displayUnit}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Saved Preparations */}
                <div style={{
                  background: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  position: 'relative'
                }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setSummaryOpenDropdown((prev) => prev === 'savedPreparations' ? null : 'savedPreparations');
                    }}
                  >
                    <span style={{ fontWeight: '600', color: '#000000', fontSize: '16px', textAlign: 'left' }}>
                      Saved Preparations
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        color: 'black',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: '700',
                        fontSize: '1rem'
                      }}>
                        {summarySavedPreparationItems.length}
                      </span>
                      <FaChevronDown style={{
                        fontSize: '12px',
                        color: '#000',
                        transform: summaryOpenDropdown === 'savedPreparations' ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.22s ease'
                      }} />
                    </div>
                  </div>

                  <div style={{
                    display: 'block',
                    position: 'relative',
                    marginTop: summaryOpenDropdown === 'savedPreparations' ? '8px' : '0px',
                    background: '#ffffff',
                    border: summaryOpenDropdown === 'savedPreparations' ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '8px',
                    width: '100%',
                    maxHeight: summaryOpenDropdown === 'savedPreparations' ? '16rem' : '0px',
                    overflowY: summaryOpenDropdown === 'savedPreparations' ? 'auto' : 'hidden',
                    overflowX: 'hidden',
                    opacity: summaryOpenDropdown === 'savedPreparations' ? 1 : 0,
                    boxShadow: summaryOpenDropdown === 'savedPreparations' ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                    transition: 'max-height 0.28s ease, opacity 0.2s ease, margin-top 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
                    pointerEvents: summaryOpenDropdown === 'savedPreparations' ? 'auto' : 'none'
                  }}>
                    <div style={{
                      padding: '12px 16px',
                      borderBottom: '2px solid #e5e7eb',
                      background: '#f9fafb',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#000',
                      textAlign: 'left'
                    }}>
                      Saved Preparation Links
                    </div>
                    {summarySavedPreparationItems.length > 0 ? (
                      summarySavedPreparationItems.map((prepItem, index) => {
                        const prepMeta = savedPreparationByName.get(normalizeSummaryKey(prepItem?.name));
                        const ingredientRows = getScaledPreparationIngredientRows(prepItem, prepMeta);
                        const componentCount = ingredientRows.length;
                        const qty10 = prepItem.qty10 || 0;
                        const qty100 = prepItem.qty100 || 0;
                        const rawPerPlate = qty10 > 0 && qty100 > 0 ? (qty100 - qty10) / 90 : qty10 / 10;
                        const step = Number(plateStep) > 0 ? Number(plateStep) : 1;
                        const rawScaled = rawPerPlate * step;
                        // SP9 sacred numbers: show the stored unit, never a rescaled one.
                        const perPlate = Number(rawScaled.toFixed(2));
                        const perPlateLabel = `${formatCompactQtyUnit(perPlate, prepItem.unit || 'g')} per ${step} plate${step === 1 ? '' : 's'}`;

                        return (
                          <div
                            key={`${prepItem.name}-${index}`}
                            style={{
                              padding: '10px 16px',
                              borderBottom: index < summarySavedPreparationItems.length - 1 ? '1px solid #e5e7eb' : 'none',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                              <span style={{ color: '#000', fontSize: '14px', fontWeight: '600', flex: 1, textAlign: 'left' }}>
                                {formatSummaryTitle(prepItem.name)}
                              </span>
                              <span style={{ color: '#374151', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                {perPlateLabel}
                              </span>
                            </div>
                            <div style={{
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              overflow: 'hidden',
                              background: '#ffffff'
                            }}>
                              {ingredientRows.length > 0 ? (
                                ingredientRows.map((ing, ingIndex) => {
                                  const ingName = String(ing?.itemName || '').trim();
                                  // formatQuantity (not formatSummaryNumber) — it canonicalizes
                                  // full unit names ("Kilogram") and auto-scales kg->g, so a
                                  // per-plate 0.0025 kg reads "2.5 g" instead of "0".
                                  const qtyLabel = formatQuantity(ing?.quantity, ing?.unit);

                                  return (
                                    <div
                                      key={`${prepItem.name}-${index}-${ingIndex}-${ingName}`}
                                      style={{
                                        padding: '8px 12px',
                                        borderBottom: ingIndex < ingredientRows.length - 1 ? '1px solid #e5e7eb' : 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '8px'
                                      }}
                                    >
                                      <span style={{ color: '#111827', fontSize: '12px', fontWeight: '500', flex: 1, textAlign: 'left' }}>
                                        {formatSummaryTitle(ingName || 'Ingredient')}
                                      </span>
                                      <span style={{ color: '#111827', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                        {qtyLabel}
                                      </span>
                                    </div>
                                  );
                                })
                              ) : (
                                <div style={{ padding: '8px 12px', color: '#6b7280', fontSize: '12px' }}>
                                  No ingredients mapped
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '10px 16px', color: '#9ca3af', fontSize: '14px' }}>No Saved Preparations</div>
                    )}
                  </div>
                </div>

                {/* Complimentary Items */}
                <div style={{
                  background: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  position: 'relative'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setSummaryOpenDropdown((prev) => prev === 'complimentary' ? null : 'complimentary');
                  }}>
                    <span style={{ fontWeight: '600', color: '#000000', fontSize: '16px', textAlign:'left' }}>
                      Complimentary Items
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        color: 'black',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: '700',
                        fontSize: '1rem'
                      }}>
                        {summaryComplimentaryItems.length}
                      </span>
                      <FaChevronDown style={{
                        fontSize: '12px',
                        color: '#000',
                        transform: summaryOpenDropdown === 'complimentary' ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.22s ease'
                      }} />
                    </div>
                  </div>

                  {/* Complimentary Items Dropdown */}
                  <div id="complimentary-dropdown-standard" style={{
                    display: 'block',
                    position: 'relative',
                    marginTop: summaryOpenDropdown === 'complimentary' ? '8px' : '0px',
                    background: '#ffffff',
                    border: summaryOpenDropdown === 'complimentary' ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '8px',
                    width: '100%',
                    maxHeight: summaryOpenDropdown === 'complimentary' ? '16rem' : '0px',
                    overflowY: summaryOpenDropdown === 'complimentary' ? 'auto' : 'hidden',
                    overflowX: 'hidden',
                    opacity: summaryOpenDropdown === 'complimentary' ? 1 : 0,
                    boxShadow: summaryOpenDropdown === 'complimentary' ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                    transition: 'max-height 0.28s ease, opacity 0.2s ease, margin-top 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
                    pointerEvents: summaryOpenDropdown === 'complimentary' ? 'auto' : 'none'
                  }}>
                    <div style={{
                      padding: '12px 16px',
                      borderBottom: '2px solid #e5e7eb',
                      background: '#f9fafb',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#000',
                      textAlign: 'left'
                    }}>
                      Complimentary Links
                    </div>
                    {summaryComplimentaryItems.length > 0 ? (
                      summaryComplimentaryItems.map((item, index) => {
                        const itemName = item.name;
                        const meta = getComplimentarySummaryMeta(item);
                        const detailLabel = `${meta.ingredientCount} ingredients`;
                        const ingredientRows = Array.isArray(item?.ingredients) ? item.ingredients : [];
                        return (
                          <div
                            key={index}
                            style={{
                              padding: '10px 16px',
                              borderBottom: index < summaryComplimentaryItems.length - 1 ? '1px solid #e5e7eb' : 'none',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              gap: '4px',
                              color: '#000',
                              fontSize: '14px',
                              textAlign: 'left'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                              <span style={{ color: '#000', fontSize: '14px', fontWeight: '600', flex: 1 }}>
                                {formatSummaryTitle(itemName)}
                              </span>
                              <span style={{ color: '#111827', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                {detailLabel}
                              </span>
                            </div>
                            <div style={{
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              overflow: 'hidden',
                              background: '#ffffff'
                            }}>
                              {ingredientRows.length > 0 ? (
                                ingredientRows.map((ing, ingIndex) => {
                                  const ingName = String(ing?.item_name || '').trim();
                                  const qty = formatSummaryNumber(ing?.quantity);
                                  const unit = String(ing?.unit_symbol || '').trim();
                                  const qtyLabel = `${qty}${unit ? ` ${unit}` : ''}`;

                                  return (
                                    <div
                                      key={`${index}-${ingIndex}-${ingName}`}
                                      style={{
                                        padding: '8px 12px',
                                        borderBottom: ingIndex < ingredientRows.length - 1 ? '1px solid #e5e7eb' : 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '8px'
                                      }}
                                    >
                                      <span style={{ color: '#111827', fontSize: '12px', fontWeight: '500', flex: 1 }}>
                                        {formatSummaryTitle(ingName || 'Ingredient')}
                                      </span>
                                      <span style={{ color: '#111827', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                        {qtyLabel}
                                      </span>
                                    </div>
                                  );
                                })
                              ) : (
                                <div style={{ padding: '8px 12px', color: '#6b7280', fontSize: '12px' }}>
                                  No ingredients mapped
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '10px 16px', color: '#9ca3af', fontSize: '14px' }}>No Complimentary Items Linked</div>
                    )}
                  </div>
                </div>

                {/* Customizations */}
                <div style={{
                  background: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px',
                  position: 'relative'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    const dropdown = document.getElementById('customizations-dropdown-standard');
                    if (dropdown) {
                      dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
                    }
                  }}>
                    <span style={{ fontWeight: '600', color: '#000000', fontSize: '16px' }}>
                      Customizations Selected
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        color: 'black',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: '700',
                        fontSize: '1rem'
                      }}>
                        {selectedCustomizations.length}
                      </span>
                      <FaChevronDown style={{ fontSize: '12px', color: '#000' }} />
                    </div>
                  </div>

                  {/* Customizations Dropdown */}
                  <div id="customizations-dropdown-standard" style={{
                    display: 'none',
                    position: 'absolute',
                    bottom: '100%',
                    right: 0,
                    marginBottom: '8px',
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    width: '11rem',
                    maxHeight: '16rem',
                    overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000
                  }}>
                    {selectedCustomizations.length > 0 ? (
                      selectedCustomizations.map((customization, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '10px 16px',
                            borderBottom: index < selectedCustomizations.length - 1 ? '1px solid #e5e7eb' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            color: '#000',
                            fontSize: '14px',
                            fontWeight: '500',
                            textAlign: 'left'
                          }}
                        >
                          {customization.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '10px 16px', color: '#9ca3af', fontSize: '14px' }}>No Customizations Selected</div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => {
                      setSummaryOpenDropdown(null);
                      setShowSaveConfirmationModal(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#EBEBEB',
                      color: '#000',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      height: '44px',
                      minHeight: '44px',
                      maxHeight: '44px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmClick}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      height: '44px',
                      minHeight: '44px',
                      maxHeight: '44px'
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          {showVerifyModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2100
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '16px',
                maxWidth: '400px',
                width: '90%',
                padding: '24px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                position: 'relative'
              }}>
                <button
                  onClick={() => setShowVerifyModal(false)}
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px'
                  }}
                >
                  <FaTimes />
                </button>

                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1e293b',
                  textAlign: 'left',
                  paddingRight: '24px'
                }}>
                  Please verify
                </h3>

                <p style={{
                  margin: '0 0 24px 0',
                  fontSize: '14px',
                  color: '#64748b',
                  lineHeight: '1.5',
                  textAlign: 'left'
                }}>
                  {verifyModalMessage}
                </p>

                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => setShowVerifyModal(false)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      background: '#f1f5f9',
                      color: '#1e293b',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      height: '40px'
                    }}
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      setShowVerifyModal(false);
                      confirmAndSave();
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      background: '#0b0f19',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      height: '40px'
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Import Ingredients Confirmation Modal */}
          {showImportConfirmModal && selectedMenuItemForImport && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '0.5rem',
                maxWidth: '450px',
                width: '90%',
                padding: '28px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ 
                    margin: '0 0 12px 0', 
                    fontSize: '22px', 
                    fontWeight: '700',
                    color: '#111'
                  }}>Import Ingredients?</h3>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    color: '#666',
                    lineHeight: '1.5'
                  }}>
                    Do you want to import all ingredients from <strong style={{ color: '#000' }}>"{selectedMenuItemForImport.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}"</strong>?
                  </p>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => {
                      setShowImportConfirmModal(false);
                      setSelectedMenuItemForImport(null);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#e5e7eb',
                      color: '#000',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#d1d5db'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmImportIngredients}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1f1f1f'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#000000'}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete All Ingredients Confirmation Modal */}
          {showDeleteAllModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '0.5rem',
                maxWidth: '450px',
                width: '90%',
                padding: '28px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ 
                    margin: '0 0 12px 0', 
                    fontSize: '22px', 
                    fontWeight: '700',
                    color: '#111'
                  }}>Delete All Ingredients?</h3>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    color: '#666',
                    lineHeight: '1.5'
                  }}>
                    This will remove all <strong style={{ color: '#000' }}>{ingredientList.length}</strong> ingredient(s) from the current recipe. This action can be undone.
                  </p>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => setShowDeleteAllModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#e5e7eb',
                      color: '#000',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#d1d5db'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  >
                    No
                  </button>
                  <button
                    onClick={confirmDeleteAllIngredients}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1f1f1f'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#000000'}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add New Customization Modal */}
          {showAddCustomizationModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div className="itemmap-custom-option-modal" style={{
                background: '#ffffff',
                borderRadius: '12px',
                maxWidth: '600px',
                width: '100%',
                padding: '24px'
              }}>
                <div className="itemmap-custom-option-header" style={{ marginBottom: '20px', textAlign: 'left' }}>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', textAlign: 'left', fontFamily: 'Roboto, sans-serif' }}>Add Custom Option</h3>
                </div>

                <div className="itemmap-custom-option-input-container" style={{ 
                  maxWidth: '100%', 
                  width: '100%', 
                  margin: '0 auto 16px auto', 
                  position: 'relative', 
                  textAlign: 'left'
                }}>
                  <label className="itemmap-custom-option-label" style={{ 
                    display: 'block',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#000',
                    marginBottom: '12px',
                    fontFamily: 'Roboto, sans-serif'
                  }}>Customization</label>
                  <input
                    type="text"
                    value={newCustomizationName}
                    onChange={(e) => setNewCustomizationName(e.target.value)}
                    placeholder="e.g., Extra Spicy, No Ice, etc."
                    className="itemmap-custom-option-input"
                    style={{ 
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '15px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      outline: 'none',
                      background: '#ffffff',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s ease',
                      fontFamily: 'Roboto, sans-serif'
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        createNewCustomization();
                      }
                    }}
                  />
                </div>

                {/* Bottom Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '20px'
                }}>
                  <button
                    onClick={() => setShowAddCustomizationModal(false)}
                    className="btn btn--secondary"
                    style={{
                      flex: 1,
                      padding: '14px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      fontFamily: 'Roboto, sans-serif'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createNewCustomization}
                    className="btn btn--primary"
                    style={{
                      flex: 1,
                      padding: '14px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      fontFamily: 'Roboto, sans-serif'
                    }}
                  >
                    Add Customization
                  </button>
                </div>
              </div>
            </div>
          )}

          {complimentaryLinkModal.visible && complimentaryLinkModal.item && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2001,
                padding: '20px'
              }}
              onClick={() => {
                if (!complimentaryLinkModal.isSubmitting) {
                  closeComplimentaryLinkModal();
                }
              }}
            >
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '0.5rem',
                  maxWidth: '460px',
                  width: '100%',
                  padding: '28px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                }}
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <h3 style={{
                    margin: '0 0 12px 0',
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#111'
                  }}>
                    {complimentaryLinkModal.currentlyLinked ? 'Remove Complimentary Item?' : 'Add Complimentary Item?'}
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    color: '#666',
                    lineHeight: '1.6'
                  }}>
                    {complimentaryLinkModal.currentlyLinked ? (
                      <>
                        <strong style={{ color: '#000' }}>&quot;{getComplimentaryItemName(complimentaryLinkModal.item)}&quot;</strong> will be removed from <strong style={{ color: '#000' }}>&quot;{selectedRecipe?.name || 'this recipe'}&quot;</strong> and will no longer appear as a complimentary item for this dish.
                      </>
                    ) : (
                      <>
                        <strong style={{ color: '#000' }}>&quot;{getComplimentaryItemName(complimentaryLinkModal.item)}&quot;</strong> will be added to <strong style={{ color: '#000' }}>&quot;{selectedRecipe?.name || 'this recipe'}&quot;</strong> and will be served as a complimentary item for this dish.
                      </>
                    )}
                  </p>
                  {complimentaryLinkModal.error && (
                    <p style={{
                      margin: '14px 0 0 0',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#dc2626',
                      lineHeight: '1.5'
                    }}>
                      {complimentaryLinkModal.error}
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={closeComplimentaryLinkModal}
                    disabled={complimentaryLinkModal.isSubmitting}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#e5e7eb',
                      color: '#000',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: complimentaryLinkModal.isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: complimentaryLinkModal.isSubmitting ? 0.7 : 1,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!complimentaryLinkModal.isSubmitting) e.currentTarget.style.background = '#d1d5db';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#e5e7eb';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmComplimentaryLinkChange}
                    disabled={complimentaryLinkModal.isSubmitting}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#000000',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: complimentaryLinkModal.isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: complimentaryLinkModal.isSubmitting ? 0.7 : 1,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!complimentaryLinkModal.isSubmitting) {
                        e.currentTarget.style.background = '#1f1f1f';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#000000';
                    }}
                  >
                    {complimentaryLinkModal.isSubmitting
                      ? (complimentaryLinkModal.currentlyLinked ? 'Removing...' : 'Adding...')
                      : (complimentaryLinkModal.currentlyLinked ? 'Remove Item' : 'Add Item')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {toastModal.visible && (
            <div className="qrb-toast-modal-overlay" onClick={closeToast} role="presentation">
              <div
                className="qrb-toast-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <div className="qrb-toast-modal-message">{toastModal.message}</div>
              </div>
            </div>
          )}
          {savingIngredients && (
            <div className="saving-overlay">
              <div className="saving-card">
                <div style={{ 
                  width: '50px', 
                  height: '50px', 
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 24px'
                }}></div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                <div className="saving-title">Saving Ingredient List</div>
                <div className="saving-subtitle">Please wait while we save your item ingredients...</div>
              </div>
            </div>
          )}
        </div>
        </div>
        )}

          {showOCRModal && (
            <div className="modal-overlay">
              <div className="modal-card modal-card--wide">
                <div className="modal-header">
                  <h3 style={{ margin: 0,color:"#000" }}>Scan Receipt for Ingredients</h3>
                  </div>
                {!ocrProcessing && ocrResults.length === 0 && (
                  <div>
                    {/* Selected menu images display */}
                    {selectedMenuImages.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                          <span style={{ fontWeight: 'bold' }}>
                            Selected Images ({selectedMenuImages.length})
                          </span>
                          <button 
                            onClick={clearAllMenuImages} 
                            className="btn btn--secondary"
                            style={{ padding: '4px 12px', fontSize: 12 }}
                          >
                            Clear All
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                          {selectedMenuImages.map((file, idx) => (
                            <div 
                              key={idx}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 12px',
                                backgroundColor: '#f0f0f0',
                                borderRadius: 16,
                                fontSize: 13,
                                gap: 8
                              }}
                            >
                              <span>📄 {file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name}</span>
                              <button 
                                onClick={() => removeMenuImage(idx)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#d32f2f',
                                  cursor: 'pointer',
                                  padding: 0,
                                  fontSize: 14,
                                  fontWeight: 'bold'
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                        <button 
                          onClick={processAllMenuImages}
                          className="btn btn--primary"
                          style={{ width: '100%', marginBottom: 12 }}
                        >
                          🔍 Scan {selectedMenuImages.length} Menu Image{selectedMenuImages.length > 1 ? 's' : ''}
                        </button>
                      </div>
                    )}
                    
                    <label htmlFor="ocr-upload" className="dropzone">
                      <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                        {selectedMenuImages.length > 0 ? 'Add More Menu Images' : 'Upload Menu Image'}
                      </div>
                      <div style={{ fontSize: 14, color: '#666' }}>JPG, PNG, or PDF files supported (multiple selection enabled)</div>
                    </label>
                    <input id="ocr-upload" type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }} onChange={handleOCRUpload} />
                  </div>
                )}
                {ocrProcessing && (
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #000',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                    </div>
                    <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                      {menuProcessingProgress.total > 0 
                        ? `Processing Menu ${menuProcessingProgress.current} of ${menuProcessingProgress.total}...`
                        : 'Processing Menu...'
                      }
                    </div>
                    {menuScanProgress > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ 
                          width: '100%', 
                          height: 8, 
                          backgroundColor: '#e0e0e0', 
                          borderRadius: 4, 
                          overflow: 'hidden',
                          marginBottom: 8
                        }}>
                          <div style={{ 
                            width: `${menuScanProgress}%`, 
                            height: '100%', 
                            backgroundColor: '#4caf50',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                        <div style={{ fontSize: 14, color: '#666' }}>{menuScanProgress}% complete</div>
                      </div>
                    )}
                  </div>
                )}
                {ocrResults.length > 0 && (
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: 16 }}> Found {ocrResults.length} ingredients:</div>
                    <div style={{ marginBottom: 16 }}>
                      {ocrResults.map((item, idx) => (
                        <div key={idx} className="modal-list-item">
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{item.name}</div>
                            <div style={{ fontSize: 12, color: '#666' }}>{item.quantity} {item.unit} • {item.category}</div>
                          </div>
                          <FontAwesomeIcon icon={faCheck} style={{ fontSize: 12, color: '#28a745' }} />
                        </div>
                      ))}
                    </div>
                    <div className="modal-actions">
                      <button onClick={() => { setShowOCRModal(false); setOcrResults([]); }} className="btn btn--secondary" style={{ flex: 1 }}>Cancel</button>
                      <button onClick={addOCRIngredientsToRecipe} className="btn btn--primary" style={{ flex: 2 }}> Add All Ingredients</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

      </>
    );
  }

  if (loading) {
    return (
      <div className="status-container">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
          <div style={{ position: 'relative', width: '95px', height: '95px' }}>
            <img 
              src="/invexis-logo.png"
              alt="Invexis"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                backgroundColor: '#fff',
                padding: '0px',
                pointerEvents: 'none',
                objectFit: 'contain',
                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                zIndex: 1
              }}
            />
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '70px',
              height: '70px',
              border: '6px solid #000000',
              borderTop: '6px solid #f9c806',
              borderRight: '6px solid #f9c806',
              borderRadius: '50%',
              animation: 'qrItemMapSpin 1s linear infinite',
              zIndex: 16
            }}></div>
          </div>
          <div style={{ fontSize: 16, color: '#666', fontWeight: '500', margin: 0 }}>Loading items...</div>
        </div>
        <style>{`
          @keyframes qrItemMapSpin {
            0% { transform: translate(-50%, -50%) rotate(0deg); }
            100% { transform: translate(-50%, -50%) rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="status-container">
        <div>
          <div style={{ fontSize: 18, marginBottom: 10, color: '#d32f2f' }}>Error</div>
          <div style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>{error}</div>
          <button onClick={() => window.location.reload()} style={{ padding: '10px 20px', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Retry</button>
        </div>
      </div>
    );
  }

  if (showNewRecipeForm) {
    return (
      <>
        <div className="newrecipe-container" style={{ 
          maxWidth: '600px', 
          margin: '0 auto', 
          paddingBottom: '80px', 
          minHeight: '100vh',
          height: '100vh',
          background: '#ffffff',
          position: 'relative',
          overflowY: 'auto'
        }}>
        <div className="itemmap-header" style={{ marginBottom: '16px' }}>
          <button
            type="button"
            onClick={() => { setShowNewRecipeForm(false); setRecipeCards([{ id: Date.now(), name: '', price: '', servings: '' }]); setCapturedImage(null); }}
            className="back-arrow-btn"
            aria-label="Back to Item Mapping"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h3 className="page-title" style={{ marginTop: '0.2rem' }}>Create New Item</h3>
        </div>
        {capturedImage && (
          <div className="qrb-recipe-hero-shell qrb-recipe-hero-shell--padded">
            <img src={capturedImage} alt="Captured menu" className="recipe-hero" />
          </div>
        )}

        <div style={{ padding: '0 16px', marginBottom: '5rem' }}>
          {/* Recipe Cards */}
          {recipeCards.map((card, index) => (
            <div key={card.id} className="card itemmap-create-recipe-card" style={{ maxWidth: '111%', width: 'calc(100% + 20px)', margin: '0 -10px 16px -10px', position: 'relative', textAlign: 'left', padding:'0px 16px 0px 16px' }}>
              <div className="itemmap-create-recipe-card-topbar">
                <label className="form-label itemmap-recipe-label itemmap-create-recipe-heading" style={{ color: '#000', fontFamily: 'Roboto, sans-serif', fontWeight: '600', fontSize: '16px', marginBottom: '0', display: 'block' }}>Item Name</label>
                {recipeCards.length > 1 && (
                  <button
                    onClick={() => removeRecipeCard(card.id)}
                    className="itemmap-create-recipe-remove-btn"
                    aria-label="Remove recipe card"
                  >
                    <FontAwesomeIcon icon={faTimes} className="itemmap-create-recipe-remove-icon" />
                  </button>
                )}
              </div>

              <div className="form-row itemmap-recipe-name-row">
                <input
                  type="text"
                  value={card.name}
                  onChange={(e) => updateRecipeCard(card.id, 'name', e.target.value)}
                  placeholder="Enter item name"
                  className="boxed-input itemmap-recipe-name-input itemmap-create-recipe-input"
                  style={{ 
                    width: '100%', 
                    fontWeight:'600', 
                    fontSize: '14px',
                    textAlign: 'left',
                    fontFamily: 'Roboto, sans-serif',
                    borderColor: creationErrors[card.id]?.name ? '#ef4444' : '',
                    backgroundColor: creationErrors[card.id]?.name ? '#fef2f2' : ''
                  }}
                />
                {creationErrors[card.id]?.name && (
                  <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '-2px', lineHeight: '1.25', display: 'block', fontWeight: '500', textAlign: 'left' }}>
                    Item Name is required
                  </span>
                )}
              </div>
              <div className="form-grid itemmap-recipe-details-grid">
                <div className="form-col itemmap-price-col">
                  <label className="form-label itemmap-recipe-label" style={{ color: '#000', fontFamily: 'Roboto, sans-serif', fontWeight: '600', fontSize: '16px', marginBottom: '8px', display: 'block' }}>Price (₹)</label>
                  <input
                    type="text"
                    value={card.price}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d*\.?\d*$/.test(val)) {
                        updateRecipeCard(card.id, 'price', val);
                      }
                    }}
                    className="boxed-input itemmap-recipe-price-input itemmap-create-recipe-input"
                    placeholder="Enter price"
                    style={{ 
                      width: '100%', 
                      fontWeight:'600', 
                      fontSize: '14px',
                      textAlign: 'left',
                      fontFamily: 'Roboto, sans-serif',
                      borderColor: creationErrors[card.id]?.price ? '#ef4444' : '',
                      backgroundColor: creationErrors[card.id]?.price ? '#fef2f2' : ''
                    }}
                  />
                  {creationErrors[card.id]?.price && (
                    <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '4px', lineHeight: '1.25', display: 'block', fontWeight: '500', textAlign: 'left' }}>
                      Price is required
                    </span>
                  )}
                </div>
                <div className="form-col itemmap-servings-col">
                  <label className="form-label itemmap-recipe-label" style={{ color: '#000', fontFamily: 'Roboto, sans-serif', fontWeight: '600', fontSize: '16px', marginBottom: '8px', display: 'block' }}>Servings</label>
                  <input
                    type="text"
                    value={card.servings}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d+$/.test(val)) {
                        updateRecipeCard(card.id, 'servings', val);
                      }
                    }}
                    className="boxed-input itemmap-recipe-servings-input itemmap-create-recipe-input"
                    placeholder="Enter servings"
                    style={{ 
                      width: '100%', 
                      fontWeight:'600', 
                      fontSize: '14px',
                      textAlign: 'left',
                      fontFamily: 'Roboto, sans-serif',
                      borderColor: creationErrors[card.id]?.servings ? '#ef4444' : '',
                      backgroundColor: creationErrors[card.id]?.servings ? '#fef2f2' : ''
                    }}
                  />
                  {creationErrors[card.id]?.servings && (
                    <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '4px', lineHeight: '1.25', display: 'block', fontWeight: '500', textAlign: 'left' }}>
                      Servings is required
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Add More Recipe Button below cards */}
          <div style={{ marginBottom: '16px', width: 'calc(100% + 20px)', margin: '0 -10px 16px -10px', maxWidth: '111%' }}>
            <button
              onClick={addMoreRecipeCard}
              className="btn btn--primary"
              style={{
                width: '100%',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                background: '#000000',
                border: 'none',
                fontSize: '14px',
                fontFamily: 'Roboto, sans-serif'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Add More Items
            </button>
          </div>

          {/* Bottom Action Button */}
          <div style={{
            width: 'calc(100% + 20px)',
            margin: '0 -10px 0 -10px',
            maxWidth: '111%',
            padding: '0'
          }}>
            <button
              onClick={createNewRecipes}
              className="btn btn--primary"
              style={{
                width: '100%',
                padding: '16px 24px',
                fontSize: '14px',
                fontWeight: '600',
                background:'#16A34A',
                fontFamily: 'Roboto, sans-serif'
              }}
            >
              Create Item
            </button>
          </div>
        </div>
        </div>

        {/* Quick Add FAB Button - NC Items Style */}
        <button 
          className="qrb-quick-add-fab" 
          onClick={() => setShowQuickAddModal(true)}
          title="Quick Add Menu Item"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>

        {/* Quick Add Modal - NC Items Style */}
        {showQuickAddModal && (
          <div className="nc-modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowQuickAddModal(false);
              setQuickAddItemName('');
              setQuickAddError('');
            }
          }}>
            <div className="nc-modal-content">
              <button 
                className="nc-modal-close" 
                onClick={() => {
                  setShowQuickAddModal(false);
                  setQuickAddItemName('');
                  setQuickAddError('');
                }}
                aria-label="Close"
              >
                ×
              </button>
              <h3 className="nc-modal-title">Quick Add Menu Item</h3>
              
              {quickAddError && (
                <div className="nc-modal-error">
                  {quickAddError}
                </div>
              )}
              
              <div className="nc-form-group">
                <label className="nc-form-label">Menu Item Name</label>
                <input
                  type="text"
                  className="nc-form-input"
                  value={quickAddItemName}
                  onChange={(e) => setQuickAddItemName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !isCreatingQuickItem) {
                      createQuickMenuItem();
                    }
                  }}
                  placeholder="Enter menu item name..."
                  autoFocus
                  disabled={isCreatingQuickItem}
                />
                <p style={{ 
                  fontSize: '13px', 
                  color: '#6b7280', 
                  marginTop: '8px',
                  marginBottom: '0'
                }}>
                  This will create an empty menu item. You can add ingredients manually after creation.
                </p>
              </div>
              
              <div className="nc-modal-actions">
                <button
                  className="nc-modal-btn nc-modal-btn-cancel"
                  onClick={() => {
                    setShowQuickAddModal(false);
                    setQuickAddItemName('');
                    setQuickAddError('');
                  }}
                  disabled={isCreatingQuickItem}
                >
                  Cancel
                </button>
                <button
                  className="nc-modal-btn nc-modal-btn-submit"
                  onClick={createQuickMenuItem}
                  disabled={isCreatingQuickItem}
                >
                  {isCreatingQuickItem ? 'Creating...' : 'Create Item'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recipe Creation Loading Overlay */}
        {isCreatingRecipes && (
          <div className="modal-overlay" style={{ zIndex: 10001 }}>
            <div className="creating-recipe-popup" style={{ 
              width: '420px',
              maxWidth: 'calc(100% - 40px)',
              height: '280px',
              textAlign: 'center', 
              padding: '30px', 
              borderRadius: '0.5rem',
              overflow: 'visible',
              boxSizing: 'border-box',
              background: 'white',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              margin: '0 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}>
              {/* Loading Spinner */}
              <div style={{ 
                width: '60px', 
                height: '60px', 
                position: 'relative',
                margin: '0 auto 20px auto'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  width: '60px',
                  height: '60px',
                  border: '6px solid transparent',
                  borderTop: '6px solid #000',
                  borderRight: '6px solid #000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
              </div>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
              
              <div style={{ fontWeight: 'bold', fontSize: '28px', marginBottom: '10px', color: '#000' }}>
                Creating Ingredients...
              </div>
              
              {recipeCreationProgress.message && (
                <div style={{ fontSize: '18px', color: '#666', marginBottom: '10px' }}>
                  {recipeCreationProgress.message}
                </div>
              )}
              {recipeCreationProgress.total > 0 && (
                <div style={{ width: '100%' }}>
                  <div style={{ 
                    fontSize: '16px', 
                    color: '#000', 
                    fontWeight: 600,
                    marginBottom: '10px'
                  }}>
                    {Math.round((recipeCreationProgress.current / recipeCreationProgress.total) * 100)}% complete
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '7px', 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: '4px', 
                    overflow: 'hidden',
                    marginBottom: '18px'
                  }}>
                    <div style={{ 
                      width: `${(recipeCreationProgress.current / recipeCreationProgress.total) * 100}%`, 
                      height: '100%', 
                      backgroundColor: '#000',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}
              
              {/* Cancel Button */}
              <button 
                onClick={() => setIsCreatingRecipes(false)}
                style={{
                  padding: '10px 24px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#666',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontWeight: 500
                }}
                onMouseOver={(e) => e.target.style.background = '#f5f5f5'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                Cancel
              </button>
              
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                  
                  @media (max-width: 600px) {
                    .creating-recipe-popup {
                      height: 320px !important;
                      padding: 25px !important;
                      justify-content: flex-start !important;
                    }
                  }
                `}
              </style>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="itemmap-container">
      <div className="itemmap-header">
        {cameFromTSR && (
          <button
            type="button"
            onClick={() => {
              goTo && goTo('todays-sales-report');
            }}
            className="back-arrow-btn"
            aria-label="Back to Today's Sales Report"
            title="Back to Sales Report"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        {cameFromWastage && (
          <button
            type="button"
            onClick={() => {
              goTo && goTo('stockOutUsage');
            }}
            className="back-arrow-btn"
            aria-label="Back to Wastage"
            title="Back to Wastage"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        {cameFromMenuManagement && (
          <button
            type="button"
            onClick={() => {
              goTo && goTo('qr-menu-management');
            }}
            className="back-arrow-btn"
            aria-label="Back to Menu Management"
            title="Back to Menu Management"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        {!cameFromTSR && !cameFromWastage && !cameFromMenuManagement && previousScreen !== 'qr-onboarding' && (
          <button
            type="button"
            onClick={() => goTo && goTo('dashboard')}
            className="back-arrow-btn"
            aria-label="Back to Dashboard"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        {!cameFromTSR && !cameFromWastage && !cameFromMenuManagement && previousScreen === 'qr-onboarding' && (
          <button
            type="button"
            onClick={() => goTo && goTo('map3')}
            className="back-arrow-btn"
            aria-label="Back to Setup Complete"
            title="Back to Setup Complete"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        <h3 className="page-title" style={{marginTop:'0.2rem'}}>Item Mapping</h3>
      </div>
      
      <div className="action-tiles">
        <div className="action-tile scan-full" style={{ padding: 0 }}>
          <button onClick={() => setShowMenuOptionsModal(true)} className="tile-btn">
            <div className="scan-card scan-card--filled">
              <div className="camera-icon-container">
                <div className="camera-icon" aria-hidden>
                  <FontAwesomeIcon icon={faCamera} className="scan-svg" />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className="scan-title">Scan Menu</div>
                <div className="scan-subtitle">Click here to upload a photo of your menu.</div>
              </div>
            </div>
          </button>
        </div>
      </div>
      {recipes.length === 0 && (
        <div style={{ textAlign: 'center', padding: '8rem 1rem 0 1rem', color: '#000', fontSize: '1rem', fontWeight: '500' }}>
          Get instant ingredient measurements for 10 plates by scanning your menu.
        </div>
      )}
      
      {recipes.length > 0 && (
        <div className="qrb-itemmap-sticky-filters">
          <div className="qrb-search-wrap">
            <StandardSearch
              value={search}
              onChange={setSearch}
              placeholder="Search ingredients..."
            />
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '8px 4px',
            marginBottom: '4px'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937', fontFamily: 'Roboto, sans-serif' }}>
              Filter by Master Categories
            </span>
            <label className="switch" style={{ 
              position: 'relative', 
              display: 'inline-block', 
              width: '42px', 
              height: '24px' 
            }}>
              <input 
                type="checkbox" 
                checked={useMasterCategories} 
                onChange={(e) => setUseMasterCategories(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span className="slider round" style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: useMasterCategories ? '#000000' : '#D1D5DB',
                transition: '0.3s',
                borderRadius: '24px'
              }}>
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '18px',
                  width: '18px',
                  left: '3px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  transition: '0.3s',
                  borderRadius: '50%',
                  transform: useMasterCategories ? 'translateX(18px)' : 'none',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }} />
              </span>
            </label>
          </div>

          <div className="qrb-category-chips-viewall-wrap">
            <div ref={categoryChipsRef} className="qrb-category-chips">
              {visibleCategoryOptions.map((c) => (
                <button
                  key={c}
                  data-visible-category-chip="true"
                  onClick={() => setFilter(c)}
                  className={`chip ${filter === c ? 'active' : ''}`}
                >
                  {c}
                </button>
              ))}
              {hasCategoryChipOverflow && (
                <button
                  type="button"
                  data-category-toggle="true"
                  onClick={() => setShowAllCategoryChips((prev) => !prev)}
                  className="chip qrb-chip-view-all"
                >
                  {showAllCategoryChips ? 'Show less' : 'View all'}
                </button>
              )}
            </div>
            <div
              ref={categoryMeasureRef}
              className="qrb-category-chips qrb-category-chips-measure"
              aria-hidden="true"
            >
              {categoryFilterOptions.map((c) => (
                <button
                  key={`measure-${c}`}
                  type="button"
                  data-measure-chip="true"
                  tabIndex={-1}
                  className="chip"
                >
                  {c}
                </button>
              ))}
              <button
                type="button"
                data-measure-view-all="true"
                tabIndex={-1}
                className="chip qrb-chip-view-all"
              >
                View all
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredRecipes.map((r) => {
        const isEditing = r.editing;
        const handleUpdateRecipeField = async (id, field, value) => {
          setRecipes((prev) => prev.map((rec) => rec.id === id ? { ...rec, [field]: value } : rec));
          if (field === 'price') {
            try {
              const recipe = recipes.find(r => r.id === id);
              const updatedData = { name: recipe.name, price: parseFloat(value) || 0, servings: parseFloat(recipe.servings) || 1 };
              await RecipesService.updateRecipe(id, updatedData);
            } catch (error) {
              console.error('Failed to auto-save price:', error);
            }
          }
        };
        const saveRecipeChanges = async (id) => {
          const recipe = recipes.find(r => r.id === id);
          if (recipe) {
            const editedName = recipe.name ? recipe.name.trim().toLowerCase() : '';
            const isDuplicate = recipes.some(r => r.id !== id && r.name.trim().toLowerCase() === editedName);
            if (isDuplicate) {
              setShowDuplicateModal(true);
              return;
            }
          }
          try {
            setSavingRecipeId(id);
            const recipe = recipes.find(r => r.id === id);
            const updatedData = { name: recipe.name, price: parseFloat(recipe.price) || 0, servings: parseFloat(recipe.servings) || 1 };
            const result = await RecipesService.updateRecipe(id, updatedData, UnitMappingService.getBusinessId());
            setRecipes((prev) => prev.map((rec) => rec.id === id ? { ...rec, ...result } : rec));
            
            //  Navigate back to TSR if we came from there
            const pendingTSRData = localStorage.getItem('pendingTSRData');
            const pendingWastageData = localStorage.getItem('pendingWastageData');
            const pendingMenuManagementData = localStorage.getItem('pendingMenuManagementData');
            
            if (pendingTSRData) {
              // Small delay to show success before navigating
              setTimeout(() => {
                goTo && goTo('todays-sales-report');
              }, 500);
            } else if (pendingWastageData) {
              setTimeout(() => {
                goTo && goTo('stockOutUsage');
              }, 500);
            } else if (pendingMenuManagementData) {
              
              // Mark the removed ingredients as resolved now that recipe is saved
              try {
                const data = JSON.parse(pendingMenuManagementData);
                if (data.pendingResolve && Array.isArray(data.pendingResolve)) {
                  
                  // Update the stored data to include newly resolved ingredients
                  const updatedResolved = [...(data.resolvedIngredients || []), ...data.pendingResolve];
                  data.resolvedIngredients = updatedResolved;
                  delete data.pendingResolve; // No longer pending
                  
                  // Update localStorage with the resolved ingredients
                  localStorage.setItem('pendingMenuManagementData', JSON.stringify(data));
                }
              } catch (err) {
                console.error('Error updating resolved ingredients:', err);
                showToast('Failed to update resolved ingredients');
              }

              setTimeout(() => {
                goTo && goTo('qr-menu-management');
              }, 500);
            }
          } catch (error) {
            console.error('Failed to update recipe:', error);
            showToast('Failed To Save Recipe Changes', 'error');
            try {
              const recipesData = await RecipesService.getRecipes(UnitMappingService.getBusinessId());
              setRecipes(applyLocalReviewDrafts(recipesData));
            } catch (reloadError) {
              console.error('Failed to reload recipes:', reloadError);
            }
          } finally {
            setSavingRecipeId(null);
          }
        };

        // No need for inline version - it will trigger the modal confirmation

        const toggleEditMode = (id, shouldRevert = false) => {
          setRecipes((prev) => prev.map((rec) => {
            if (rec.id === id) {
              const nextEditing = !rec.editing;
              if (nextEditing) {
                editingOriginalsRef.current[id] = {
                  name: rec.name,
                  price: rec.price,
                  servings: rec.servings,
                  category: rec.category,
                  master_category: rec.master_category,
                  status: rec.status
                };
                return { ...rec, editing: nextEditing };
              } else {
                const original = editingOriginalsRef.current[id];
                delete editingOriginalsRef.current[id];
                if (shouldRevert && original) {
                  return { ...rec, ...original, editing: nextEditing };
                }
                return { ...rec, editing: nextEditing };
              }
            }
            return rec;
          }));
        };
        return (
          <div key={r.id} className={`recipe-card${r.status === 'draft' ? ' recipe-card--review' : ''}${isEditing ? ' recipe-card--editing' : ''}`}>
            {!isEditing && (
              <FontAwesomeIcon
                icon={faPenToSquare}
                onClick={(e) => { e.stopPropagation(); toggleEditMode(r.id); }}
                className="edit-icon"
              />
            )}
            {isEditing ? (
              <div className="recipe-card-edit-container" style={{ textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                  <h4 style={{ textAlign: 'left', margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>Edit Item</h4>
                  <FontAwesomeIcon
                    icon={faPenToSquare}
                    onClick={(e) => { e.stopPropagation(); toggleEditMode(r.id, true); }}
                    style={{ fontSize: 16, cursor: 'pointer', color: '#000', flexShrink: 0 }}
                  />
                </div>
                <label className="form-label" style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: 7, marginTop: 7 }}>Item Name</label>
                <input
                  type="text"
                  value={r.name}
                  onChange={(e) => handleUpdateRecipeField(r.id, 'name', e.target.value)}
                  className="boxed-input itemmap-recipe-name-input"
                  style={{ fontSize: 16, marginBottom: 8 }}
                />
                <div style={{ display: 'flex', gap: '10px', marginBottom: 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label className="form-label" style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: 7 }}>Servings</label>
                    <input
                      type="number"
                      value={r.servings}
                      onChange={(e) => handleUpdateRecipeField(r.id, 'servings', parseInt(e.target.value))}
                      className="boxed-input itemmap-recipe-servings-input"
                      style={{ fontSize: 14, marginBottom: 0 }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label className="form-label" style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: 7 }}>Price (₹)</label>
                    <input
                      type="number"
                      value={r.price}
                      onChange={(e) => handleUpdateRecipeField(r.id, 'price', parseInt(e.target.value))}
                      className="boxed-input itemmap-recipe-price-input"
                      style={{ fontSize: 14, marginBottom: 0 }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: 16 }}>
                  <button
                    onClick={async (e) => { e.stopPropagation(); await deleteRecipe(r.id); }}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      background: '#EBEBEB',
                      color: '#000',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                  <button
                    onClick={async (e) => { e.stopPropagation(); await saveRecipeChanges(r.id); toggleEditMode(r.id); }}
                    disabled={savingRecipeId === r.id}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      background: savingRecipeId === r.id ? '#666' : '#000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: savingRecipeId === r.id ? 'not-allowed' : 'pointer',
                      opacity: savingRecipeId === r.id ? 0.7 : 1
                    }}
                  >
                    {savingRecipeId === r.id ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => {
                  setSelectedRecipe(withImageMeta(r));
                }}
              >
                <div className="card-title">{formatSummaryTitle(r.name)}</div>
                <div className="recipe-meta">
                  {r.servings} servings ・ {r.ingredientscount || r.ingredientsCount || 0} ingredients
                </div>
                {isPendingAddedFromSales(r) && (
                  <div className="recipe-added-from-sales">{addedFromSalesLabel(r.added_from_sales)}</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                  <div className={`recipe-status ${r.status === 'confirmed' ? 'confirmed' : r.status === 'draft' ? 'review' : 'pending'}`}>
                    {r.status === 'confirmed' ? (
                      <>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />
                        Confirmed
                      </>
                    ) : r.status === 'draft' ? (
                      <>Under Review</>
                    ) : 'Status'}
                  </div>
                  <div 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      toggleEditMode(r.id); 
                    }}
                    style={{
                      color: '#000',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      transition: 'background 0.2s'
                    }}
                    >
                    ₹{r.price || 0}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {recipes.length > 0 && (() => {
        // Spec D4: a dish added by Sales Name Check does not hold back Finish until its recipe is confirmed.
        const countable = recipes.filter(r => !isPendingAddedFromSales(r));
        const addedPending = recipes.length - countable.length;
        const total = countable.length;
        const confirmed = countable.filter(r => r.status === 'confirmed').length;
        const allConfirmed = total > 0 && confirmed === total;
        return (
          <div style={{ marginTop: 16 }}>
            <div style={{ textAlign: 'center', color: '#666', fontSize: 12, marginBottom: 8 }}>
              {confirmed}/{total} items confirmed
              {addedPending > 0 && (
                <div style={{ color: '#ff9100', marginTop: 2 }}>
                  {addedPending} added from sales {addedPending === 1 ? 'is' : 'are'} not counted until confirmed
                </div>
              )}
            </div>
            <button
              disabled={!allConfirmed}
              onClick={() => {
                if (allConfirmed && goTo) {
                  // Trigger step 3 completion popup
                  if (onStep3Complete) {
                    onStep3Complete();
                  }
                  goTo('dashboard');
                }
              }}
              style={{
                width: '100%',
                padding: 14,
                marginBottom:'4rem',
                background: allConfirmed ? '#111' : '#ccc',
                color: allConfirmed ? '#fff' : '#666',
                fontWeight: 'bold',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: allConfirmed ? 'pointer' : 'not-allowed'
              }}
            >
              Finish
            </button>
          </div>
        );
      })()}

      {showDuplicateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 20000
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '0.5rem',
            maxWidth: '400px',
            width: '90%',
            padding: '28px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            textAlign: 'center'
          }}>
            <h3 style={{ 
              margin: '0 0 12px 0', 
              fontSize: '22px', 
              fontWeight: '700',
              color: '#111'
            }}>Duplicate Item Name</h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '15px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              Duplicate item name found please verify.
            </p>
            <button
              onClick={() => setShowDuplicateModal(false)}
              style={{
                width: '100%',
                padding: '12px 20px',
                fontSize: '15px',
                fontWeight: '600',
                background: '#000000',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Menu Options Modal */}
      {showMenuOptionsModal && (
        <div className="itemmap-modal-overlay">
          <div className="itemmap-modal-card" style={{ maxWidth: 400, position: 'relative' }}>
            <div className="itemmap-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: '#000', fontSize: '18px' }}>Add Item</h3>
              <button 
                type="button" 
                onClick={() => setShowMenuOptionsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#000',
                  padding: '0 4px',
                  lineHeight: '1',
                  fontWeight: '300'
                }}
              >
                ×
              </button>
            </div>

            <div>
              <p style={{ fontSize: 14, color: '#666', marginBottom: 24, textAlign: 'left' }}>
                Choose how you want to add your items
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Scan Menu Button */}
                <button
                  onClick={() => {
                    setShowMenuOptionsModal(false);
                    /* OCR UI removed */;
                  }}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    background: '#000',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12
                  }}
                >
                  <span>Scan Menu</span>
                </button>

                {/* Add Manually Button */}
                <button
                  onClick={() => {
                    setShowMenuOptionsModal(false);
                    setShowNewRecipeForm(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    background: '#fff',
                    color: '#000',
                    border: '2px solid #000',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12
                  }}
                >
                  <span>Add Manually</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu Item Selector */}
      {showMenuSelector && (
        <MenuItemSelector
          onSelectMenuItem={async (newRecipe) => {
            try {
              const refreshedRecipes = await RecipesService.getRecipes();
              setRecipes(applyLocalReviewDrafts(refreshedRecipes));
              setShowMenuSelector(false);
              showToast(`Recipe "${newRecipe.name}" created successfully with pre-mapped ingredients!`, 'success');
            } catch (error) {
              console.error('Failed to refresh recipes:', error);
              setShowMenuSelector(false);
            }
          }}
          onClose={() => setShowMenuSelector(false)}
        />
      )}

      {/* AI Menu Scan Modal */}
      {showOCRModal && (
        <div
          className="itemmap-menuscan-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowOCRModal(false);
          }}
        >
          <div
            className={`itemmap-menuscan-card ${ocrResults.length > 0 ? 'results-shown' : ''}`}
            style={{ maxWidth: 600, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Upload Screen */}
            {!ocrProcessing && ocrResults.length === 0 && (
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {/* Show selected images if any */}
                {selectedMenuImages.length > 0 && (
                  <div style={{ 
                    background: 'white',
                    borderRadius: '12px',
                    padding: '1px',
                    marginBottom: '6px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      marginBottom: '20px',
                      paddingBottom: '15px',
                      flexWrap: 'nowrap',
                      gap: '8px',
                      width: '100%'
                    }}>
                      <h3 style={{ 
                        margin: 0,
                        color: '#333',
                        fontSize: '18px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        Selected Files ({selectedMenuImages.length})
                      </h3>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            document.getElementById('ocr-menu-upload').click();
                          }}
                          style={{
                            background: '#000',
                            color: 'white',
                            border: 'none',
                            padding: '8px 14px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                            transition: 'background 0.2s',
                            minWidth: 'auto',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Add
                        </button>
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            clearAllMenuImages();
                          }}
                          style={{
                            background: '#000',
                            color: 'white',
                            border: 'none',
                            padding: '8px 14px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                            transition: 'background 0.2s',
                            minWidth: 'auto',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    
                    {/* Image thumbnails */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                      gap: '15px', 
                      marginBottom: '20px',
                      maxHeight: '320px',
                      overflowY: 'auto',
                      overflowX: 'hidden'
                    }}>
                      {selectedMenuImages.map((file, idx) => (
                        <div 
                          key={idx}
                          style={{
                            position: 'relative',
                            border: '2px solid #ddd',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: '#f8f9fa',
                            aspectRatio: '1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <img 
                            src={URL.createObjectURL(file)} 
                            alt={file.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <button 
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeMenuImage(idx);
                            }}
                            style={{
                              position: 'absolute',
                              top: '5px',
                              left: '5px',
                              width: '24px',
                              height: '24px',
                              background: 'rgba(220, 53, 69, 0.9)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              fontWeight: 'bold'
                            }}
                            title="Remove this image"
                          >
                            ✕
                          </button>
                          <div style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            background: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600
                          }}>
                            #{idx + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Process button */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '15px', 
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                      marginBottom: 0
                    }}>
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          processMenuImagesDirectly(selectedMenuImages);
                        }}
                        style={{
                          background: '#000',
                          color: 'white',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 600,
                          transition: 'background-color 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        Extract Items from {selectedMenuImages.length} Image{selectedMenuImages.length > 1 ? 's' : ''}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Upload zone - Only show when no images selected */}
                {selectedMenuImages.length === 0 && (
                  <label htmlFor="ocr-menu-upload" className="dropzone">
                    <FontAwesomeIcon icon={faCamera} style={{ fontSize: '3rem', color: '#000000', marginBottom: 12 }} />
                    <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Upload Menu Image(s)</div>
                  </label>
                )}
                <input
                  ref={ocrMenuInputRef}
                  id="ocr-menu-upload"
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleOCRUpload}
                />
              </div>
            )}

            {/* Processing Screen */}
            {ocrProcessing && (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ 
                  width: '50px', 
                  height: '50px', 
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 24px'
                }}></div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                <div style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 8 }}>
                  {menuProcessingProgress.total > 0 
                    ? `Processing Image ${menuProcessingProgress.current} of ${menuProcessingProgress.total}...`
                    : 'Scanning Menu...'
                  }
                </div>
                {menuScanProgress > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ 
                      width: '100%', 
                      height: 8, 
                      background: '#ddd', 
                      borderRadius: 4, 
                      overflow: 'hidden',
                      marginBottom: 8
                    }}>
                      <div style={{ 
                        width: `${menuScanProgress}%`, 
                        height: '100%', 
                        background: 'linear-gradient(90deg, #000, #666)',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <div style={{ fontSize: 14, color: '#666' }}>{menuScanProgress}% complete</div>
                  </div>
                )}
                
                {/* Cancel button */}
                <button 
                  onClick={() => {
                    setShowOCRModal(false);
                    setOcrResults([]);
                    setOcrProcessing(false);
                    setScannedMenuItems([]);
                    setMenuScanJob(null);
                    setMenuScanProgress(0);
                    if (pollIntervalRef.current) {
                      clearInterval(pollIntervalRef.current);
                      pollIntervalRef.current = null;
                    }
                  }}
                  style={{
                    marginTop: 24,
                    padding: '12px 32px',
                    background: '#fff',
                    color: '#666',
                    border: '2px solid #ddd',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#f5f5f5';
                    e.target.style.borderColor = '#999';
                    e.target.style.color = '#333';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fff';
                    e.target.style.borderColor = '#ddd';
                    e.target.style.color = '#666';
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Progress Screen */}
            {!ocrProcessing && ocrResults.length > 0 && (
              <div>
                {/* Enhanced Progress Bar with Real-time Stats */}
                {menuScanJob && menuScanProgress < 100 && (
                  <div style={{
                    marginBottom: 20,
                    padding: 20,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 12,
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                    color: 'white'
                  }}>
                    {/* Header with animated pulse */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ 
                          fontSize: 24, 
                          animation: 'pulse 1.5s ease-in-out infinite',
                          display: 'inline-block'
                        }}>🎨</span>
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: 18 }}>AI Processing Menu Items</div>
                          {ocrResults.length > 0 && ocrResults[ocrResults.length - 1].name && (
                            <div style={{ 
                              fontSize: 13, 
                              opacity: 0.9,
                              marginTop: 4,
                              animation: 'fadeIn 0.5s ease-in'
                            }}>
                              Currently processing: <strong>{ocrResults[ocrResults.length - 1].name}</strong>
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 28, fontWeight: 'bold', lineHeight: 1 }}>{menuScanProgress}%</div>
                        <div style={{ fontSize: 12, opacity: 0.8 }}>Complete</div>
                      </div>
                    </div>

                    {/* Progress Bar with Animation */}
                    <div style={{ 
                      width: '100%', 
                      height: 12, 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: 6, 
                      overflow: 'hidden',
                      marginBottom: 16
                    }}>
                      <div style={{
                        width: `${menuScanProgress}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #4CAF50, #81C784)',
                        transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)'
                      }} />
                    </div>

                    {/* Statistics Grid */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(4, 1fr)', 
                      gap: 12,
                      marginTop: 16 
                    }}>
                      <div style={{
                        background: 'rgba(255,255,255,0.15)',
                        padding: 12,
                        borderRadius: 8,
                        textAlign: 'center',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <div style={{ fontSize: 24, marginBottom: 4 }}></div>
                        <div style={{ fontSize: 20, fontWeight: 'bold' }}>
                          {ocrResults.filter(i => i.status === 'completed').length}
                        </div>
                        <div style={{ fontSize: 11, opacity: 0.9 }}>Completed</div>
                      </div>
                      <div style={{
                        background: 'rgba(255,255,255,0.15)',
                        padding: 12,
                        borderRadius: 8,
                        textAlign: 'center',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <div style={{ fontSize: 24, marginBottom: 4 }}>⏳</div>
                        <div style={{ fontSize: 20, fontWeight: 'bold' }}>
                          {ocrResults.filter(i => i.status === 'processing').length}
                        </div>
                        <div style={{ fontSize: 11, opacity: 0.9 }}>Processing</div>
                      </div>
                      <div style={{
                        background: 'rgba(255,255,255,0.15)',
                        padding: 12,
                        borderRadius: 8,
                        textAlign: 'center',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <div style={{ fontSize: 24, marginBottom: 4 }}>❌</div>
                        <div style={{ fontSize: 20, fontWeight: 'bold' }}>
                          {ocrResults.filter(i => i.status === 'failed').length}
                        </div>
                        <div style={{ fontSize: 11, opacity: 0.9 }}>Failed</div>
                      </div>
                      <div style={{
                        background: 'rgba(255,255,255,0.15)',
                        padding: 12,
                        borderRadius: 8,
                        textAlign: 'center',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <div style={{ fontSize: 24, marginBottom: 4 }}>📋</div>
                        <div style={{ fontSize: 20, fontWeight: 'bold' }}>
                          {ocrResults.filter(i => i.status === 'pending').length}
                        </div>
                        <div style={{ fontSize: 11, opacity: 0.9 }}>Pending</div>
                      </div>
                    </div>

                    {/* Inline CSS Animation */}
                    <style>{`
                      @keyframes pulse {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.2); opacity: 0.8; }
                      }
                      @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(-10px); }
                        to { opacity: 1; transform: translateY(0); }
                      }
                    `}</style>
                  </div>
                )}

                {/* Menu Items List */}
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 12, color: '#000000' }}>
                    Detected Menu Items ({ocrResults.length})
                  </h4>
                  <div style={{ maxHeight: 400, overflowY: 'auto', display: 'grid', gap: 8, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    <style>{`
                      div::-webkit-scrollbar {
                        display: none;
                      }
                    `}</style>
                    {ocrResults.map((item, i) => (
                      <div
                        key={i}
                        style={{
                          padding: 12,
                          background: '#ffffff',
                          border: 'none',
                          borderRadius: 8,
                          display: 'flex',
                          gap: 12,
                          alignItems: 'center',
                          transition: 'all 0.3s ease',
                          transform: 'scale(1)',
                          boxShadow: 'none'
                        }}
                      >
                        {/* Thumbnail */}
                        <div style={{ width: 80, height: 60, borderRadius: 6, overflow: 'hidden', background: '#eee', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <span style={{ 
                              fontSize: 18, 
                              color: '#999',
                              animation: item.status === 'processing' ? 'pulse 1.5s ease-in-out infinite' : 'none'
                            }}>
                              {item.status === 'processing' ? '🎨' : (item.status === 'failed' ? '❌' : '🖼️')}
                            </span>
                          )}
                        </div>
                        {/* Meta */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.name}
                          </div>
                          {/* Status Badge */}
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                            {item.status === 'completed' && item.ingredients && (
                              <span style={{ 
                                fontSize: 11, 
                                background: '#000000', 
                                color: '#ffffff', 
                                padding: '2px 8px', 
                                borderRadius: 4,
                                fontWeight: 600
                              }}>
                                {item.ingredients.length} ingredients
                              </span>
                            )}
                            {item.status === 'processing' && (
                              <span style={{ 
                                fontSize: 11, 
                                background: '#fed7aa', 
                                color: '#9a3412', 
                                padding: '2px 8px', 
                                borderRadius: 4,
                                fontWeight: 600,
                                animation: 'fadeIn 0.5s ease-in'
                              }}>
                                Generating...
                              </span>
                            )}
                            {item.status === 'failed' && item.error && (
                              <span style={{ 
                                fontSize: 11, 
                                background: '#fecaca', 
                                color: '#991b1b', 
                                padding: '2px 8px', 
                                borderRadius: 4,
                                fontWeight: 600
                              }}>
                                {item.error}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Price */}
                        {item.price > 0 && (
                          <div style={{ color: '#000000', fontWeight: 700, fontSize: 16, flex: '0 0 auto' }}>
                            ₹{item.price}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => {
                      setShowOCRModal(false);
                      setOcrResults([]);
                      setScannedMenuItems([]);
                      setMenuScanJob(null);
                      setMenuScanProgress(0);
                      if (pollIntervalRef.current) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: '#f3f4f6',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      marginLeft: '20px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addOCRIngredientsToRecipe}
                    disabled={menuScanProgress < 100 || scannedMenuItems.length === 0}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: menuScanProgress === 100 && scannedMenuItems.length > 0 ? '#000000' : '#ccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 'bold',
                      cursor: menuScanProgress === 100 && scannedMenuItems.length > 0 ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {menuScanProgress < 100
                      ? `Processing... (${menuScanProgress}%)`
                      : `Add ${scannedMenuItems.length} Recipes`
                    }
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 🆕 Multiplier Editor Modal */}
      {showMultiplierEditor && (
        <div className="itemmap-multiplier-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
            <h2>🍽️ Customize Portion Multipliers</h2>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              We detected variant groups in your menu. Review and adjust the portion multipliers before creating recipes.
              The <strong>base variant</strong> (×1.0) is highlighted in green.
            </p>

            {pendingVariantGroups.validGroups && pendingVariantGroups.validGroups.map((group, groupIdx) => (
              <div key={groupIdx} style={{ marginBottom: '30px', padding: '15px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                <h3 style={{ marginBottom: '15px', color: '#333' }}>📋 {group.baseName}</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e0e0e0' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Variant Name</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Portion Size</th>
                      <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ccc', width: '150px' }}>Multiplier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.variants.map((variant, variantIdx) => {
                      const keyword = variant.detectedPortionSize.toLowerCase();
                      const isBase = variant.isBaseVariant;
                      return (
                        <tr key={variantIdx} style={{ backgroundColor: isBase ? '#d4edda' : 'white' }}>
                          <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{variant.name}</td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>
                            {variant.detectedPortionSize}
                            {isBase && <span style={{ marginLeft: '8px', color: '#28a745', fontWeight: 'bold' }}>⭐ BASE</span>}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center' }}>
                            <input
                              type="number"
                              step="0.1"
                              min="0.1"
                              max="10"
                              value={editableMultipliers[keyword] || 1.0}
                              onChange={(e) => {
                                const newValue = parseFloat(e.target.value) || 1.0;
                                setEditableMultipliers(prev => ({
                                  ...prev,
                                  [keyword]: newValue
                                }));
                              }}
                              style={{
                                width: '80px',
                                padding: '5px',
                                textAlign: 'center',
                                border: isBase ? '2px solid #28a745' : '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                              disabled={isBase}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}

            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowMultiplierEditor(false);
                  setPendingVariantGroups([]);
                  setEditableMultipliers({});
                }}
                className="btn btn--secondary"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  
                  try {
                    await saveCustomMultipliers(editableMultipliers);
                    
                    await processVariantGroupsWithMultipliers();
                  } catch (error) {
                    console.error('❌ Error saving multipliers or processing variants:', error);
                    console.error('❌ Error stack:', error.stack);
                    showToast('❌ Error processing recipes: ' + (error.message || 'Unknown error'));
                  }
                }}
                className="btn btn--primary"
              >
                Save & Create Recipes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔄 Recipe Creation Loading Overlay */}
      {isCreatingRecipes && (
        <div 
          className="modal-overlay" 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
        >
          <div 
            className="qritemmap-recipe-creation-modal"
          >
            <div style={{ textAlign: 'center', padding: window.innerWidth <= 640 ? '10px 0' : '20px 0' }}>
              <div style={{ 
                width: '50px', 
                height: '50px', 
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #000',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 24px'
              }}></div>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
              
              <div style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 8, color: '#000' }}>
                Creating Recipes...
              </div>
              
              {recipeCreationProgress.message && (
                <div style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
                  {recipeCreationProgress.message}
                </div>
              )}
              
              {recipeCreationProgress.total > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ 
                    width: '100%', 
                    height: 8, 
                    background: '#ddd', 
                    borderRadius: 4, 
                    overflow: 'hidden',
                    marginBottom: 8
                  }}>
                    <div style={{ 
                      width: `${(recipeCreationProgress.current / recipeCreationProgress.total) * 100}%`, 
                      height: '100%', 
                      background: 'linear-gradient(90deg, #000, #666)',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <div style={{ fontSize: 14, color: '#666' }}>
                    {recipeCreationProgress.current} of {recipeCreationProgress.total} recipes processed
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            width: 'min(420px, 90vw)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            padding: '24px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '19px',
              fontWeight: '600',
              marginBottom: '12px',
              color: '#1f2937'
            }}>
              Are you sure you want to delete this item?
            </div>
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              Note: If this item has sales history, it will be deactivated instead of permanently deleted.
            </div>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'stretch',
              width: '100%'
            }}>
              <button 
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setRecipeToDelete(null);
                }}
              >
                Cancel
              </button>
              <button 
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: '#000000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                onClick={handleConfirmDelete}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showDeleteSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            width: 'min(380px, 90vw)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            padding: '24px',
            textAlign: 'center'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                border: '3px solid #22c55e',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Check size={36} color="#22c55e" strokeWidth={3} />
              </div>
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '16px',
              color: '#1f2937'
            }}>
              {deleteModalMessage}
            </div>
            <button 
              style={{
                padding: '10px 24px',
                backgroundColor: '#000000',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              onClick={() => setShowDeleteSuccessModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Progress Popup - Step 2 out of 5 completed */}
      {displayProgressPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease-in-out'
        }}>
          <div style={{
            background: '#2c2c2c',
            color: 'white',
            padding: '40px 60px',
            borderRadius: '0.5rem',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: '600',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            animation: 'scaleIn 0.3s ease-out'
          }}>
            step 2 out of 5 completed
          </div>
        </div>
      )}
      <button
        type="button"
        className={`itemmap-scroll-top-btn ${showScrollTop ? 'visible' : ''}`}
        onClick={() => scrollToTop()}
        aria-label="Scroll to top"
        title="Scroll to top"
      >
        <FontAwesomeIcon icon={faArrowUp} />
      </button>
    </div>
  );
};

// Lightweight suggestion item component
const OCRLibrarySuggestion = ({ name, onImport }) => (
  <div className="modal-list-item">
    <div style={{ fontWeight: 600 }}>{name}</div>
    <button onClick={() => onImport(name)} className="btn btn--primary">Use Suggested</button>
  </div>
);

export default RecipeListScreen;
